import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { setNavigationGuard } from '../utils/navigationGuard';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, addDays, isBefore, isAfter, startOfDay } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Trash2,
  Save,
  AlertTriangle,
  CheckCircle,
  Store,
  Clock,
  Loader2,
  PoundSterling,
  Fuel,
  Ticket,
  Gift,
  Pencil,
  CreditCard,
  Wind,
  Droplets,
  Car,
  FileText,
  Receipt,
  XCircle,
  DollarSign,
  Search,
  Edit2,
  Smartphone,
  Banknote,
  TrendingUp,
  TrendingDown,
  X,
  Coffee,
  ClipboardCopy,
  Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

// Format number to 2 decimal places for display
const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

// Import fuel grade color utility
import { getFuelGradeColor } from '../utils/fuelGradeColors';

// Import extracted components
import { PaidOutsTab, TendersTab, FuelCostTab, ShopSalesTab, FuelSalesTab, BunkeringTab, ScratchCardsTab, ApprovalStatusBanner, VarianceApprovalDialog, DayEndReportTab } from './SalesEntry/components';

const EMPTY_PAID_OUT = {
  description: '', paid_out_type_id: '', paid_out_type_name: '', supplier_id: '', supplier_name: '',
  creditor_id: '', creditor_name: '', amount: '0.00'
};
const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const SalesEntryPage = () => {
  const { user, isAdmin, isManager, isStaff, isSuperUser } = useAuth();
  const { stores, selectedStore, setSelectedStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEntries, setExistingEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedShift, setSelectedShift] = useState('dayend');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storeDetails, setStoreDetails] = useState(null);
  const varianceLimits = {
    drive_off: 0.01, retail: 2.5, bunkering: 1, z_reading: 1, cash: 10, cash_reason: 2,
    ...(storeDetails?.variance_limits || {})
  };
  const [varianceDialogOpen, setVarianceDialogOpen] = useState(false);
  const [paidOutTypes, setPaidOutTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // Store-specific suppliers for paid outs
  const [lastEntryDate, setLastEntryDate] = useState(null); // Track last entry date for auto-set
  const [lastEntryDateLoaded, setLastEntryDateLoaded] = useState(false); // Track if we've checked for last entry

  // Scratch card verification dialog
  const [scratchCardDialogOpen, setScratchCardDialogOpen] = useState(false);
  const [scratchCardDenominations, setScratchCardDenominations] = useState([]);
  const [scratchCardQuantities, setScratchCardQuantities] = useState({}); // Calculated sold qty
  const [scratchCardSettled, setScratchCardSettled] = useState({}); // User enters
  const [scratchCardSettledPackNumbers, setScratchCardSettledPackNumbers] = useState({}); // Pack numbers for settlements
  const [scratchCardOpeningStock, setScratchCardOpeningStock] = useState({}); // From system
  const [scratchCardClosingStock, setScratchCardClosingStock] = useState({}); // User enters
  const [scratchCardVerified, setScratchCardVerified] = useState(false);
  const [scratchCardStock, setScratchCardStock] = useState({}); // Current stock from API
  const [scratchCardVarianceDialogOpen, setScratchCardVarianceDialogOpen] = useState(false); // Variance confirmation dialog
  const [scratchCardVarianceDetails, setScratchCardVarianceDetails] = useState([]); // Variance data for dialog
  const [scratchCardVarianceReason, setScratchCardVarianceReason] = useState(''); // Reason for scratch card variance
  const [showScratchCardReasonInput, setShowScratchCardReasonInput] = useState(false); // Show reason input in dialog

  // Form data
  const [departmentAmounts, setDepartmentAmounts] = useState({});
  const [departmentVolumes, setDepartmentVolumes] = useState({});
  const [fuelVolumes, setFuelVolumes] = useState({});
  const [fuelCosts, setFuelCosts] = useState({});
  const [fuelPrices, setFuelPrices] = useState({});  // Now from admin retail prices (read-only)
  const [fuelPriceSourceDate, setFuelPriceSourceDate] = useState(null);  // Source date of retail prices
  const [fuelPricesSource, setFuelPricesSource] = useState('none');  // 'exact_date', 'fallback_from_...', or 'none'
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [serviceAmounts, setServiceAmounts] = useState({});
  const [lotteryPrizePaid, setLotteryPrizePaid] = useState('0.00');
  const [scratchPrizePaid, setScratchPrizePaid] = useState('0.00');
  const [paidOuts, setPaidOuts] = useState([]);
  const [varianceReason, setVarianceReason] = useState('');
  
  // Approval Request state
  const [approvalRequest, setApprovalRequest] = useState(null);  // Current approval request for this entry
  const [varianceApprovalDialogOpen, setVarianceApprovalDialogOpen] = useState(false);
  const [varianceDetails, setVarianceDetails] = useState(null);  // Details to show in variance dialog
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const adminEditRequestId = (isAdmin || isSuperUser) && searchParams.get('mode') === 'admin_edit' ? searchParams.get('approval_request_id') : null;
  const [varianceComment, setVarianceComment] = useState('');
  const isAdminUser = isAdmin || isSuperUser;
  const isStaffLike = isStaff || ['manager', 'store_manager'].includes(user?.user_type);
  const adminVarianceAckRef = React.useRef(false);
  const [lastEntryShift, setLastEntryShift] = useState('dayend');
  const [viewingPreviousDay, setViewingPreviousDay] = useState(false);
  // Day End till report applied to this entry (figures read-only for staff)
  const [tillReport, setTillReport] = useState(null);
  
  // Total Sales (Z-reading) state
  const [totalSalesAmount, setTotalSalesAmount] = useState('0.00');
  const [totalSalesWarning, setTotalSalesWarning] = useState(null);
  // New paid out form
  const [newPaidOut, setNewPaidOut] = useState(EMPTY_PAID_OUT);
  const [editingPaidOutId, setEditingPaidOutId] = useState(null);

  // Creditors list for paid outs
  const [creditors, setCreditors] = useState([]);

  // Editing mode for managers/admins
  const [isEditing, setIsEditing] = useState(false);
  // Locks only apply to staff working on a new (not yet submitted) entry - admins are never locked
  // Pending: staff locked. Approved: everyone locked (read-only) - only an admin submits via the banner button
  const lockApplies = !adminEditRequestId && !isEditing;
  const isPendingLock = lockApplies && !isAdmin && !isSuperUser && approvalRequest?.status === 'pending_approval';
  const isApprovedLock = lockApplies && approvalRequest?.status === 'approved';
  const isEntryLocked = isPendingLock || isApprovedLock;
  const tillLock = !!tillReport && !isAdmin && !isSuperUser;
  const approvedVarianceReason = `Approved by admin${approvalRequest?.resolution_notes ? ': ' + approvalRequest.resolution_notes : ''}`;
  const interactionTimerRef = React.useRef(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  
  // View mode - all users can view existing entries (read-only)
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Fuel sales variance warning
  const [fuelVarianceWarning, setFuelVarianceWarning] = useState(null);
  
  // Fuel Sales department amount (Z-reading for fuel sales verification)
  const [fuelSalesDeptAmount, setFuelSalesDeptAmount] = useState('0.00');
  
  // Retail Sales amount (entered by user for validation against calculated retail sales)
  const [retailSalesAmount, setRetailSalesAmount] = useState('0.00');
  
  // Bunkering Sales amount (entered by user for validation against calculated bunkering total)
  const [bunkeringSalesAmount, setBunkeringSalesAmount] = useState('0.00');
  
  // Drive Off / Indebtedness amount for validation
  const [driveOffDeptAmount, setDriveOffDeptAmount] = useState('0.00');

  // Coffee Tracking state
  const [coffeeTracking, setCoffeeTracking] = useState({
    coffee_sold_enabled: false,
    coffee_sold_qty: '',
    small: {
      enabled: false,
      opening_reading: null, // null = not loaded, 0 = genuine zero
      closing_reading: '',
      calculated_sold: 0,
      loading: false,
      error: null
    },
    large: {
      enabled: false,
      opening_reading: null, // null = not loaded, 0 = genuine zero
      closing_reading: '',
      calculated_sold: 0,
      loading: false,
      error: null
    }
  });

  // SEPARATE loading/error states for coffee and scratch cards
  // This prevents race conditions where one completing clears the other's loading state
  const [coffeeOpeningLoading, setCoffeeOpeningLoading] = useState(false);
  const [coffeeOpeningError, setCoffeeOpeningError] = useState(null);
  const [scratchOpeningLoading, setScratchOpeningLoading] = useState(false);
  const [scratchOpeningError, setScratchOpeningError] = useState(null);

  // Combined check for form validation (both must be resolved)
  const openingReadingsLoading = coffeeOpeningLoading || scratchOpeningLoading;
  const openingReadingsError = coffeeOpeningError || scratchOpeningError;

  // Fuel Cost Prices state (for daily cost entry tab)
  const [fuelCostPrices, setFuelCostPrices] = useState({});
  const [previousDayCostPrices, setPreviousDayCostPrices] = useState({});
  const [savingCostPrices, setSavingCostPrices] = useState(false);
  const [fuelStock, setFuelStock] = useState([]);
  const [fuelCostsCompleted, setFuelCostsCompleted] = useState(false);

  // Tab state - default to 'fuelcost' for fuel stations, will be set in useEffect
  const [activeTab, setActiveTab] = useState('sales');
  
  // Fuel Card state
  const [fuelCardTypes, setFuelCardTypes] = useState([]);
  const [fuelCardTransactions, setFuelCardTransactions] = useState({
    non_bunkering: {},  // { card_name: amount }
    bunkering: {},      // { card_name: { transactions: [{grade_id, volume}], total_amount } }
    key_fuels: { transactions: [], shop_items_amount: 0, total_amount: 0 }
  });
  const [bunkeringDayEndAmount, setBunkeringDayEndAmount] = useState('');
  
  // Commission Income state
  const [commissions, setCommissions] = useState([]);
  const [commissionTypes, setCommissionTypes] = useState([]);
  const [newCommission, setNewCommission] = useState({
    commission_type: '',
    description: '',
    amount: ''
  });
  
  // Drive Off / Indebtedness state (inline)
  const [incidents, setIncidents] = useState([]);
  const [newIncident, setNewIncident] = useState({
    incident_type: 'drive_off',
    incident_time: '',
    pump_number: '',
    fuel_grade_id: '',
    fuel_grade_name: '',
    litres: '',
    amount: '',
    vehicle_registration: '',
    vehicle_description: '',
    police_reference: '',
    customer_name: '',
    customer_phone: '',
    agreement_signed: false,
    notes: '',
    cashier_id: '',
    cashier_name: ''
  });
  const [cashiers, setCashiers] = useState([]);
  const [incidentPaymentDialogOpen, setIncidentPaymentDialogOpen] = useState(false);
  const [selectedIncidentForPayment, setSelectedIncidentForPayment] = useState(null);
  const [incidentPaymentForm, setIncidentPaymentForm] = useState({
    payment_type: 'store',
    payment_method: 'cash',
    notes: ''
  });
  
  // Track unsaved changes for navigation warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Track incidents marked for deletion (will be deleted only on successful submission)
  const [incidentsToDelete, setIncidentsToDelete] = useState([]);
  
  // Track pending incidents (not yet saved to DB - will be saved on successful submission)
  const [pendingIncidents, setPendingIncidents] = useState([]);
  
  // Incident Recovery Search state
  const [incidentSearchOpen, setIncidentSearchOpen] = useState(false);
  const [incidentSearchDateFrom, setIncidentSearchDateFrom] = useState('');
  const [incidentSearchDateTo, setIncidentSearchDateTo] = useState('');
  const [incidentSearchCarReg, setIncidentSearchCarReg] = useState('');
  const [incidentSearchAmount, setIncidentSearchAmount] = useState('');
  const [incidentSearchResults, setIncidentSearchResults] = useState([]);
  const [incidentSearchLoading, setIncidentSearchLoading] = useState(false);
  const [selectedRecoveryIncidents, setSelectedRecoveryIncidents] = useState([]);
  // Track incidents marked for recovery (will be marked as paid on successful submission)
  const [incidentsToRecover, setIncidentsToRecover] = useState([]);
  
  // Date change dialog
  const [dateChangeDialogOpen, setDateChangeDialogOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);
  // Store change dialog
  const [storeChangeDialogOpen, setStoreChangeDialogOpen] = useState(false);
  const [pendingStoreId, setPendingStoreId] = useState(null);
  // Submit confirmation dialog
  const [submitConfirmDialogOpen, setSubmitConfirmDialogOpen] = useState(false);
  
  // Total Pump validation dialog
  const [pumpValidationDialogOpen, setPumpValidationDialogOpen] = useState(false);
  const [pumpValidationData, setPumpValidationData] = useState(null);

  // Bunkering validation dialog
  const [bunkeringValidationDialogOpen, setBunkeringValidationDialogOpen] = useState(false);
  const [bunkeringValidationData, setBunkeringValidationData] = useState(null);

  // Retail Sales minor variance confirmation dialog
  const [retailVarianceDialogOpen, setRetailVarianceDialogOpen] = useState(false);
  const [retailVarianceData, setRetailVarianceData] = useState(null);

  // Draft functionality
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [draftKey, setDraftKey] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null); // Info about who saved the draft

  // Generate draft key based on store + date + shift
  const getDraftKey = useCallback((storeId, date, shift) => {
    if (!storeId || !date || !shift) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    return `${storeId}_${dateStr}_${shift}`;
  }, []);

  // Save draft to database (accessible from any device)
  const saveDraft = async () => {
    if (!selectedStoreId || !selectedDate || !selectedShift) {
      toast.error('Please select store, date, and shift first');
      return;
    }

    setSavingDraft(true);
    try {
      const draftData = buildDraftData();

      await axios.post(`${API}/drafts`, {
        store_id: selectedStoreId,
        sales_date: format(selectedDate, 'yyyy-MM-dd'),
        shift: selectedShift,
        draft_data: draftData
      });
      
      toast.success('Draft saved to server! Accessible from any device.');
      setUserHasInteracted(false);
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const applyTillReport = (data) => {
    const m = data.mapped;
    const toStr = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v)]));
    if (m.fuel_volumes) {
      setFuelVolumes(prev => ({ ...prev, ...toStr(m.fuel_volumes) }));
      setFuelSalesDeptAmount(String(m.total_fuel_sales));
      setRetailSalesAmount(String(m.retail_sales));
      setBunkeringSalesAmount(String(m.bunkering_sales));
    }
    setTotalSalesAmount(String(m.shop_total_sales));
    setDepartmentAmounts(prev => ({ ...prev, ...toStr(m.department_amounts) }));
    setDepartmentVolumes(prev => ({ ...prev, ...toStr(m.department_volumes) }));
    setServiceAmounts(prev => ({ ...prev, ...toStr(m.service_amounts) }));
    // Bunkering fuel card lines (amount = report GBP)
    if (m.bunkering) {
      const withPrice = (t) => ({
        grade_id: t.grade_id, grade_name: t.grade_name, volume: t.volume, amount: t.amount,
        unit_price: t.volume ? t.amount / t.volume : 0,
        selling_price: parseFloat(fuelPrices[t.grade_id]) || 0,
        is_diesel: (t.grade_name || '').toLowerCase().includes('diesel')
      });
      const sum = (arr) => Math.round(arr.reduce((acc, t) => acc + (t.amount || 0), 0) * 100) / 100;
      const bunkering = Object.fromEntries(Object.entries(m.bunkering.cards).map(([card, txs]) => {
        const transactions = txs.map(withPrice);
        return [card, { transactions, total_amount: sum(transactions) }];
      }));
      const kfTx = m.bunkering.key_fuels.transactions.map(withPrice);
      const shopItems = m.bunkering.key_fuels.shop_items_amount || 0;
      setFuelCardTransactions(prev => ({
        ...prev,
        bunkering,
        key_fuels: { transactions: kfTx, shop_items_amount: String(shopItems), total_amount: sum(kfTx) + shopItems }
      }));
    }
    if (m.tenders) {
      const fix = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, (v || 0).toFixed(2)]));
      setPaymentAmounts(prev => ({ ...prev, ...fix(m.tenders.payments), ...fix(m.tenders.other_payments || {}) }));
      setFuelCardTransactions(prev => ({ ...prev, non_bunkering: { ...prev.non_bunkering, ...fix(m.tenders.fuel_cards) } }));
    }
    if (m.paid_outs) {
      if (hasLottery) setLotteryPrizePaid(m.paid_outs.lottery_prize.toFixed(2));
      if (hasScratchCards) setScratchPrizePaid(m.paid_outs.scratch_prize.toFixed(2));
      const supplierType = paidOutTypes.find(t => t.name === 'Supplier Payment');
      const lines = m.paid_outs.lines.map((l, i) => {
        const base = { ...EMPTY_PAID_OUT, id: `till-${Date.now()}-${i}`, amount: l.amount.toFixed(2), from_till: true };
        const linkedSupplier = l.supplier_id && suppliers.find(x => x.id === l.supplier_id);
        if (linkedSupplier && supplierType) {
          return { ...base, paid_out_type_id: supplierType.id, paid_out_type_name: supplierType.name,
            supplier_id: linkedSupplier.id, supplier_name: linkedSupplier.name, description: l.heading };
        }
        const linkedType = l.paid_out_type_id && paidOutTypes.find(x => x.id === l.paid_out_type_id);
        if (linkedType) return { ...base, paid_out_type_id: linkedType.id, paid_out_type_name: linkedType.name, description: l.heading };
        if (l.supplier_name !== null && l.supplier_name !== undefined && supplierType) {
          const s = suppliers.find(x => normName(x.name) === normName(l.supplier_name));
          return { ...base, paid_out_type_id: supplierType.id, paid_out_type_name: supplierType.name,
            supplier_id: s?.id || '', supplier_name: s?.name || '',
            description: s ? l.supplier_name : l.supplier_name ? `${l.supplier_name} - supplier not set up, contact admin` : `${l.heading} - select supplier`, till_warning: !s };
        }
        const headingBase = l.heading.replace(/\s*\(.*\)\s*$/, '');
        const t = paidOutTypes.find(x => [normName(l.heading), normName(headingBase)].includes(normName(x.name)));
        if (t) return { ...base, paid_out_type_id: t.id, paid_out_type_name: t.name, description: l.heading };
        const s = supplierType && suppliers.find(x => normName(x.name) === normName(l.heading));
        if (s) return { ...base, paid_out_type_id: supplierType.id, paid_out_type_name: supplierType.name,
          supplier_id: s.id, supplier_name: s.name, description: l.heading };
        return { ...base, description: `${l.heading} - paid out type not matched, please edit`, till_warning: true };
      });
      setPaidOuts(prev => [...prev.filter(p => !p.from_till), ...lines]);
    }
    // Coffee Tracking POS Sold = Coffee sales count from the report
    if (m.coffee_sold_qty !== null && m.coffee_sold_qty !== undefined) {
      setCoffeeTracking(prev => ({ ...prev, coffee_sold_qty: String(m.coffee_sold_qty) }));
    }
    setTillReport({ report: data.report, mapped: m });
    setUserHasInteracted(true);
  };

  function buildDraftData() {
      return {
        departmentAmounts,
        departmentVolumes,
        fuelVolumes,
        fuelCosts,
        fuelPrices,
        paymentAmounts,
        serviceAmounts,
        lotteryPrizePaid,
        scratchPrizePaid,
        paidOuts,
        varianceReason,
        totalSalesAmount,
        fuelSalesDeptAmount,
        retailSalesAmount,
        bunkeringSalesAmount,
        driveOffDeptAmount,
        coffeeTracking,
        fuelCostPrices,
        fuelCardTransactions,
        bunkeringDayEndAmount,
        commissions,
        scratchCardSettled,
        scratchCardClosingStock,
        scratchCardSettledPackNumbers,
        tillReport
      };
  }

  // Load draft from database
  const loadDraft = useCallback(async (storeId, dateStr, shift) => {
    try {
      const response = await axios.get(`${API}/drafts/${storeId}/${dateStr}/${shift}`);
      const draftDoc = response.data;
      
      if (!draftDoc || !draftDoc.draft_data) return false;

      const draft = draftDoc.draft_data;
      
      // Restore form data
      setTillReport(draft.tillReport || null);
      if (draft.departmentAmounts) setDepartmentAmounts(draft.departmentAmounts);
      if (draft.departmentVolumes) setDepartmentVolumes(draft.departmentVolumes);
      if (draft.fuelVolumes) setFuelVolumes(draft.fuelVolumes);
      if (draft.fuelCosts) setFuelCosts(draft.fuelCosts);
      if (draft.paymentAmounts) setPaymentAmounts(draft.paymentAmounts);
      if (draft.serviceAmounts) setServiceAmounts(draft.serviceAmounts);
      if (draft.lotteryPrizePaid) setLotteryPrizePaid(draft.lotteryPrizePaid);
      if (draft.scratchPrizePaid) setScratchPrizePaid(draft.scratchPrizePaid);
      if (draft.paidOuts) setPaidOuts(draft.paidOuts);
      if (draft.varianceReason) setVarianceReason(draft.varianceReason);
      if (draft.totalSalesAmount) setTotalSalesAmount(draft.totalSalesAmount);
      if (draft.fuelSalesDeptAmount) setFuelSalesDeptAmount(draft.fuelSalesDeptAmount);
      if (draft.retailSalesAmount) setRetailSalesAmount(draft.retailSalesAmount);
      if (draft.bunkeringSalesAmount) setBunkeringSalesAmount(draft.bunkeringSalesAmount);
      if (draft.driveOffDeptAmount) setDriveOffDeptAmount(draft.driveOffDeptAmount);
      if (draft.coffeeTracking) setCoffeeTracking(draft.coffeeTracking);
      // Only apply non-empty draft costs - an empty draft must not wipe New Cost saved on the server
      if (draft.fuelCostPrices && Object.keys(draft.fuelCostPrices).length > 0) {
        setFuelCostPrices(draft.fuelCostPrices);
        setFuelCostsCompleted(true);
      }
      if (draft.fuelCardTransactions) setFuelCardTransactions(draft.fuelCardTransactions);
      if (draft.bunkeringDayEndAmount) setBunkeringDayEndAmount(draft.bunkeringDayEndAmount);
      if (draft.commissions) setCommissions(draft.commissions);
      if (draft.scratchCardSettled) setScratchCardSettled(draft.scratchCardSettled);
      if (draft.scratchCardClosingStock) setScratchCardClosingStock(draft.scratchCardClosingStock);
      if (draft.scratchCardSettledPackNumbers) setScratchCardSettledPackNumbers(draft.scratchCardSettledPackNumbers);

      // If draft has fuel cost prices, switch to sales tab instead of staying on fuel cost tab
      if (draft.fuelCostPrices && Object.keys(draft.fuelCostPrices).length > 0) {
        setActiveTab('sales');
      }

      toast.success(`Draft loaded! (Saved by ${draftDoc.saved_by_name || 'Unknown'})`);
      return true;
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load draft:', error);
        toast.error('Failed to load draft');
      }
      return false;
    }
  }, []);

  // Clear draft from database
  const clearDraft = useCallback(async (storeId, dateStr, shift) => {
    try {
      if (storeId && dateStr && shift) {
        await axios.delete(`${API}/drafts/${storeId}/${dateStr}/${shift}`);
      }
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, []);

  // Check for existing draft when store/date/shift changes
  const draftCheckedSlotRef = React.useRef(null);
  const checkForDraft = useCallback(async (storeId, date, shift) => {
    if (!storeId || !date || !shift) return;
    
    const dateStr = format(date, 'yyyy-MM-dd');

    try {
      const response = await axios.get(`${API}/drafts/${storeId}/${dateStr}/${shift}`);
      const draftDoc = response.data;
      
      if (draftDoc && draftDoc.draft_data && !isEditing) {
        // Locked/approval drafts must be preserved - load them directly (no "Start Fresh")
        if (draftDoc.is_locked || adminEditRequestId) {
          await loadDraft(storeId, dateStr, shift);
          return;
        }
        setDraftKey({ storeId, dateStr, shift });
        setDraftInfo({
          savedBy: draftDoc.saved_by_name || 'Unknown',
          savedAt: draftDoc.saved_at
        });
        setDraftDialogOpen(true);
      }
    } catch (error) {
      // 404 means no draft exists - that's fine
      if (error.response?.status !== 404) {
        console.error('Failed to check for draft:', error);
      }
    }
  }, [isEditing, adminEditRequestId, loadDraft]);

  // Check if form has any data entered (ANY tab)
  const checkFormHasData = useCallback(() => {
    // Shop Sales tab
    const hasDepartmentData = Object.values(departmentAmounts).some(v => parseFloat(v) > 0);
    const hasServiceData = Object.values(serviceAmounts).some(v => parseFloat(v) > 0);
    const hasTotalSales = parseFloat(totalSalesAmount) > 0;
    
    // Fuel Sales tab
    const hasFuelData = Object.values(fuelVolumes).some(v => parseFloat(v) > 0);
    const hasFuelSales = parseFloat(fuelSalesDeptAmount) > 0;
    const hasDriveOffAmount = parseFloat(driveOffDeptAmount) > 0;
    
    // Paid Outs tab
    const hasPaidOuts = paidOuts.length > 0;
    const hasPrizes = parseFloat(lotteryPrizePaid) > 0 || parseFloat(scratchPrizePaid) > 0;
    
    // Scratch Cards tab
    const hasScratchCardSettled = Object.values(scratchCardSettled).some(v => parseInt(v) > 0);
    const hasScratchCardClosing = Object.values(scratchCardClosingStock).some(v => parseInt(v) > 0);
    
    // Drive Off / Debt tab
    const hasIncidents = incidents.length > 0;
    
    // Tenders tab
    const hasPaymentData = Object.values(paymentAmounts).some(v => parseFloat(v) > 0);
    
    const hasFuelCosts = Object.values(fuelCostPrices || {}).some(v => parseFloat(v) > 0);
    
    return hasDepartmentData || hasServiceData || hasFuelData || hasPaymentData || 
           hasPaidOuts || hasPrizes || hasTotalSales || hasFuelSales || hasDriveOffAmount ||
           hasScratchCardSettled || hasScratchCardClosing || hasIncidents || hasFuelCosts;
  }, [departmentAmounts, serviceAmounts, fuelVolumes, paymentAmounts, paidOuts, 
      lotteryPrizePaid, scratchPrizePaid, totalSalesAmount, fuelSalesDeptAmount, 
      driveOffDeptAmount, scratchCardSettled, scratchCardClosingStock, incidents, fuelCostPrices]);

  // Track if user has interacted with the form (typed something)
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  
  // Track previous store/date/shift to detect navigation vs data entry
  const prevStoreRef = React.useRef(selectedStoreId);
  const prevDateRef = React.useRef(selectedDate);
  const prevShiftRef = React.useRef(selectedShift);
  
  // Update unsaved changes flag when form data changes (only when not editing existing entry)
  useEffect(() => {
    // Don't show warning when editing existing entry
    if (isEditing) {
      setHasUnsavedChanges(false);
      return;
    }
    
    // Check if store, date, or shift changed - if so, reset warning and refs
    const storeChanged = prevStoreRef.current !== selectedStoreId;
    const dateChanged = prevDateRef.current !== selectedDate;
    const shiftChanged = prevShiftRef.current !== selectedShift;
    
    if (storeChanged || dateChanged || shiftChanged) {
      prevStoreRef.current = selectedStoreId;
      prevDateRef.current = selectedDate;
      prevShiftRef.current = selectedShift;
      // Navigation change - always reset unsaved warning and interaction flag
      setHasUnsavedChanges(false);
      setUserHasInteracted(false);
      return;
    }
    
    // Only show warning if user has actually interacted with the form
    if (!userHasInteracted) {
      setHasUnsavedChanges(false);
      return;
    }
    
    // Check if there's an existing entry for current date/shift
    const normalizeShift = (shift) => shift?.toLowerCase().replace(/\s+/g, '');
    const hasExistingEntry = existingEntries.some(
      e => e.sales_date === format(selectedDate, 'yyyy-MM-dd') && 
           normalizeShift(e.shift) === normalizeShift(selectedShift)
    );
    
    // Only show warning if form actually has data and no existing entry
    if (!hasExistingEntry) {
      const hasData = checkFormHasData();
      setHasUnsavedChanges(hasData);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [departmentAmounts, serviceAmounts, fuelVolumes, paymentAmounts, paidOuts,
      lotteryPrizePaid, scratchPrizePaid, totalSalesAmount, fuelSalesDeptAmount, 
      driveOffDeptAmount, scratchCardSettled, scratchCardClosingStock, incidents,
      existingEntries, selectedDate, selectedShift, selectedStoreId, isEditing, 
      userHasInteracted, checkFormHasData, fuelCostPrices]);

  // In-app navigation guard (sidebar links, logout) while there is unsaved data
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const pendingNavRef = React.useRef(null);
  useEffect(() => {
    if (hasUnsavedChanges && !isEntryLocked) {
      setNavigationGuard((proceed) => {
        pendingNavRef.current = proceed;
        setLeaveDialogOpen(true);
      });
    } else {
      setNavigationGuard(null);
    }
    return () => setNavigationGuard(null);
  }, [hasUnsavedChanges, isEntryLocked]);

  const leavePage = async (saveFirst) => {
    if (saveFirst) {
      const ok = await saveDraft();
      if (!ok) return;
    }
    setLeaveDialogOpen(false);
    setNavigationGuard(null);
    pendingNavRef.current?.();
  };

  // Browser navigation warning (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (stores.length > 0) {
      const storeToSelect = selectedStore || stores[0];
      setSelectedStoreId(storeToSelect.id);
      fetchStoreDetails(storeToSelect.id);
      fetchScratchCardOpeningStock(storeToSelect.id, selectedDate, selectedShift);
      fetchLastEntryDate(storeToSelect.id); // Auto-set date for staff
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchExistingEntries();
      fetchAllStoreEntries(); // Fetch all entries to determine latest entry date
    }
  }, [selectedStoreId, selectedDate]);

  // Check for drafts when store/date/shift changes
  useEffect(() => {
    const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
    const slotSubmitted = existingEntries.some(e =>
      e.sales_date === dateStr && (e.shift || '').toLowerCase().replace(/[_\s]/g, '') === selectedShift
    );
    const slotKey = `${selectedStoreId}_${dateStr}_${selectedShift}`;
    if (selectedStoreId && selectedDate && selectedShift && !isEditing && !slotSubmitted && !viewingPreviousDay
        && draftCheckedSlotRef.current !== slotKey) {
      // Small delay to ensure other data is loaded first
      const timer = setTimeout(() => {
        // Check each store/date/shift only once (effect re-runs on unrelated refreshes)
        draftCheckedSlotRef.current = slotKey;
        checkForDraft(selectedStoreId, selectedDate, selectedShift);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedStoreId, selectedDate, selectedShift, isEditing, checkForDraft, existingEntries, viewingPreviousDay]);

  useEffect(() => {
    fetchPaidOutTypes();
    fetchCommissionTypes();
  }, []);

  // Fetch scratch card denominations, suppliers, and cashiers when store changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchScratchCardDenominations(selectedStoreId);
      fetchSuppliers(selectedStoreId);
      fetchCreditors(selectedStoreId);
      fetchCashiers(selectedStoreId);  // Load store-specific cashiers
    }
  }, [selectedStoreId]);

  // Fetch commissions and incidents when store/date changes
  useEffect(() => {
    if (selectedStoreId && selectedDate) {
      fetchCommissionsForDate();
      // Only fetch opening stock for NEW entries, not when editing existing entries
      // This prevents overwriting user-entered or saved values
      // Note: We check isEditing here; existingEntry check happens via the isEditing flow
      if (!isEditing) {
        // Fetch scratch card opening stock when date changes (for closing stock auto-populate)
        // Pass the selected date and shift so backend can find correct opening stock
        // For Day End: checks for Mid Day entry on same date first
        fetchScratchCardOpeningStock(selectedStoreId, selectedDate, selectedShift);
        // Initialize coffee tracking from store settings
        // IMPORTANT: Pass current date and store to avoid stale closure values
        initializeCoffeeTracking(selectedDate, selectedStoreId);
      }
      if (storeDetails?.store_type === 'fuel_station') {
        fetchIncidentsForDate();
        // New entry: blank New Cost; editing: saved costs for this date
        checkFuelCostsCompleted(selectedStoreId, selectedDate, isEditing);
        // Reset previous day cost prices before fetching new ones
        setPreviousDayCostPrices({});
        // Fetch previous day's cost prices for reference
        fetchPreviousDayCostPrices(selectedStoreId, selectedDate);
        // Fetch admin retail prices for the selected date (READ-ONLY for staff)
        fetchAdminRetailPrices(selectedStoreId, selectedDate);
      }
      checkApprovalRequest(selectedStoreId, selectedDate, selectedShift);
    }
  }, [selectedStoreId, selectedDate, storeDetails, isEditing]);

  // Re-check approval request status when shift changes
  useEffect(() => {
    if (selectedStoreId && selectedDate && selectedShift) {
      checkApprovalRequest(selectedStoreId, selectedDate, selectedShift);
    }
  }, [selectedShift]);

  // Admin "Edit & Submit" mode: jump to the request's store/date/shift
  useEffect(() => {
    if (!adminEditRequestId || stores.length === 0) return;
    const store = stores.find(s => s.id === searchParams.get('store_id'));
    if (store) setSelectedStore(store);
    const dateParam = searchParams.get('date');
    if (dateParam) setSelectedDate(new Date(dateParam + 'T00:00:00'));
    const shiftParam = searchParams.get('shift');
    if (shiftParam) setSelectedShift(shiftParam);
  }, [adminEditRequestId, stores]);

  // Re-fetch scratch card opening stock when shift changes
  // This is important for Day End entry: uses Mid Day closing stock if available
  useEffect(() => {
    if (selectedStoreId && selectedDate && selectedShift && !isEditing && !existingEntry) {
      fetchScratchCardOpeningStock(selectedStoreId, selectedDate, selectedShift);
    }
  }, [selectedShift]);

  // Initialize coffee tracking from store settings and previous day's readings
  // CRITICAL: Uses the staff-selected sales date (selectedDate), NOT the current system date
  // preserveClosing: if true, keeps existing closing_reading values (for Retry scenario)
  const initializeCoffeeTracking = async (dateOverride = null, storeIdOverride = null, preserveClosing = false) => {
    const targetStoreId = storeIdOverride || selectedStoreId;
    const targetDate = dateOverride || selectedDate;
    
    // If store has no coffee tracking, reset to disabled state and exit
    if (!storeDetails?.coffee_tracking || !targetStoreId || !targetDate) {
      setCoffeeTracking({
        coffee_sold_enabled: false,
        coffee_sold_qty: '',
        small: { enabled: false, opening_reading: null, closing_reading: '', calculated_sold: 0, loading: false, error: null },
        large: { enabled: false, opening_reading: null, closing_reading: '', calculated_sold: 0, loading: false, error: null }
      });
      return;
    }
    
    const ct = storeDetails.coffee_tracking;
    const coffeeSoldEnabled = ct.coffee_sold_enabled || false;
    const smallEnabled = ct.small_enabled || false;
    const largeEnabled = ct.large_enabled || false;
    
    if (!coffeeSoldEnabled && !smallEnabled && !largeEnabled) {
      setCoffeeTracking({
        coffee_sold_enabled: false,
        coffee_sold_qty: '',
        small: { enabled: false, opening_reading: null, closing_reading: '', calculated_sold: 0, loading: false, error: null },
        large: { enabled: false, opening_reading: null, closing_reading: '', calculated_sold: 0, loading: false, error: null }
      });
      return;
    }
    
    // Set COFFEE-SPECIFIC loading state
    setCoffeeOpeningLoading(true);
    setCoffeeOpeningError(null);
    
    // Set initial state with loading indicators
    // If preserveClosing is true (Retry scenario), keep existing closing_reading values
    setCoffeeTracking(prev => ({
      coffee_sold_enabled: coffeeSoldEnabled,
      coffee_sold_qty: preserveClosing ? prev.coffee_sold_qty : '',
      small: {
        enabled: smallEnabled,
        opening_reading: null, // null indicates loading
        closing_reading: preserveClosing ? prev.small?.closing_reading || '' : '',
        calculated_sold: 0,
        loading: true,
        error: null
      },
      large: {
        enabled: largeEnabled,
        opening_reading: null, // null indicates loading
        closing_reading: preserveClosing ? prev.large?.closing_reading || '' : '',
        calculated_sold: 0,
        loading: true,
        error: null
      }
    }));
    
    // Default to store baseline readings (will be overwritten if previous entry found)
    let smallOpening = ct.small_opening_reading || 0;
    let largeOpening = ct.large_opening_reading || 0;
    let fetchError = null;
    
    try {
      // Calculate previous date based on the SELECTED sales date, not system date
      // Use date-fns subDays for reliable date calculation (handles month/year boundaries)
      const { subDays } = await import('date-fns');
      const targetDateObj = typeof targetDate === 'string' ? new Date(targetDate + 'T12:00:00') : targetDate;
      const prevDateObj = subDays(targetDateObj, 1);
      const prevDateStr = format(prevDateObj, 'yyyy-MM-dd');
      
      console.log(`[Coffee] Fetching previous day (${prevDateStr}) readings for sales date ${format(targetDateObj, 'yyyy-MM-dd')}`);
      
      const response = await axios.get(`${API}/sales`, {
        params: { store_id: targetStoreId, start_date: prevDateStr, end_date: prevDateStr }
      });
      
      if (response.data.length > 0) {
        // Find the Day End shift entry (preferred) or use the most recent entry
        let prevEntry = response.data.find(e => 
          e.shift?.toLowerCase().replace(/\s+/g, '') === 'dayend' ||
          e.shift?.toLowerCase() === 'day end' ||
          e.shift?.toLowerCase() === 'day_end'
        );
        // If no Day End found, use the first entry (most recent due to sort order)
        if (!prevEntry) {
          prevEntry = response.data[0];
        }
        
        if (prevEntry.coffee_tracking) {
          // Parse closing readings - handle both string and number formats
          if (smallEnabled && prevEntry.coffee_tracking.small?.closing_reading !== undefined) {
            const closingVal = prevEntry.coffee_tracking.small.closing_reading;
            smallOpening = parseInt(closingVal) || 0;
            console.log(`[Coffee] Small opening from prev closing: ${smallOpening}`);
          }
          if (largeEnabled && prevEntry.coffee_tracking.large?.closing_reading !== undefined) {
            const closingVal = prevEntry.coffee_tracking.large.closing_reading;
            largeOpening = parseInt(closingVal) || 0;
            console.log(`[Coffee] Large opening from prev closing: ${largeOpening}`);
          }
        } else {
          console.log(`[Coffee] Previous entry found but no coffee_tracking data`);
        }
      } else {
        console.log(`[Coffee] No entry found for ${prevDateStr}, using store default readings`);
      }
    } catch (error) {
      console.error('[Coffee] Failed to fetch previous coffee readings:', error);
      fetchError = 'Failed to load coffee readings. Please try again.';
      setCoffeeOpeningError(fetchError);
      // On error, keep loading state with error flag - don't default to 0
    }
    
    // Update state with final values
    // If preserveClosing is true, keep existing closing_reading and recalculate sold
    setCoffeeTracking(prev => {
      const smallClosing = preserveClosing ? prev.small?.closing_reading || '' : '';
      const largeClosing = preserveClosing ? prev.large?.closing_reading || '' : '';
      const smallSold = smallClosing && !fetchError ? (parseInt(smallClosing) || 0) - smallOpening : 0;
      const largeSold = largeClosing && !fetchError ? (parseInt(largeClosing) || 0) - largeOpening : 0;
      
      return {
        coffee_sold_enabled: coffeeSoldEnabled,
        coffee_sold_qty: preserveClosing ? prev.coffee_sold_qty : '',
        small: {
          enabled: smallEnabled,
          opening_reading: fetchError ? null : smallOpening, // Keep null on error
          closing_reading: smallClosing,
          calculated_sold: smallSold,
          loading: false,
          error: fetchError
        },
        large: {
          enabled: largeEnabled,
          opening_reading: fetchError ? null : largeOpening, // Keep null on error
          closing_reading: largeClosing,
          calculated_sold: largeSold,
          loading: false,
          error: fetchError
        }
      };
    });
    
    setCoffeeOpeningLoading(false);
  };

  // Update coffee readings and calculate sold
  const updateCoffeeReading = (size, field, value) => {
    setCoffeeTracking(prev => {
      const updated = { ...prev };
      updated[size] = { ...updated[size], [field]: value };
      
      // Calculate sold
      const opening = parseInt(updated[size].opening_reading) || 0;
      const closing = parseInt(updated[size].closing_reading) || 0;
      const calculatedSold = closing > 0 ? closing - opening : 0;
      updated[size].calculated_sold = calculatedSold;
      
      return updated;
    });
  };

  // Update coffee sold qty
  const updateCoffeeSoldQty = (value) => {
    setCoffeeTracking(prev => ({ ...prev, coffee_sold_qty: value }));
  };

  const fetchScratchCardDenominations = async (storeId = selectedStoreId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/settings/scratch-card-denominations`, {
        params: { store_id: storeId }
      });
      // Filter active denominations and sort by value ascending
      const active = (response.data || []).filter(d => d.is_active);
      // Sort by value in ascending order (£1, £2, £3, £5...)
      const sorted = active.sort((a, b) => a.value - b.value);
      setScratchCardDenominations(sorted);
    } catch (error) {
      console.error('Failed to fetch scratch card denominations:', error);
    }
  };

  const fetchCommissionTypes = async () => {
    try {
      const response = await axios.get(`${API}/commissions/types`);
      setCommissionTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch commission types:', error);
    }
  };

  // Fetch last entry date to auto-set next date for staff
  const viewPreviousDay = () => {
    setViewingPreviousDay(true);
    setSelectedDate(new Date(lastEntryDate));
    setSelectedShift(lastEntryShift);
  };

  const fetchLastEntryDate = async (storeId) => {
    setLastEntryDateLoaded(false); // Reset loading state
    try {
      const response = await axios.get(`${API}/sales`, {
        params: { store_id: storeId }
      });
      const entries = response.data || [];
      if (entries.length > 0) {
        // Sort by date descending, then by shift (day_end comes after mid_day)
        const sortedEntries = entries.sort((a, b) => {
          const dateCompare = new Date(b.sales_date) - new Date(a.sales_date);
          if (dateCompare !== 0) return dateCompare;
          // Same date - day_end should come after mid_day
          const aIsDayEnd = a.shift?.toLowerCase().replace(/\s+/g, '') === 'dayend';
          const bIsDayEnd = b.shift?.toLowerCase().replace(/\s+/g, '') === 'dayend';
          return bIsDayEnd - aIsDayEnd; // day_end entries first (most recent)
        });
        
        const lastEntry = sortedEntries[0];
        const lastDate = lastEntry.sales_date;
        const lastShift = lastEntry.shift?.toLowerCase().replace(/\s+/g, '');
        setLastEntryDate(lastDate);
        setLastEntryShift(lastShift?.replace('_', '') || 'dayend');
        setViewingPreviousDay(false);
        
        // Open on the next date/shift after the last entry (all users; admins can still pick any date)
        if (!adminEditRequestId) {
          // Check if last entry was Mid Day - if so, stay on same date for Day End
          if (lastShift === 'midday' || lastShift === 'mid_day') {
            // Check if Day End already exists for this date
            const dayEndExists = entries.some(e => 
              e.sales_date === lastDate && 
              (e.shift?.toLowerCase().replace(/\s+/g, '') === 'dayend')
            );
            
            if (!dayEndExists) {
              // Stay on same date, switch to Day End
              setSelectedDate(new Date(lastDate));
              setSelectedShift('dayend');
            } else {
              // Both shifts done, move to next day - default to Day End
              const nextDate = addDays(new Date(lastDate), 1);
              setSelectedDate(nextDate);
              setSelectedShift('dayend');
            }
          } else {
            // Last entry was Day End, move to next day - default to Day End
            const nextDate = addDays(new Date(lastDate), 1);
            setSelectedDate(nextDate);
            setSelectedShift('dayend');
          }
        }
      } else {
        // No entries exist, allow staff to select first date
        setLastEntryDate(null);
        if (isStaffLike) {
          setSelectedDate(new Date());
          setSelectedShift('dayend'); // Default to Day End
        }
      }
    } catch (error) {
      console.error('Failed to fetch last entry date:', error);
      setLastEntryDate(null); // Allow date selection on error
    } finally {
      setLastEntryDateLoaded(true); // Mark as loaded
    }
  };

  const fetchCommissionsForDate = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/commissions/for-date/${selectedStoreId}/${dateStr}`);
      setCommissions(response.data.commissions || []);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    }
  };

  const fetchIncidentsForDate = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/incidents/for-date/${selectedStoreId}/${dateStr}`);
      // Combine new and resolved incidents, but filter to ensure only incidents for this date
      const allIncidents = [
        ...(response.data.new_incidents || []).filter(i => i.incident_date === dateStr),
        ...(response.data.resolved_incidents || []).filter(i => i.resolution_date === dateStr)
      ];
      setIncidents(allIncidents);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    }
  };

  const fetchPaidOutTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/paid-out-types`);
      setPaidOutTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch paid out types:', error);
      // Set defaults if API fails
      setPaidOutTypes([
        { id: 'pot-1', name: 'Supplier Payment' },
        { id: 'pot-2', name: 'Staff Wages' },
        { id: 'pot-3', name: 'Utilities' },
        { id: 'pot-4', name: 'Other' }
      ]);
    }
  };

  const fetchCashiers = async (storeId) => {
    try {
      // Fetch store-specific cashiers (from store settings)
      const response = await axios.get(`${API}/settings/cashiers`, {
        params: { store_id: storeId }
      });
      // Sort cashiers by name in ascending order
      const sortedCashiers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCashiers(sortedCashiers);
    } catch (error) {
      console.error('Failed to fetch cashiers:', error);
    }
  };

  const fetchSuppliers = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { store_id: storeId }
      });
      // Sort suppliers by name in ascending order (case-insensitive)
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchCreditors = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/creditors/creditors`, {
        params: { store_id: storeId }
      });
      // Filter to only show creditors (not investments)
      const creditorsOnly = (response.data || []).filter(c => c.type === 'creditor');
      setCreditors(creditorsOnly);
    } catch (error) {
      console.error('Failed to fetch creditors:', error);
      setCreditors([]);
    }
  };

  // preserveClosing: if true, keeps existing closing stock values (for Retry scenario)
  const fetchScratchCardOpeningStock = async (storeId, date = null, shiftValue = null, preserveClosing = false) => {
    // Set SCRATCH-SPECIFIC loading state
    setScratchOpeningLoading(true);
    setScratchOpeningError(null); // Clear error at start of fetch
    // Clear opening stock to show loading state
    setScratchCardOpeningStock({});
    // Only clear closing stock if NOT preserving (new date/store selection)
    if (!preserveClosing) {
      setScratchCardClosingStock({});
    }
    
    try {
      // Pass the entry date and shift to get the correct opening stock
      // For Day End: check for Mid Day entry on same date first
      // Otherwise: use previous day's closing stock
      // CRITICAL: date must be the staff-selected sales date, NOT system date
      let params = [];
      if (date) {
        // Handle both Date objects and strings
        const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
        params.push(`entry_date=${dateStr}`);
        console.log(`[ScratchCard] Fetching opening stock for date: ${dateStr}, shift: ${shiftValue}`);
      }
      if (shiftValue) params.push(`shift=${shiftValue}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const response = await axios.get(`${API}/scratch-cards/opening-stock/${storeId}${queryString}`);
      const stockData = response.data || [];
      
      // Create opening stock map by denomination ID
      const openingStock = {};
      stockData.forEach(item => {
        openingStock[item.denomination_id] = item.opening_stock;
      });
      
      console.log(`[ScratchCard] Loaded opening stock:`, openingStock);
      setScratchCardOpeningStock(openingStock);
      // Auto-populate closing stock with opening stock values ONLY if not preserving
      // If preserving (Retry), keep user's manually entered values
      if (!preserveClosing) {
        setScratchCardClosingStock({...openingStock});
      }
      setScratchOpeningError(null);
    } catch (error) {
      console.error('[ScratchCard] Failed to fetch opening stock:', error);
      setScratchOpeningError('Failed to load scratch card opening stock. Please try again.');
      // Clear opening to prevent stale data, but keep closing if preserving (Retry)
      setScratchCardOpeningStock({});
      if (!preserveClosing) {
        setScratchCardClosingStock({});
      }
      setScratchCardOpeningStock({});
      setScratchCardClosingStock({});
    } finally {
      setScratchOpeningLoading(false);
    }
  };

  const fetchStoreDetails = async (storeId) => {
    try {
      const response = await axios.get(`${API}/stores/${storeId}`);
      const store = response.data;
      setStoreDetails(store);
      
      // Initialize department values
      const amounts = {};
      const volumes = {};
      (store.departments || []).forEach(dept => {
        if (dept.field_type === 'amount') {
          amounts[dept.id] = '0.00';
        } else {
          volumes[dept.id] = '0';
        }
      });
      setDepartmentAmounts(amounts);
      setDepartmentVolumes(volumes);

      // Initialize service amounts for enabled services
      const services = {};
      (store.enabled_services || []).forEach(service => {
        if (service.has_amount) {
          services[service.key] = '0.00';
        }
      });
      // Also check the services array (legacy format)
      (store.services || []).forEach(serviceKey => {
        if (!services[serviceKey]) {
          services[serviceKey] = '0.00';
        }
      });
      setServiceAmounts(services);

      // Initialize payment amounts from store payment types
      const payments = {};
      (store.payment_types || []).forEach(pt => {
        payments[pt.id || pt.name] = '0.00';
      });
      // Add defaults if no payment types defined
      if (Object.keys(payments).length === 0) {
        payments['cash'] = '0.00';
        payments['card'] = '0.00';
        payments['vouchers'] = '0.00';
      }
      setPaymentAmounts(payments);

      // Initialize fuel volumes for fuel stations
      if (store.store_type === 'fuel_station') {
        const fuelVols = {};
        const costs = {};
        const prices = {};
        (store.fuel_grades || []).forEach(grade => {
          fuelVols[grade.id] = '0';
          // Get average cost from store fuel grades
          costs[grade.id] = grade.average_price || 0;
          // Initialize prices - will be populated from previous entry
          prices[grade.id] = grade.selling_price ? grade.selling_price.toString() : '';
        });
        setFuelVolumes(fuelVols);
        setFuelCosts(costs);
        setFuelPrices(prices);
        
        // Retail prices come only from admin-set prices (fetchAdminRetailPrices)
        
        // Fetch fuel stock for cost prices tab
        fetchFuelStock(storeId);
        
        // Set default tab to 'fuelcost' for fuel stations and check if costs are completed
        setActiveTab('fuelcost');
        checkFuelCostsCompleted(storeId, selectedDate);
        // Fetch previous day's cost prices for reference
        fetchPreviousDayCostPrices(storeId, selectedDate);
      }

      // Fetch scratch card stock for this store
      fetchScratchCardStock(storeId);
      
      // Fetch fuel card types and initialize if fuel cards are enabled
      if (store.fuel_cards_enabled) {
        // Fetch global fuel card types first
        try {
          const fcResponse = await axios.get(`${API}/settings/fuel-card-types`);
          const activeFuelCardTypes = fcResponse.data.filter(fc => fc.is_active) || [];
          setFuelCardTypes(activeFuelCardTypes);
          
          // Get IDs of globally active fuel card types
          const activeTypeIds = new Set(activeFuelCardTypes.map(fc => fc.id));
          
          // Initialize fuel card transactions structure only for globally active cards
          const nonBunkering = {};
          const bunkering = {};
          (store.active_fuel_cards || []).forEach(card => {
            // Only include if card is active in global settings
            if (activeTypeIds.has(card.card_type_id)) {
              if (card.category === 'non_bunkering') {
                nonBunkering[card.card_name] = '';
              } else if (card.category === 'bunkering') {
                bunkering[card.card_name] = { transactions: [], total_amount: 0 };
              }
            }
          });
          setFuelCardTransactions({
            non_bunkering: nonBunkering,
            bunkering: bunkering,
            key_fuels: { transactions: [], shop_items_amount: 0, total_amount: 0 }
          });
        } catch (error) {
          console.error('Failed to fetch fuel card types:', error);
        }
      }

    } catch (error) {
      console.error('Failed to fetch store details:', error);
    }
  };

  const fetchFuelCardTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/fuel-card-types`);
      setFuelCardTypes(response.data.filter(fc => fc.is_active) || []);
    } catch (error) {
      console.error('Failed to fetch fuel card types:', error);
    }
  };

  const fetchScratchCardStock = async (storeId) => {
    try {
      const response = await axios.get(`${API}/scratch-cards/items`, {
        params: { store_id: storeId }
      });
      const stockMap = {};
      response.data.forEach(item => {
        if (item.denomination_id) {
          stockMap[item.denomination_id] = item.current_stock || 0;
        }
      });
      setScratchCardStock(stockMap);
    } catch (error) {
      console.error('Failed to fetch scratch card stock:', error);
    }
  };

  const fetchExistingEntries = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/sales`, {
        params: { store_id: selectedStoreId, start_date: dateStr, end_date: dateStr }
      });
      setExistingEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    }
  };

  // Fetch all entries for the store to determine the latest entry date (for edit restriction)
  const [allStoreEntries, setAllStoreEntries] = useState([]);
  const fetchAllStoreEntries = async () => {
    try {
      const response = await axios.get(`${API}/sales`, {
        params: { store_id: selectedStoreId }
      });
      setAllStoreEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch all store entries:', error);
    }
  };

  // Fetch fuel stock for cost prices tab
  const fetchFuelStock = async (storeId) => {
    try {
      const response = await axios.get(`${API}/fuel/stock/${storeId}`);
      setFuelStock(response.data);
    } catch (error) {
      console.error('Failed to fetch fuel stock:', error);
    }
  };

  // Check if fuel cost prices have been entered for the selected date
  // New entries always start with a blank New Cost (only a reloaded draft fills it).
  // loadSaved=true (editing/viewing a submitted entry) uses the costs saved for that date.
  const checkFuelCostsCompleted = async (storeId, date, loadSaved = false) => {
    if (!loadSaved) {
      setFuelCostPrices({});
      setFuelCostsCompleted(false);
      setTillReport(null);
      return false;
    }
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/fuel/cost-prices`, {
        params: { store_id: storeId, price_date: dateStr }
      });
      // If cost prices exist for this date, mark as completed
      if (response.data && response.data.prices && Object.keys(response.data.prices).length > 0) {
        // Show the already-entered New Cost values for this date
        setFuelCostPrices(response.data.prices);
        setFuelCostsCompleted(true);
        return true;
      }
      setFuelCostsCompleted(false);
      return false;
    } catch (error) {
      // 404 or any error means no costs entered yet
      setFuelCostsCompleted(false);
      return false;
    }
  };

  // Fetch previous day's cost prices for display
  const fetchPreviousDayCostPrices = async (storeId, date) => {
    try {
      // Calculate previous day - ensure we create a new date object
      const currentDate = date instanceof Date ? new Date(date.getTime()) : new Date(date);
      currentDate.setDate(currentDate.getDate() - 1);
      const prevDateStr = format(currentDate, 'yyyy-MM-dd');
      
      console.log('Fetching previous day cost prices for:', prevDateStr); // Debug log
      
      // Use allow_fallback=true to get costs from the most recent entry if none exist for prev day
      const response = await axios.get(`${API}/fuel/cost-prices`, {
        params: { store_id: storeId, price_date: prevDateStr, allow_fallback: true }
      });
      
      if (response.data && response.data.prices) {
        setPreviousDayCostPrices(response.data.prices);
      } else {
        setPreviousDayCostPrices({});
      }
    } catch (error) {
      // No previous day costs - set empty
      console.error('Error fetching previous day costs:', error);
      setPreviousDayCostPrices({});
    }
  };

  // Fetch admin retail prices for the selected date
  // Prices are set by Admin and are READ-ONLY for staff
  const fetchAdminRetailPrices = async (storeId, date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      console.log('[RetailPrices] Fetching admin retail prices for:', dateStr);
      
      const response = await axios.get(`${API}/fuel/retail-prices`, {
        params: { store_id: storeId, price_date: dateStr, allow_fallback: true }
      });
      
      if (response.data && response.data.prices && Object.keys(response.data.prices).length > 0) {
        setFuelPrices(response.data.prices);
        const gradeDates = [...new Set(Object.values(response.data.grade_dates || {}))].sort().reverse();
        setFuelPriceSourceDate(gradeDates.length > 1 ? gradeDates.join(' / ') : (response.data.source_date || dateStr));
        setFuelPricesSource(response.data.source || 'exact_date');
        console.log('[RetailPrices] Loaded prices:', response.data.prices, 'from:', response.data.source);
      } else {
        setFuelPrices({});
        setFuelPriceSourceDate(null);
        setFuelPricesSource('none');
        console.log('[RetailPrices] No prices found for this date');
      }
    } catch (error) {
      console.error('[RetailPrices] Error fetching admin retail prices:', error);
      setFuelPrices({});
      setFuelPriceSourceDate(null);
      setFuelPricesSource('none');
    }
  };

  // Check for existing approval request for this entry
  const checkApprovalRequest = async (storeId, salesDate, shift) => {
    try {
      const dateStr = typeof salesDate === 'string' ? salesDate : format(salesDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/approval-requests/check`, {
        params: { store_id: storeId, sales_date: dateStr, shift }
      });
      
      if (response.data.has_request) {
        setApprovalRequest(response.data.request);
        console.log('[ApprovalRequest] Found existing request:', response.data.request.status);
      } else {
        setApprovalRequest(null);
      }
    } catch (error) {
      console.error('[ApprovalRequest] Error checking approval request:', error);
      setApprovalRequest(null);
    }
  };

  // Request admin approval for variance
  const requestVarianceApproval = async () => {
    if (!selectedStoreId || !selectedDate || !selectedShift) return;
    
    setRequestingApproval(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      const draftData = buildDraftData();
      
      const response = await axios.post(`${API}/approval-requests`, {
        store_id: selectedStoreId,
        sales_date: dateStr,
        shift: selectedShift,
        variance_type: 'sales_variance',
        variance_amount: varianceDetails?.variance || 0,
        variance_details: varianceDetails,
        staff_comment: varianceComment.trim(),
        draft_data: draftData
      });
      
      if (response.data.success) {
        toast.success('Approval request submitted. Entry is now locked pending admin review.');
        setVarianceComment('');
        setVarianceApprovalDialogOpen(false);
        checkApprovalRequest(selectedStoreId, dateStr, selectedShift);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit approval request');
    } finally {
      setRequestingApproval(false);
    }
  };

  // Save fuel cost prices
  const saveFuelCostPrices = async () => {
    if (!selectedStoreId) return;
    
    setSavingCostPrices(true);
    try {
      await axios.post(`${API}/fuel/cost-prices`, {
        store_id: selectedStoreId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        prices: Object.entries(fuelCostPrices).map(([gradeId, price]) => ({
          grade_id: gradeId,
          cost_price: parseFloat(price) || 0
        }))
      });
      toast.success('Cost prices saved successfully! Now upload the Day End report.');
      if (!isEditing && !isViewMode) setActiveTab('dayend');
      // Mark fuel costs as completed to unlock other tabs
      setFuelCostsCompleted(true);
      // DO NOT clear fuelCostPrices - keep them so drafts preserve the values
      // This allows Save Draft -> Load Draft to skip the fuel cost screen
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cost prices');
    } finally {
      setSavingCostPrices(false);
    }
  };

  // Format pennies for display (e.g., 123.456 => "123.456")
  const formatPennies = (value, decimals = 3) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.000' : num.toFixed(decimals);
  };

  // Load existing entry data for editing (managers/admins)
  const loadEntryForEditing = (entry) => {
    setIsEditing(true);
    setEditingEntryId(entry.id);
    
    // Load department amounts (field may be department_amounts or department_sales)
    const deptAmounts = entry.department_amounts || entry.department_sales;
    if (deptAmounts) {
      setDepartmentAmounts(
        Object.fromEntries(
          Object.entries(deptAmounts).map(([k, v]) => [k, formatAmount(v)])
        )
      );
    }
    
    // Load service amounts (field may be service_amounts or service_sales)
    const svcAmounts = entry.service_amounts || entry.service_sales;
    if (svcAmounts) {
      setServiceAmounts(
        Object.fromEntries(
          Object.entries(svcAmounts).map(([k, v]) => [k, formatAmount(v)])
        )
      );
    }
    
    // Load payment amounts - properly match with store payment types
    const loadedPayments = {};
    
    // First, try to load from payment_amounts if it exists in the entry
    if (entry.payment_amounts && Object.keys(entry.payment_amounts).length > 0) {
      Object.entries(entry.payment_amounts).forEach(([k, v]) => {
        loadedPayments[k] = formatAmount(v);
      });
    }
    
    // ALWAYS populate the hardcoded keys that the input fields use
    // This ensures the values display correctly regardless of how they were stored
    if (entry.cash_amount !== undefined && entry.cash_amount !== null) {
      loadedPayments['cash'] = formatAmount(entry.cash_amount);
      loadedPayments['Cash'] = formatAmount(entry.cash_amount);
    }
    if (entry.credit_card_amount !== undefined && entry.credit_card_amount !== null) {
      loadedPayments['card'] = formatAmount(entry.credit_card_amount);
      loadedPayments['Card Payments'] = formatAmount(entry.credit_card_amount);
      loadedPayments['Card Payments (BMS)'] = formatAmount(entry.credit_card_amount);
    }
    if (entry.vouchers_amount !== undefined && entry.vouchers_amount !== null) {
      loadedPayments['vouchers'] = formatAmount(entry.vouchers_amount);
      loadedPayments['Voucher'] = formatAmount(entry.vouchers_amount);
      loadedPayments['Vouchers'] = formatAmount(entry.vouchers_amount);
    }
    
    // Also load to payment type IDs for custom payment types
    if (storeDetails?.payment_types?.length > 0) {
      storeDetails.payment_types.forEach(pt => {
        const key = pt.id || pt.name;
        const ptName = (pt.name || '').toLowerCase();
        // Map payment type to individual amount fields if not already set
        if (loadedPayments[key] === undefined || loadedPayments[key] === '0.00') {
          if (ptName === 'cash' && entry.cash_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.cash_amount);
          } else if ((ptName === 'card payments' || ptName === 'card' || ptName === 'card payments (bms)') && entry.credit_card_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.credit_card_amount);
          } else if ((ptName === 'voucher' || ptName === 'vouchers') && entry.vouchers_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.vouchers_amount);
          }
        }
      });
    }
    
    if (Object.keys(loadedPayments).length > 0) {
      setPaymentAmounts(prev => ({ ...prev, ...loadedPayments }));
    }
    
    // Load fuel data for fuel stations
    if (entry.fuel_volumes) {
      setFuelVolumes(
        Object.fromEntries(
          Object.entries(entry.fuel_volumes).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    if (entry.fuel_costs) {
      setFuelCosts(entry.fuel_costs);
    }
    if (entry.fuel_prices) {
      setFuelPrices(
        Object.fromEntries(
          Object.entries(entry.fuel_prices).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    
    // Load prizes paid
    setLotteryPrizePaid(formatAmount(entry.lottery_prize_paid || 0));
    setScratchPrizePaid(formatAmount(entry.scratch_card_prize_paid || 0));
    
    // Load paid outs
    if (entry.paid_outs) {
      setPaidOuts(entry.paid_outs);
    }
    
    // Load variance reason
    setVarianceReason(entry.variance_reason || '');
    
    // Load scratch card quantities and settled
    if (entry.scratch_card_quantities) {
      setScratchCardQuantities(
        Object.fromEntries(
          Object.entries(entry.scratch_card_quantities).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    if (entry.scratch_card_settled) {
      setScratchCardSettled(
        Object.fromEntries(
          Object.entries(entry.scratch_card_settled).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    // Load settled pack numbers
    if (entry.scratch_card_settled_pack_numbers) {
      setScratchCardSettledPackNumbers(entry.scratch_card_settled_pack_numbers);
    }
    if (entry.scratch_card_closing_stock) {
      setScratchCardClosingStock(
        Object.fromEntries(
          Object.entries(entry.scratch_card_closing_stock).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    
    // Load or calculate scratch card opening stock for editing
    // Opening Stock = Closing Stock + Sold Qty - Settled
    // Where Sold Qty = scratch_card_quantities (what was sold)
    if (entry.scratch_card_opening_stock) {
      // If opening stock was saved, use it
      setScratchCardOpeningStock(
        Object.fromEntries(
          Object.entries(entry.scratch_card_opening_stock).map(([k, v]) => [k, v.toString()])
        )
      );
    } else if (entry.scratch_card_closing_stock) {
      // Calculate opening stock from: Opening = Closing + Sold - Settled
      const calculatedOpeningStock = {};
      Object.keys(entry.scratch_card_closing_stock).forEach(denomId => {
        const closing = parseInt(entry.scratch_card_closing_stock[denomId]) || 0;
        const sold = parseInt(entry.scratch_card_quantities?.[denomId]) || 0;
        const settled = parseInt(entry.scratch_card_settled?.[denomId]) || 0;
        // Opening = Closing + Sold - Settled
        calculatedOpeningStock[denomId] = (closing + sold - settled).toString();
      });
      setScratchCardOpeningStock(calculatedOpeningStock);
    }
    
    // Mark scratch card as verified since we're loading existing data
    setScratchCardVerified(true);
    
    // Load Total Sales (Z-reading) if present
    if (entry.z_reading_total !== undefined && entry.z_reading_total !== null) {
      setTotalSalesAmount(formatAmount(entry.z_reading_total));
    } else if (entry.total_sales_amount !== undefined && entry.total_sales_amount !== null) {
      // Fallback to total_sales_amount for backward compatibility
      setTotalSalesAmount(formatAmount(entry.total_sales_amount));
    }
    
    // Load Fuel Sales department amount if present
    if (entry.fuel_sales_dept_amount !== undefined && entry.fuel_sales_dept_amount !== null) {
      setFuelSalesDeptAmount(formatAmount(entry.fuel_sales_dept_amount));
    }
    
    // Load Drive Off department amount if present
    if (entry.driveoff_dept_amount !== undefined && entry.driveoff_dept_amount !== null) {
      setDriveOffDeptAmount(formatAmount(entry.driveoff_dept_amount));
    }
    
    // Load Retail Sales amount if present
    if (entry.retail_sales_amount !== undefined && entry.retail_sales_amount !== null) {
      setRetailSalesAmount(formatAmount(entry.retail_sales_amount));
    }
    
    // Load Bunkering Sales amount if present
    if (entry.bunkering_sales_amount !== undefined && entry.bunkering_sales_amount !== null) {
      setBunkeringSalesAmount(formatAmount(entry.bunkering_sales_amount));
    }
    
    // Load Fuel Card Transactions (Bunkering data)
    if (entry.fuel_card_transactions) {
      const fct = entry.fuel_card_transactions;
      const loadedFCT = {
        non_bunkering: {},
        bunkering: {},
        key_fuels: { transactions: [], shop_items_amount: 0, total_amount: 0 }
      };
      
      // Load non-bunkering card amounts
      if (fct.non_bunkering) {
        Object.entries(fct.non_bunkering).forEach(([cardName, amount]) => {
          loadedFCT.non_bunkering[cardName] = formatAmount(amount);
        });
      }
      
      // Load bunkering card data (with transactions)
      if (fct.bunkering) {
        Object.entries(fct.bunkering).forEach(([cardName, cardData]) => {
          if (typeof cardData === 'object') {
            loadedFCT.bunkering[cardName] = {
              transactions: cardData.transactions || [],
              total_amount: cardData.total_amount || 0
            };
          }
        });
      }
      
      // Load Key Fuels data
      if (fct.key_fuels) {
        loadedFCT.key_fuels = {
          transactions: fct.key_fuels.transactions || [],
          shop_items_amount: fct.key_fuels.shop_items_amount || 0,
          total_amount: fct.key_fuels.total_amount || 0
        };
      }
      
      setFuelCardTransactions(loadedFCT);
    }
    
    // Load Coffee Tracking data if present
    if (entry.coffee_tracking) {
      const ct = entry.coffee_tracking;
      setCoffeeTracking({
        coffee_sold_enabled: storeDetails?.coffee_tracking?.coffee_sold_enabled || false,
        coffee_sold_qty: ct.coffee_sold_qty?.toString() || '',
        small: {
          enabled: storeDetails?.coffee_tracking?.small_enabled || false,
          opening_reading: ct.small?.opening_reading?.toString() || '',
          closing_reading: ct.small?.closing_reading?.toString() || '',
          calculated_sold: ct.small?.calculated_sold || 0
        },
        large: {
          enabled: storeDetails?.coffee_tracking?.large_enabled || false,
          opening_reading: ct.large?.opening_reading?.toString() || '',
          closing_reading: ct.large?.closing_reading?.toString() || '',
          calculated_sold: ct.large?.calculated_sold || 0
        }
      });
    }
    
    toast.info('Entry loaded for editing');
  };

  // Cancel editing and reset form
  const cancelEditing = () => {
    setIsEditing(false);
    setEditingEntryId(null);
    // Clear deferred incident deletions and restore incidents from server
    setIncidentsToDelete([]);
    if (storeDetails?.store_type === 'fuel_station') {
      fetchIncidentsForDate();
    }
    resetForm();
  };

  // Load existing entry data for viewing (read-only mode for all users)
  const loadEntryForViewing = (entry) => {
    setIsViewMode(true);
    setEditingEntryId(entry.id);
    if (storeDetails?.store_type === 'fuel_station') {
      checkFuelCostsCompleted(selectedStoreId, selectedDate, true);
    }
    
    // Load department amounts (field may be department_amounts or department_sales)
    const deptAmounts = entry.department_amounts || entry.department_sales;
    if (deptAmounts) {
      setDepartmentAmounts(
        Object.fromEntries(
          Object.entries(deptAmounts).map(([k, v]) => [k, formatAmount(v)])
        )
      );
    }
    
    // Load service amounts (field may be service_amounts or service_sales)
    const svcAmounts = entry.service_amounts || entry.service_sales;
    if (svcAmounts) {
      setServiceAmounts(
        Object.fromEntries(
          Object.entries(svcAmounts).map(([k, v]) => [k, formatAmount(v)])
        )
      );
    }
    
    // Load payment amounts - properly match with store payment types
    const loadedPayments = {};
    
    // First, try to load from payment_amounts if it exists in the entry
    if (entry.payment_amounts && Object.keys(entry.payment_amounts).length > 0) {
      Object.entries(entry.payment_amounts).forEach(([k, v]) => {
        loadedPayments[k] = formatAmount(v);
      });
    }
    
    // ALWAYS populate the hardcoded keys that the input fields use
    if (entry.cash_amount !== undefined && entry.cash_amount !== null) {
      loadedPayments['cash'] = formatAmount(entry.cash_amount);
      loadedPayments['Cash'] = formatAmount(entry.cash_amount);
    }
    if (entry.credit_card_amount !== undefined && entry.credit_card_amount !== null) {
      loadedPayments['card'] = formatAmount(entry.credit_card_amount);
      loadedPayments['Card Payments'] = formatAmount(entry.credit_card_amount);
      loadedPayments['Card Payments (BMS)'] = formatAmount(entry.credit_card_amount);
    }
    if (entry.vouchers_amount !== undefined && entry.vouchers_amount !== null) {
      loadedPayments['vouchers'] = formatAmount(entry.vouchers_amount);
      loadedPayments['Voucher'] = formatAmount(entry.vouchers_amount);
      loadedPayments['Vouchers'] = formatAmount(entry.vouchers_amount);
    }
    
    // Also load to payment type IDs for custom payment types
    if (storeDetails?.payment_types?.length > 0) {
      storeDetails.payment_types.forEach(pt => {
        const key = pt.id || pt.name;
        const ptName = (pt.name || '').toLowerCase();
        if (loadedPayments[key] === undefined || loadedPayments[key] === '0.00') {
          if (ptName === 'cash' && entry.cash_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.cash_amount);
          } else if ((ptName === 'card payments' || ptName === 'card' || ptName === 'card payments (bms)') && entry.credit_card_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.credit_card_amount);
          } else if ((ptName === 'voucher' || ptName === 'vouchers') && entry.vouchers_amount !== undefined) {
            loadedPayments[key] = formatAmount(entry.vouchers_amount);
          }
        }
      });
    }
    
    if (Object.keys(loadedPayments).length > 0) {
      setPaymentAmounts(prev => ({ ...prev, ...loadedPayments }));
    }
    
    // Load fuel data for fuel stations (mirror loadEntryForEditing logic exactly)
    if (entry.fuel_volumes) {
      setFuelVolumes(
        Object.fromEntries(
          Object.entries(entry.fuel_volumes).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    if (entry.fuel_costs) {
      setFuelCosts(entry.fuel_costs);
    }
    if (entry.fuel_prices) {
      setFuelPrices(
        Object.fromEntries(
          Object.entries(entry.fuel_prices).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    
    // Load lottery and scratch prize
    setLotteryPrizePaid(formatAmount(entry.lottery_prize_paid || 0));
    setScratchPrizePaid(formatAmount(entry.scratch_card_prize_paid || 0));
    
    // Load paid outs
    if (entry.paid_outs) {
      setPaidOuts(entry.paid_outs);
    }
    
    // Load variance reason
    setVarianceReason(entry.variance_reason || '');
    
    // Load scratch card quantities and settled (mirror loadEntryForEditing exactly)
    if (entry.scratch_card_quantities) {
      setScratchCardQuantities(
        Object.fromEntries(
          Object.entries(entry.scratch_card_quantities).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    if (entry.scratch_card_settled) {
      setScratchCardSettled(
        Object.fromEntries(
          Object.entries(entry.scratch_card_settled).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    // Load settled pack numbers
    if (entry.scratch_card_settled_pack_numbers) {
      setScratchCardSettledPackNumbers(entry.scratch_card_settled_pack_numbers);
    }
    if (entry.scratch_card_closing_stock) {
      setScratchCardClosingStock(
        Object.fromEntries(
          Object.entries(entry.scratch_card_closing_stock).map(([k, v]) => [k, v.toString()])
        )
      );
    }
    
    // Load or calculate scratch card opening stock for viewing
    // Opening Stock = Closing Stock + Sold Qty - Settled
    if (entry.scratch_card_opening_stock) {
      setScratchCardOpeningStock(
        Object.fromEntries(
          Object.entries(entry.scratch_card_opening_stock).map(([k, v]) => [k, v.toString()])
        )
      );
    } else if (entry.scratch_card_closing_stock) {
      // Calculate opening stock from: Opening = Closing + Sold - Settled
      const calculatedOpeningStock = {};
      Object.keys(entry.scratch_card_closing_stock).forEach(denomId => {
        const closing = parseInt(entry.scratch_card_closing_stock[denomId]) || 0;
        const sold = parseInt(entry.scratch_card_quantities?.[denomId]) || 0;
        const settled = parseInt(entry.scratch_card_settled?.[denomId]) || 0;
        calculatedOpeningStock[denomId] = (closing + sold - settled).toString();
      });
      setScratchCardOpeningStock(calculatedOpeningStock);
    }
    
    // Mark scratch card as verified since we're loading existing data
    setScratchCardVerified(true);
    
    // Load Total Sales (Z-reading) if present
    if (entry.z_reading_total !== undefined && entry.z_reading_total !== null) {
      setTotalSalesAmount(formatAmount(entry.z_reading_total));
    } else if (entry.total_sales_amount !== undefined && entry.total_sales_amount !== null) {
      setTotalSalesAmount(formatAmount(entry.total_sales_amount));
    }
    
    // Load Fuel Sales department amount if present
    if (entry.fuel_sales_dept_amount !== undefined && entry.fuel_sales_dept_amount !== null) {
      setFuelSalesDeptAmount(formatAmount(entry.fuel_sales_dept_amount));
    }
    
    // Load Drive Off department amount if present
    if (entry.driveoff_dept_amount !== undefined && entry.driveoff_dept_amount !== null) {
      setDriveOffDeptAmount(formatAmount(entry.driveoff_dept_amount));
    }
    
    // Load Retail Sales amount if present
    if (entry.retail_sales_amount !== undefined && entry.retail_sales_amount !== null) {
      setRetailSalesAmount(formatAmount(entry.retail_sales_amount));
    }
    
    // Load Bunkering Sales amount if present
    if (entry.bunkering_sales_amount !== undefined && entry.bunkering_sales_amount !== null) {
      setBunkeringSalesAmount(formatAmount(entry.bunkering_sales_amount));
    }
    
    // Load Fuel Card Transactions (Bunkering data)
    if (entry.fuel_card_transactions) {
      const fct = entry.fuel_card_transactions;
      const loadedFCT = {
        non_bunkering: {},
        bunkering: {},
        key_fuels: { transactions: [], shop_items_amount: 0, total_amount: 0 }
      };
      
      // Load non-bunkering card amounts
      if (fct.non_bunkering) {
        Object.entries(fct.non_bunkering).forEach(([cardName, amount]) => {
          loadedFCT.non_bunkering[cardName] = formatAmount(amount);
        });
      }
      
      // Load bunkering card data (with transactions)
      if (fct.bunkering) {
        Object.entries(fct.bunkering).forEach(([cardName, cardData]) => {
          if (typeof cardData === 'object') {
            loadedFCT.bunkering[cardName] = {
              transactions: cardData.transactions || [],
              total_amount: cardData.total_amount || 0
            };
          }
        });
      }
      
      // Load Key Fuels data
      if (fct.key_fuels) {
        loadedFCT.key_fuels = {
          transactions: fct.key_fuels.transactions || [],
          shop_items_amount: fct.key_fuels.shop_items_amount || 0,
          total_amount: fct.key_fuels.total_amount || 0
        };
      }
      
      setFuelCardTransactions(loadedFCT);
    }
    
    // Load Coffee Tracking data if present
    if (entry.coffee_tracking) {
      const ct = entry.coffee_tracking;
      setCoffeeTracking({
        coffee_sold_enabled: storeDetails?.coffee_tracking?.coffee_sold_enabled || false,
        coffee_sold_qty: ct.coffee_sold_qty?.toString() || '',
        small: {
          enabled: storeDetails?.coffee_tracking?.small_enabled || false,
          opening_reading: ct.small?.opening_reading?.toString() || '',
          closing_reading: ct.small?.closing_reading?.toString() || '',
          calculated_sold: ct.small?.calculated_sold || 0
        },
        large: {
          enabled: storeDetails?.coffee_tracking?.large_enabled || false,
          opening_reading: ct.large?.opening_reading?.toString() || '',
          closing_reading: ct.large?.closing_reading?.toString() || '',
          calculated_sold: ct.large?.calculated_sold || 0
        }
      });
    }
    
    toast.info('Entry loaded for viewing (read-only)');
  };

  // Cancel viewing and reset form
  const cancelViewing = () => {
    setIsViewMode(false);
    setEditingEntryId(null);
    resetForm();
  };

  // Reset form to initial state
  const resetForm = () => {
    setTillReport(null);
    const amounts = {};
    const vols = {};
    storeDetails?.departments?.forEach(dept => {
      if (dept.field_type === 'amount') {
        amounts[dept.id] = '0.00';
      } else {
        vols[dept.id] = '0';
      }
    });
    setDepartmentAmounts(amounts);
    setDepartmentVolumes(vols);
    
    const payments = {};
    storeDetails?.payment_types?.forEach(pt => {
      if (pt.is_active) {
        payments[pt.name] = '0.00';
      }
    });
    setPaymentAmounts(payments);
    
    setServiceAmounts({});
    setLotteryPrizePaid('0.00');
    setScratchPrizePaid('0.00');
    setPaidOuts([]);
    setVarianceReason('');
    setScratchCardQuantities({});
    setScratchCardSettled({});
    setScratchCardSettledPackNumbers({});
    setScratchCardClosingStock({});
    setScratchCardVerified(false);
    setTotalSalesAmount('0.00');
    setFuelSalesDeptAmount('0.00');
    setDriveOffDeptAmount('0.00');
    // Clear deferred incident deletions and recoveries
    setIncidentsToDelete([]);
    setIncidentsToRecover([]);
    setIncidentSearchResults([]);
    setSelectedRecoveryIncidents([]);
    // Clear pending incidents (not yet saved to DB)
    setPendingIncidents([]);
  };

  const handleStoreChange = (storeId) => {
    // If there are unsaved changes, show confirmation dialog
    if (hasUnsavedChanges) {
      setPendingStoreId(storeId);
      setStoreChangeDialogOpen(true);
      return;
    }
    
    applyStoreChange(storeId);
  };
  
  const applyStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
      fetchStoreDetails(storeId);
      fetchScratchCardOpeningStock(storeId, selectedDate, selectedShift);
      fetchLastEntryDate(storeId); // Auto-set date for staff when store changes
    }
  };
  
  // Apply store change - clear form for new store AND delete recorded incidents
  const applyStoreChangeWithClear = async () => {
    // Delete any OPEN incidents recorded for the current store/date before clearing
    // But only delete incidents that are actually in the DB (not pending ones)
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const openIncidents = incidents.filter(i => 
      i.store_id === selectedStoreId && i.incident_date === dateStr && i.status === 'open' && !i.isPending
    );
    
    if (openIncidents.length > 0) {
      try {
        // Delete each open incident recorded for this store/date
        for (const incident of openIncidents) {
          await axios.delete(`${API}/incidents/${incident.id}`);
        }
        toast.info(`${openIncidents.length} recorded incident(s) deleted`);
      } catch (error) {
        console.error('Failed to delete incidents:', error);
        toast.error('Failed to delete some incidents');
      }
    }
    
    // Notify about discarded pending incidents
    const pendingCount = pendingIncidents.length;
    if (pendingCount > 0) {
      toast.warning(`${pendingCount} unsaved incident(s) discarded`);
    }
    
    clearFormData();
    setIncidents([]); // Clear incidents state
    applyStoreChange(pendingStoreId);
    setStoreChangeDialogOpen(false);
    setPendingStoreId(null);
    toast.info('Form cleared for new store');
  };

  // Apply store change - keep data (move to new store)
  const applyStoreChangeKeepData = () => {
    applyStoreChange(pendingStoreId);
    setStoreChangeDialogOpen(false);
    setPendingStoreId(null);
    toast.info('Data kept - will be saved to new store');
  };

  const handleAmountChange = (key, value, setter, currentValues) => {
    // Mark that user has interacted with the form
    setUserHasInteracted(true);
    // Allow empty string for clearing
    if (value === '') {
      setter({ ...currentValues, [key]: '' });
      return;
    }
    // Allow numbers with optional decimal point
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      setter({ ...currentValues, [key]: value });
    }
  };

  const handleAmountBlur = (key, setter, currentValues) => {
    // Format to 2 decimal places on blur, or 0.00 if empty
    const value = currentValues[key];
    const num = parseFloat(value);
    const formatted = isNaN(num) || value === '' ? '0.00' : num.toFixed(2);
    setter({ ...currentValues, [key]: formatted });
  };

  // Handle amount focus - select all text for easy replacement
  const handleAmountFocus = (e) => {
    e.target.select();
  };

  const addPaidOut = () => {
    if (!newPaidOut.paid_out_type_id) {
      toast.error('Please select a paid out type');
      return;
    }
    // Require supplier selection when type is "Supplier Payment"
    if (newPaidOut.paid_out_type_name === 'Supplier Payment' && !newPaidOut.supplier_id) {
      toast.error('Please select a supplier for Supplier Payment');
      return;
    }
    // Require creditor selection when type is "Creditors"
    if (newPaidOut.paid_out_type_name === 'Creditors' && !newPaidOut.creditor_id) {
      toast.error('Please select a creditor');
      return;
    }
    if (!newPaidOut.description) {
      toast.error('Please enter a description');
      return;
    }
    if (parseFloat(newPaidOut.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (editingPaidOutId) {
      const description = newPaidOut.description.replace(/ - (supplier not set up, contact admin|paid out type not matched, please edit|select supplier)$/, '');
      setPaidOuts(paidOuts.map(p => p.id === editingPaidOutId ? { ...newPaidOut, description, id: editingPaidOutId, till_warning: false } : p));
      setEditingPaidOutId(null);
    } else {
      setPaidOuts([...paidOuts, { ...newPaidOut, id: Date.now().toString() }]);
    }
    setNewPaidOut(EMPTY_PAID_OUT);
  };

  const editPaidOut = (p) => {
    setEditingPaidOutId(p.id);
    setNewPaidOut({ ...EMPTY_PAID_OUT, ...p, amount: (parseFloat(p.amount) || 0).toFixed(2) });
  };

  const cancelEditPaidOut = () => {
    setEditingPaidOutId(null);
    setNewPaidOut(EMPTY_PAID_OUT);
  };

  const removePaidOut = (id) => {
    if (id === editingPaidOutId) cancelEditPaidOut();
    setPaidOuts(paidOuts.filter(p => p.id !== id));
  };

  // Commission handlers
  const addCommission = async () => {
    if (!newCommission.commission_type) {
      toast.error('Please select a commission type');
      return;
    }
    if (parseFloat(newCommission.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const commissionType = commissionTypes.find(t => t.id === newCommission.commission_type);
      
      await axios.post(`${API}/commissions`, {
        store_id: selectedStoreId,
        commission_date: dateStr,
        commission_type: newCommission.commission_type,
        description: newCommission.description || commissionType?.name || '',
        amount: parseFloat(newCommission.amount)
      });
      
      toast.success('Commission added successfully');
      fetchCommissionsForDate();
      setNewCommission({ commission_type: '', description: '', amount: '' });
    } catch (error) {
      console.error('Failed to add commission:', error);
      toast.error(error.response?.data?.detail || 'Failed to add commission');
    }
  };

  const deleteCommission = async (commissionId) => {
    if (!window.confirm('Are you sure you want to delete this commission?')) return;
    
    try {
      await axios.delete(`${API}/commissions/${commissionId}`);
      toast.success('Commission deleted');
      fetchCommissionsForDate();
    } catch (error) {
      console.error('Failed to delete commission:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete commission');
    }
  };

  // Incident handlers
  const addIncident = async () => {
    if (!newIncident.incident_time || !newIncident.pump_number || !newIncident.fuel_grade_id || !newIncident.litres) {
      toast.error('Please fill in all required fields (Time, Pump, Fuel Grade, Litres)');
      return;
    }
    
    // Validate time range (06:00-22:00)
    const [hours] = newIncident.incident_time.split(':').map(Number);
    if (hours < 6 || hours >= 22) {
      toast.error('Incident time must be between 06:00 and 22:00');
      return;
    }
    
    if (newIncident.incident_type === 'indebtedness' && !newIncident.agreement_signed) {
      toast.error('Credit agreement must be signed for indebtedness incidents');
      return;
    }
    
    if (newIncident.incident_type === 'indebtedness' && !newIncident.customer_address?.trim()) {
      toast.error('Customer address is required for indebtedness incidents');
      return;
    }

    // Instead of saving to DB immediately, add to pending incidents list
    // Will be saved to DB only on successful sales entry submission
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const pendingIncident = {
      ...newIncident,
      id: tempId,
      store_id: selectedStoreId,
      incident_date: dateStr,
      pump_number: parseInt(newIncident.pump_number),
      litres: parseFloat(newIncident.litres),
      amount: parseFloat(newIncident.amount),
      status: 'pending', // Mark as pending (not yet saved to DB)
      isPending: true // Flag to identify pending incidents
    };
    
    setPendingIncidents(prev => [...prev, pendingIncident]);
    
    // Also add to incidents list for display
    setIncidents(prev => [...prev, pendingIncident]);
    
    toast.success('Incident added (will be saved on submission)');
    setNewIncident({
      incident_type: 'drive_off',
      incident_time: '',
      pump_number: '',
      fuel_grade_id: '',
      fuel_grade_name: '',
      litres: '',
      amount: '',
      vehicle_registration: '',
      vehicle_description: '',
      police_reference: '',
      customer_name: '',
      customer_phone: '',
      customer_address: '',
      agreement_signed: false,
      notes: '',
      cashier_id: '',
      cashier_name: ''
    });
  };

  const handleIncidentLitresChange = (value) => {
    const litres = value;
    let calculatedAmount = 0;
    
    if (litres && newIncident.fuel_grade_id && storeDetails?.fuel_grades) {
      const grade = storeDetails.fuel_grades.find(g => g.id === newIncident.fuel_grade_id);
      if (grade) {
        // Use current day's price from fuelPrices state (entered on Fuel Sales tab)
        // Fall back to store's default price if not entered yet
        const currentPrice = parseFloat(fuelPrices[grade.id]) || grade.selling_price || 0;
        calculatedAmount = (parseFloat(litres) * currentPrice) / 100;
      }
    }
    
    setNewIncident(prev => ({
      ...prev,
      litres: litres,
      amount: calculatedAmount.toFixed(2)
    }));
  };

  const handleMarkIncidentPaid = async (incident) => {
    try {
      // Mark as paid at store - no payment method needed, counts as income
      await axios.put(`${API}/incidents/${incident.id}/pay`, {
        payment_type: 'store',
        payment_method: 'cash', // Default method, but amount counts as income
        notes: 'Marked as paid via Sales Entry'
      });
      toast.success('Incident marked as paid - amount will count as income');
      fetchIncidentsForDate();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleIncidentWriteOff = async (incident) => {
    if (!window.confirm('Are you sure you want to write off this incident?')) return;
    
    try {
      await axios.put(`${API}/incidents/${incident.id}/write-off`);
      toast.success('Incident written off');
      fetchIncidentsForDate();
    } catch (error) {
      console.error('Failed to write off:', error);
      toast.error(error.response?.data?.detail || 'Failed to write off');
    }
  };

  // Delete incident
  const handleDeleteIncident = async (incident) => {
    if (!window.confirm(`Are you sure you want to delete this ${incident.incident_type === 'drive_off' ? 'Drive Off' : 'Indebtedness'} incident for £${incident.amount?.toFixed(2)}?`)) return;
    
    // If this is a pending incident (not yet saved to DB), just remove from state
    if (incident.isPending) {
      setPendingIncidents(prev => prev.filter(i => i.id !== incident.id));
      setIncidents(prev => prev.filter(i => i.id !== incident.id));
      toast.success('Pending incident removed');
      return;
    }
    
    // If editing an existing entry, defer the deletion until successful submission
    if (isEditing) {
      // Add to deferred deletion list
      setIncidentsToDelete(prev => [...prev, incident.id]);
      // Remove from UI immediately (but not from database yet)
      setIncidents(prev => prev.filter(i => i.id !== incident.id));
      toast.info('Incident marked for deletion - will be removed when you submit');
      return;
    }
    
    // For new entries, delete immediately
    try {
      await axios.delete(`${API}/incidents/${incident.id}`);
      toast.success('Incident deleted');
      fetchIncidentsForDate();
    } catch (error) {
      console.error('Failed to delete incident:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete incident');
    }
  };

  // Edit incident - load into form
  const [editingIncident, setEditingIncident] = useState(null);
  
  const handleEditIncident = (incident) => {
    setEditingIncident(incident);
    setNewIncident({
      incident_type: incident.incident_type,
      incident_time: incident.incident_time || '',
      pump_number: incident.pump_number?.toString() || '',
      fuel_grade_id: incident.fuel_grade_id || '',
      fuel_grade_name: incident.fuel_grade_name || '',
      litres: incident.litres?.toString() || '',
      amount: incident.amount?.toFixed(2) || '',
      vehicle_registration: incident.vehicle_registration || '',
      vehicle_description: incident.vehicle_description || '',
      police_reference: incident.police_reference || '',
      customer_name: incident.customer_name || '',
      customer_phone: incident.customer_phone || '',
      agreement_signed: incident.agreement_signed || false,
      notes: incident.notes || '',
      cashier_id: incident.cashier_id || '',
      cashier_name: incident.cashier_name || ''
    });
  };

  // Update incident
  const handleUpdateIncident = async () => {
    if (!editingIncident) return;
    
    // Validate time - must be between 06:00 and 22:00
    if (newIncident.incident_time) {
      const [hours] = newIncident.incident_time.split(':').map(Number);
      if (hours < 6 || hours >= 22) {
        toast.error('Incident time must be between 06:00 and 22:00');
        return;
      }
    }
    
    if (newIncident.incident_type === 'indebtedness' && !newIncident.agreement_signed) {
      toast.error('Credit agreement must be signed for indebtedness incidents');
      return;
    }
    
    if (newIncident.incident_type === 'indebtedness' && !newIncident.customer_address?.trim()) {
      toast.error('Customer address is required for indebtedness incidents');
      return;
    }

    try {
      await axios.put(`${API}/incidents/${editingIncident.id}`, {
        ...newIncident,
        pump_number: parseInt(newIncident.pump_number),
        litres: parseFloat(newIncident.litres),
        amount: parseFloat(newIncident.amount)
      });
      
      toast.success('Incident updated successfully');
      setEditingIncident(null);
      fetchIncidentsForDate();
      setNewIncident({
        incident_type: 'drive_off',
        incident_time: '',
        pump_number: '',
        fuel_grade_id: '',
        fuel_grade_name: '',
        litres: '',
        amount: '',
        vehicle_registration: '',
        vehicle_description: '',
        police_reference: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        agreement_signed: false,
        notes: '',
        cashier_id: '',
        cashier_name: ''
      });
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast.error(error.response?.data?.detail || 'Failed to update incident');
    }
  };

  // Cancel editing
  const handleCancelEditIncident = () => {
    setEditingIncident(null);
    setNewIncident({
      incident_type: 'drive_off',
      incident_time: '',
      pump_number: '',
      fuel_grade_id: '',
      fuel_grade_name: '',
      litres: '',
      amount: '',
      vehicle_registration: '',
      vehicle_description: '',
      police_reference: '',
      customer_name: '',
      customer_phone: '',
      agreement_signed: false,
      notes: '',
      cashier_id: '',
      cashier_name: ''
    });
  };

  // Search past incidents for payment recovery
  const handleIncidentSearch = async () => {
    if (!incidentSearchDateFrom && !incidentSearchDateTo && !incidentSearchCarReg && !incidentSearchAmount) {
      toast.error('Please enter at least one search criterion');
      return;
    }
    
    setIncidentSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (incidentSearchDateFrom) params.append('date_from', incidentSearchDateFrom);
      if (incidentSearchDateTo) params.append('date_to', incidentSearchDateTo);
      if (incidentSearchCarReg) params.append('car_reg', incidentSearchCarReg);
      if (incidentSearchAmount) params.append('amount', incidentSearchAmount);
      
      const response = await axios.get(`${API}/incidents/search/${selectedStoreId}?${params.toString()}`);
      
      // Filter out incidents that are already staged for recovery
      const stagedIds = incidentsToRecover.map(i => i.id);
      const filteredResults = response.data.incidents.filter(i => !stagedIds.includes(i.id));
      
      setIncidentSearchResults(filteredResults);
      if (filteredResults.length === 0) {
        toast.info('No matching open incidents found');
      }
    } catch (error) {
      console.error('Failed to search incidents:', error);
      toast.error('Failed to search incidents');
    } finally {
      setIncidentSearchLoading(false);
    }
  };

  // Toggle selection of a recovery incident
  const toggleRecoverySelection = (incident) => {
    setSelectedRecoveryIncidents(prev => {
      if (prev.find(i => i.id === incident.id)) {
        return prev.filter(i => i.id !== incident.id);
      }
      return [...prev, incident];
    });
  };

  // Mark selected incidents for recovery (staged - will be processed on submit)
  const handleMarkForRecovery = () => {
    if (selectedRecoveryIncidents.length === 0) {
      toast.error('Please select at least one incident');
      return;
    }
    
    // Add to staged recoveries
    setIncidentsToRecover(prev => [...prev, ...selectedRecoveryIncidents]);
    
    // Remove from search results
    setIncidentSearchResults(prev => 
      prev.filter(i => !selectedRecoveryIncidents.find(s => s.id === i.id))
    );
    
    // Clear selection
    setSelectedRecoveryIncidents([]);
    
    toast.success(`${selectedRecoveryIncidents.length} incident(s) marked for recovery - amount will be added to expected payments`);
  };

  // Remove incident from staged recoveries
  const handleRemoveRecovery = (incident) => {
    setIncidentsToRecover(prev => prev.filter(i => i.id !== incident.id));
    toast.info('Incident removed from recoveries');
  };

  // Clear search
  const clearIncidentSearch = () => {
    setIncidentSearchDateFrom('');
    setIncidentSearchDateTo('');
    setIncidentSearchCarReg('');
    setIncidentSearchAmount('');
    setIncidentSearchResults([]);
    setSelectedRecoveryIncidents([]);
  };

  // Calculate commission total
  const totalCommissions = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);

  // Calculate incident totals for the day
  // Include both 'open' status incidents AND pending incidents (isPending)
  const incidentDeductions = incidents
    .filter(i => i.incident_date === format(selectedDate, 'yyyy-MM-dd') && (i.status === 'open' || i.isPending))
    .reduce((sum, i) => sum + (i.amount || 0), 0);
  
  // Recoveries from existing resolved incidents + staged recoveries (incidentsToRecover)
  const existingRecoveries = incidents
    .filter(i => i.resolution_date === format(selectedDate, 'yyyy-MM-dd') && ['paid_store', 'paid_agency'].includes(i.status))
    .reduce((sum, i) => sum + (i.amount || 0), 0);
  const stagedRecoveries = incidentsToRecover.reduce((sum, i) => sum + (i.amount || 0), 0);
  const incidentRecoveries = existingRecoveries + stagedRecoveries;

  // Validate fuel price - must end with .9
  const validateFuelPrice = (price) => {
    if (!price || price === '') return true; // Allow empty
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return false;
    // Check if decimal part ends with .9
    const decimal = (numPrice % 1).toFixed(1);
    return decimal === '0.9';
  };

  const handleFuelPriceChange = (gradeId, value) => {
    // Allow typing, validate on blur
    setFuelPrices({ ...fuelPrices, [gradeId]: value });
  };

  const handleFuelPriceBlur = (gradeId) => {
    const price = fuelPrices[gradeId];
    if (price && price !== '' && !validateFuelPrice(price)) {
      toast.error(`Fuel price must end with .9 (e.g., 144.9)`);
    }
  };

  // Clear all form data
  const clearFormData = () => {
    setDepartmentAmounts({});
    setDepartmentVolumes({});
    setFuelVolumes({});
    // Don't clear fuel costs and prices - they're configuration, not data entry
    // setFuelCosts({});
    // setFuelPrices({});
    setPaymentAmounts({});
    setServiceAmounts({});
    setLotteryPrizePaid('0.00');
    setScratchPrizePaid('0.00');
    setPaidOuts([]);
    setTotalSalesAmount('0.00');
    setFuelSalesDeptAmount('0.00');
    setDriveOffDeptAmount('0.00');
    setScratchCardSettled({});
    setScratchCardSettledPackNumbers({});
    setScratchCardClosingStock({});
    setScratchCardVerified(false);
    setVarianceReason('');
    setHasUnsavedChanges(false);
    // Clear pending incidents (not saved to DB yet)
    setPendingIncidents([]);
  };

  // Handle date change with confirmation
  const handleDateChange = (newDate) => {
    if (!newDate) return;
    
    // If there are unsaved changes, show dialog
    if (hasUnsavedChanges) {
      setPendingDate(newDate);
      setDateChangeDialogOpen(true);
    } else {
      setSelectedDate(newDate);
    }
  };

  // Apply date change - clear form for new date AND delete recorded incidents
  const applyDateChangeWithClear = async () => {
    // Delete any OPEN incidents recorded for the OLD date before clearing
    // But only delete incidents that are actually in the DB (not pending ones)
    const oldDateStr = format(selectedDate, 'yyyy-MM-dd');
    const openIncidentsForDate = incidents.filter(i => 
      i.incident_date === oldDateStr && i.status === 'open' && !i.isPending
    );
    
    if (openIncidentsForDate.length > 0) {
      try {
        // Delete each open incident recorded for this date
        for (const incident of openIncidentsForDate) {
          await axios.delete(`${API}/incidents/${incident.id}`);
        }
        toast.info(`${openIncidentsForDate.length} recorded incident(s) deleted`);
      } catch (error) {
        console.error('Failed to delete incidents:', error);
        toast.error('Failed to delete some incidents');
      }
    }
    
    // Notify about discarded pending incidents
    const pendingCount = pendingIncidents.length;
    if (pendingCount > 0) {
      toast.warning(`${pendingCount} unsaved incident(s) discarded`);
    }
    
    clearFormData();
    setIncidents([]); // Clear incidents state
    setSelectedDate(pendingDate);
    setDateChangeDialogOpen(false);
    setPendingDate(null);
    toast.info('Form cleared for new date');
  };

  // Apply date change - keep data (move to new date)
  const applyDateChangeKeepData = () => {
    setSelectedDate(pendingDate);
    setDateChangeDialogOpen(false);
    setPendingDate(null);
    toast.info('Data kept - will be saved to new date');
  };

  // Calculations - include service amounts in total sales
  const totalDepartmentSales = Object.values(departmentAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const totalServiceSales = Object.values(serviceAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  
  // Calculate Other Departments (Total Sales - sum of department amounts) - EXCLUDES services
  const enteredTotalSales = parseFloat(totalSalesAmount) || 0;
  const otherDepartmentsAmount = storeDetails?.enable_total_sales 
    ? enteredTotalSales - totalDepartmentSales
    : 0;
  
  // Total sales calculation: use Total Sales (Z-reading) if enabled, otherwise sum of departments + services
  // When Z-reading is used, services are ADDED on top of the Z-reading
  const totalSales = storeDetails?.enable_total_sales 
    ? enteredTotalSales + totalServiceSales
    : totalDepartmentSales + totalServiceSales;
    
  const totalPaidOut = paidOuts.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  
  // Calculate payment amounts - need to check both hardcoded keys and payment_type keys
  // Inputs use hardcoded keys: 'cash', 'card', 'vouchers' 
  // Other cards (AMEX) use pt.id || pt.name
  const cashPayment = parseFloat(paymentAmounts['cash'] || paymentAmounts['Cash'] || 0) || 0;
  const cardBmsPayment = parseFloat(paymentAmounts['card'] || paymentAmounts['Card Payments'] || paymentAmounts['Card Payments (BMS)'] || 0) || 0;
  const voucherPayment = parseFloat(paymentAmounts['vouchers'] || paymentAmounts['Voucher'] || paymentAmounts['Vouchers'] || 0) || 0;
  
  // Calculate other card payments (AMEX, etc.) - uses pt.id || pt.name as keys
  const otherCardPayments = useMemo(() => {
    if (!storeDetails?.payment_types) return 0;
    return storeDetails.payment_types
      .filter(pt => {
        const name = (pt.name || '').toLowerCase();
        return !['cash', 'card payments', 'card', 'voucher', 'vouchers'].includes(name);
      })
      .reduce((sum, pt) => sum + (parseFloat(paymentAmounts[pt.id || pt.name]) || 0), 0);
  }, [storeDetails?.payment_types, paymentAmounts]);
  
  // AMEX payment (when amex_tracking_enabled)
  const amexPayment = useMemo(() => {
    if (!storeDetails?.amex_tracking_enabled) return 0;
    return parseFloat(paymentAmounts['amex'] || paymentAmounts['AMEX'] || 0) || 0;
  }, [storeDetails?.amex_tracking_enabled, paymentAmounts]);
  
  // Get lottery/scratch from services (new format)
  const hasLottery = storeDetails?.services?.includes('lottery') || storeDetails?.enabled_services?.some(s => s.key === 'lottery');
  const hasScratchCards = storeDetails?.services?.includes('scratch_cards') || storeDetails?.enabled_services?.some(s => s.key === 'scratch_cards');
  
  // Variance calculation: Payments - Sales - PaidOut = Variance
  // Positive variance = Over (we received more than we should)
  // Negative variance = Short (we received less than we should)
  // Expected: totalPayments should equal totalSales + fuelSales - prizes paid out - paid outs - incident losses + incident recoveries
  const fuelSalesZReading = parseFloat(fuelSalesDeptAmount) || 0;
  // Expected payments calculation - only include fuel and incidents for fuel stations
  const isFuelStationForCalc = storeDetails?.store_type === 'fuel_station';
  const expectedPayments = totalSales 
    + (isFuelStationForCalc ? fuelSalesZReading : 0)
    - (parseFloat(lotteryPrizePaid) || 0) 
    - (parseFloat(scratchPrizePaid) || 0) 
    - totalPaidOut 
    - (isFuelStationForCalc ? incidentDeductions : 0)
    + (isFuelStationForCalc ? incidentRecoveries : 0);

  // Get enabled services for display
  const enabledServices = storeDetails?.enabled_services || [];
  const serviceKeys = storeDetails?.services || [];

  // Calculate fuel sales from volumes and prices
  const calculatedFuelSales = useMemo(() => {
    if (storeDetails?.store_type !== 'fuel_station' || !storeDetails?.fuel_grades) return 0;
    return storeDetails.fuel_grades.reduce((sum, grade) => {
      const volume = parseFloat(fuelVolumes[grade.id]) || 0;
      const price = parseFloat(fuelPrices[grade.id]) || 0;
      return sum + (volume * price) / 100; // price is in pence, convert to pounds
    }, 0);
  }, [storeDetails, fuelVolumes, fuelPrices]);

  // Calculate fuel card totals for Tenders tab
  const fuelCardTotals = useMemo(() => {
    const nonBunkeringTotal = Object.values(fuelCardTransactions.non_bunkering)
      .reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const bunkeringTotal = Object.values(fuelCardTransactions.bunkering)
      .reduce((sum, card) => sum + (card.total_amount || 0), 0);
    const keyFuelsTotal = fuelCardTransactions.key_fuels.total_amount || 0;
    
    return {
      non_bunkering: nonBunkeringTotal,
      bunkering: bunkeringTotal,
      key_fuels: keyFuelsTotal,
      // Total Fuel Cards = Non-Bunkering + Bunkering Total (Bunkering + Key Fuels)
      total: nonBunkeringTotal + bunkeringTotal + keyFuelsTotal,
      bunkering_total: bunkeringTotal + keyFuelsTotal
    };
  }, [fuelCardTransactions]);

  // Total Payments for variance = Cash + Voucher + Card (BMS) + AMEX + Other Cards + Total Fuel Cards
  const isFuelStation = storeDetails?.store_type === 'fuel_station';
  const totalPayments = cashPayment + voucherPayment + cardBmsPayment + amexPayment + otherCardPayments + (isFuelStation ? fuelCardTotals.total : 0);
  
  // Variance calculation
  const variance = totalPayments - expectedPayments;

  // Calculate Total Pump value (Fuel Sales Z-Reading + Non-Bunkering Fuel Cards)
  const totalPumpExpected = useMemo(() => {
    if (storeDetails?.store_type !== 'fuel_station') return 0;
    // Total Pump = Calculated Fuel Sales + Non-Bunkering Cards (they're sold at retail price from the pump)
    return calculatedFuelSales + fuelCardTotals.non_bunkering;
  }, [storeDetails, calculatedFuelSales, fuelCardTotals]);

  // Filter store's active fuel cards to only show those that are active in global settings
  const filteredActiveFuelCards = useMemo(() => {
    if (!storeDetails?.active_fuel_cards || !fuelCardTypes.length) {
      return [];
    }
    // Get IDs of globally active fuel card types
    const activeTypeIds = new Set(fuelCardTypes.map(fc => fc.id));
    // Filter store's cards to only include globally active ones
    return storeDetails.active_fuel_cards.filter(card => 
      activeTypeIds.has(card.card_type_id)
    ).map(card => {
      // Also update handling_fee_per_litre from global settings
      const globalCard = fuelCardTypes.find(fc => fc.id === card.card_type_id);
      return {
        ...card,
        handling_fee_per_litre: globalCard?.handling_fee_per_litre || card.handling_fee_per_litre
      };
    });
  }, [storeDetails?.active_fuel_cards, fuelCardTypes]);

  const handleSubmit = async () => {
    adminVarianceAckRef.current = false;
    if (isPendingLock) {
      toast.error('This entry is locked pending admin approval.');
      return;
    }
    // Show confirmation dialog first
    setSubmitConfirmDialogOpen(true);
  };
  
  // All variance checks with their allowed limits (over limit => Admin Approval)
  function buildVarianceChecks() {
    const L = varianceLimits;
    const r2 = (v) => Math.round(Math.abs(v) * 100) / 100;
    const checks = [];
    if (storeDetails?.store_type === 'fuel_station') {
      const driveOffEntered = parseFloat(driveOffDeptAmount || 0);
      const driveOffNet = incidentDeductions - incidentRecoveries;
      if (driveOffEntered !== 0 || driveOffNet !== 0) {
        checks.push({ key: 'drive_off', group: 'fuel', label: 'Drive Off / Debt Variance', tab: 'fuel', limit: L.drive_off,
          explanation: 'Drive Off / Debt entered does not match the incidents minus recoveries recorded for the day.',
          enteredLabel: 'Entered', expectedLabel: 'Incidents - Recoveries',
          entered: driveOffEntered, expected: driveOffNet, variance: r2(driveOffEntered - driveOffNet) });
      }
      const retailEntered = parseFloat(retailSalesAmount || 0);
      if (retailEntered > 0 || calculatedFuelSales > 0) {
        checks.push({ key: 'retail', group: 'fuel', label: 'Retail Fuel Variance', tab: 'fuel', limit: L.retail,
          explanation: 'Retail Sales entered does not match the retail sales calculated from pump litres x retail price.',
          enteredLabel: 'Entered Retail Sales', expectedLabel: 'Calculated Retail Sales',
          entered: retailEntered, expected: calculatedFuelSales, variance: r2(retailEntered - calculatedFuelSales) });
      }
      const bunkeringEntered = parseFloat(bunkeringSalesAmount || 0);
      const bunkeringCalc = fuelCardTotals.bunkering_total || 0;
      if (bunkeringEntered > 0 || bunkeringCalc > 0) {
        checks.push({ key: 'bunkering', group: 'fuel', label: 'Bunker Fuel Variance', tab: 'fuel', limit: L.bunkering,
          explanation: 'Bunkering Sales entered does not match the total of the bunkering fuel card transactions.',
          enteredLabel: 'Entered Bunkering', expectedLabel: 'Fuel Card Bunkering Total',
          entered: bunkeringEntered, expected: bunkeringCalc, variance: r2(bunkeringEntered - bunkeringCalc) });
      }
      const zEntered = parseFloat(fuelSalesDeptAmount || 0);
      const zExpected = calculatedFuelSales + bunkeringCalc + (incidentDeductions || 0);
      if (zEntered > 0 || zExpected > 0) {
        checks.push({ key: 'z_reading', group: 'fuel', label: 'Total Fuel Sales (Z-Reading) Variance', tab: 'fuel', limit: L.z_reading,
          explanation: 'Total Fuel Sales (Z-Reading) does not match Retail + Bunkering + Drive Off.',
          enteredLabel: 'Entered Z-Reading', expectedLabel: 'Retail + Bunkering + Drive Off',
          entered: zEntered, expected: zExpected, variance: r2(zEntered - zExpected) });
      }
    }
    checks.push({ key: 'cash', group: 'cash', label: 'Total Cash Variance', tab: 'tenders', limit: L.cash,
      explanation: 'Total Payments (all tenders) do not match the expected payments for the day.',
      enteredLabel: 'Total Payments', expectedLabel: 'Expected',
      entered: totalPayments, expected: expectedPayments, variance: r2(variance) });
    const tillCash = tillReport?.mapped?.tenders?.payments?.cash;
    if (tillCash !== undefined) {
      checks.push({ key: 'till_cash', group: 'cash', label: 'Cash Adjusted from Day End Report', tab: 'tenders', limit: 0,
        explanation: 'Cash entered differs from the Day End report value - an admin must approve this change.',
        enteredLabel: 'Entered Cash', expectedLabel: 'Day End Report Cash',
        entered: cashPayment, expected: tillCash, variance: r2(cashPayment - tillCash),
      });
    }
    return checks;
  }

  const validateAndSubmit = async () => {
    // STEP 0: Check for unresolved opening readings - CRITICAL: never save with unresolved data
    // Check SPECIFIC loading states to give proper error messages and navigate to correct tab
    
    // Check coffee loading state first
    if (coffeeOpeningLoading) {
      toast.error('Coffee machine opening readings are still loading. Please wait.');
      setActiveTab('sales');
      setSubmitConfirmDialogOpen(false);
      return;
    }
    
    // Check if coffee tracking has unresolved opening readings
    const coffeeSmallEnabled = coffeeTracking.small?.enabled;
    const coffeeLargeEnabled = coffeeTracking.large?.enabled;
    
    if (coffeeSmallEnabled && (coffeeTracking.small?.error || coffeeTracking.small?.opening_reading === null)) {
      toast.error('Small coffee machine opening reading failed to load. Please click "Retry" or refresh the page.');
      setActiveTab('sales');
      setSubmitConfirmDialogOpen(false);
      return;
    }
    
    if (coffeeLargeEnabled && (coffeeTracking.large?.error || coffeeTracking.large?.opening_reading === null)) {
      toast.error('Large coffee machine opening reading failed to load. Please click "Retry" or refresh the page.');
      setActiveTab('sales');
      setSubmitConfirmDialogOpen(false);
      return;
    }
    
    // Check for scratch card opening stock loading/error - SEPARATE from coffee
    if (scratchOpeningLoading) {
      toast.error('Scratch card opening stock is still loading. Please wait.');
      setActiveTab('scratch');
      setSubmitConfirmDialogOpen(false);
      return;
    }
    
    if (scratchOpeningError) {
      toast.error('Scratch card opening stock failed to load. Please click "Retry" in the Scratch Cards tab.');
      setActiveTab('scratch');
      setSubmitConfirmDialogOpen(false);
      return;
    }

    // STEP 1: Check scratch card validation FIRST
    const scratchCardSalesAmount = parseFloat(serviceAmounts['scratch_cards'] || 0);
    if (scratchCardSalesAmount > 0 && !scratchCardVerified && !isApprovedLock) {
      toast.error('Please verify scratch card stock count in the Scratch Cards tab before submitting.');
      setActiveTab('scratch');
      setSubmitConfirmDialogOpen(false);
      return;
    }

    // STEP 2: Validate that total payments are entered (Cash, Cards, Fuel Cards, etc.)
    // Total Payments includes: Cash + Voucher + Card (BMS) + Other Cards + Fuel Cards
    if (totalPayments <= 0) {
      toast.error('Please enter payment amounts (Cash, Card, or Fuel Cards)');
      setActiveTab('tenders');
      setSubmitConfirmDialogOpen(false);
      return;
    }

    // STEP 2.5: Paid outs from the Day End report must have a type (and supplier for Supplier Payment)
    const incompletePaidOut = paidOuts.find(p => !p.paid_out_type_id || (p.paid_out_type_name === 'Supplier Payment' && !p.supplier_id));
    if (incompletePaidOut) {
      toast.error(`Paid out "${incompletePaidOut.description}" needs a ${incompletePaidOut.paid_out_type_id ? 'supplier - click the pencil to select one (ask an admin if it is missing)' : 'paid out type'} - edit it in the Paid Outs tab.`);
      setActiveTab('paidouts');
      setSubmitConfirmDialogOpen(false);
      return;
    }

    // STEP 2.9: Check if Other Departments is negative
    if (storeDetails?.enable_total_sales && storeDetails?.enable_other_departments && otherDepartmentsAmount < 0) {
      toast.error(
        `Department totals exceed Total Sales by £${Math.abs(otherDepartmentsAmount).toFixed(2)}. ` +
        `Please check your entered amounts before submitting.`
      );
      setSubmitConfirmDialogOpen(false);
      return;
    }

    // STEP 3: Validate fuel prices for fuel stations
    if (storeDetails?.store_type === 'fuel_station' && storeDetails?.fuel_grades?.length > 0) {
      for (const grade of storeDetails.fuel_grades) {
        const price = fuelPrices[grade.id];
        const volume = parseFloat(fuelVolumes[grade.id] || 0);
        if (volume > 0 && (!price || !validateFuelPrice(price))) {
          toast.error(`Please enter a valid price for ${grade.name} (must end with .9, e.g., 144.9)`);
          setSubmitConfirmDialogOpen(false);
          return;
        }
      }
      
      // STEP 3.5 (minor): Retail Sales within ±£2.50 but > 1p - confirmation only (when nothing exceeds limits)
      const retailCheck = buildVarianceChecks().find(c => c.key === 'retail');
      const anyFailing = buildVarianceChecks().some(c => c.variance > c.limit);
      if (!anyFailing && retailCheck && retailCheck.variance > 0.01 && !retailVarianceData?.acknowledged) {
        setRetailVarianceData({
          enteredRetailSales: retailCheck.entered,
          calculatedRetailSales: retailCheck.expected,
          variance: retailCheck.variance
        });
        setRetailVarianceDialogOpen(true);
        setSubmitConfirmDialogOpen(false);
        return;
      }
    }

    // STEP 3.6: Combined variance checks (Drive Off, Retail, Bunkering, Z-Reading, Cash)
    // Anything over its limit needs Admin Approval
    const failingChecks = buildVarianceChecks().filter(c => c.variance > c.limit);
    if (failingChecks.length > 0) {
      if (isApprovedLock) {
        console.log('[Variance] Approved entry - admin submitting approved figures');
      } else if (isAdminUser && !adminEditRequestId) {
        // Admins: show warnings only, submit without approval once acknowledged
        if (!adminVarianceAckRef.current) {
          setVarianceDetails({ checks: failingChecks, variance: Math.max(...failingChecks.map(c => c.variance)) });
          setVarianceApprovalDialogOpen(true);
          setSubmitConfirmDialogOpen(false);
          return;
        }
        console.log('[Variance] Admin acknowledged variances - submitting without approval');
      } else if (adminEditRequestId) {
        console.log('[Variance] Admin Edit & Submit - variance checks bypassed');
      } else if (approvalRequest?.status === 'approved') {
        // Older requests (before combined checks) have no per-check data - form is locked, so allow
        const approvedChecks = approvalRequest.variance_details?.checks;
        const changed = !approvedChecks ? [] : failingChecks.filter(c => {
          const a = approvedChecks.find(x => x.key === c.key);
          return !a || Math.abs(a.variance - c.variance) > 0.01;
        });
        if (changed.length > 0) {
          toast.error(
            `Data has changed since admin approval (${changed.map(c => `${c.label}: £${c.variance.toFixed(2)}`).join(', ')}). ` +
            `Please submit the approved figures without changes.`
          );
          setActiveTab(changed[0].tab);
          setSubmitConfirmDialogOpen(false);
          return;
        }
      } else if (approvalRequest?.status === 'pending_approval') {
        toast.error('Your approval request is still pending. Please wait for admin review.');
        setSubmitConfirmDialogOpen(false);
        return;
      } else {
        setVarianceDetails({
          checks: failingChecks,
          variance: Math.max(...failingChecks.map(c => c.variance))
        });
        setVarianceApprovalDialogOpen(true);
        setSubmitConfirmDialogOpen(false);
        return;
      }
    }
    
    // STEP 5: All validations passed - now check variance
    // Variance >= £2 requires reason - use >= to include exactly £2.00
    if (Math.abs(variance) >= varianceLimits.cash_reason && !varianceReason.trim() && !isApprovedLock) {
      setSubmitConfirmDialogOpen(false);
      setVarianceDialogOpen(true);
      return;
    }

    // STEP 6: If variance >= £2 with reason, proceed to submit (email sent AFTER in performSubmit)
    // Email will be sent in submitWithVariance -> performSubmit AFTER successful save
    
    // Prepare variance data for email notification
    // Check for any email address (email, admin_email, or email_secondary)
    const hasStoreEmail = storeDetails?.email || storeDetails?.admin_email || storeDetails?.email_secondary;
    const varianceEmailData = Math.abs(variance) >= varianceLimits.cash_reason && hasStoreEmail ? {
      shouldSendEmail: true,
      totalSalesForEmail: totalSales + (isFuelStation ? fuelSalesZReading : 0),
      totalDeductionsForEmail: (parseFloat(lotteryPrizePaid) || 0) + 
        (parseFloat(scratchPrizePaid) || 0) + 
        totalPaidOut + 
        (isFuelStation ? incidentDeductions - incidentRecoveries : 0)
    } : null;
    
    console.log('handleSubmit calling performSubmit with scratchCardVarianceDetails:', {
      scratchCardVarianceDetailsState: scratchCardVarianceDetails,
      length: scratchCardVarianceDetails?.length,
      passingToPerformSubmit: scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null
    });
    
    setSubmitConfirmDialogOpen(false);
    await performSubmit(varianceEmailData, scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null);
  };
  
  // Helper function to check variance reason before proceeding to submit
  const proceedToSubmitWithVarianceCheck = () => {
    // If variance >= £2 and no reason provided, show variance dialog
    if (Math.abs(variance) >= varianceLimits.cash_reason && !varianceReason.trim() && !isApprovedLock) {
      setVarianceDialogOpen(true);
      return false; // Indicate we need to wait for variance reason
    }
    return true; // OK to proceed
  };

  const performSubmit = async (varianceData = null, scratchCardVarianceData = null) => {
    console.log('performSubmit called with:', {
      varianceData,
      scratchCardVarianceData,
      scratchCardVarianceDataLength: scratchCardVarianceData?.length,
      storeEmail: storeDetails?.email
    });
    
    if (saving) {
      console.log('Already saving, skipping duplicate call');
      return;
    }
    setSaving(true);
    console.log('Starting submission...');
    
    try {
      // Build payment data for backend - ALWAYS use standard canonical keys
      // This ensures consistency across all stores regardless of their payment_type IDs
      const paymentData = {};
      
      // Map payment type names to standard keys
      const getStandardKey = (ptName) => {
        const name = (ptName || '').toLowerCase().trim();
        if (name === 'cash') return 'cash';
        if (name.includes('card') && !name.includes('amex')) return 'card';  // Catches "Card Payments", "Card Payments (BMS)", etc. but not AMEX
        if (name === 'voucher' || name === 'vouchers') return 'vouchers';
        if (name === 'staff expenses' || name === 'staff_expenses') return 'staff_expenses';
        if (name === 'amex' || name.includes('amex')) return 'amex';  // AMEX payment
        return null; // Custom payment type
      };
      
      // Initialize standard payment values
      let cashValue = null;
      let cardValue = null;
      let vouchersValue = null;
      let staffExpensesValue = null;
      let amexValue = null;
      
      // Helper to get value from paymentAmounts - handles 0 values correctly
      const getPaymentValue = (keys) => {
        for (const key of keys) {
          if (paymentAmounts[key] !== undefined && paymentAmounts[key] !== null && paymentAmounts[key] !== '') {
            return parseFloat(paymentAmounts[key]) || 0;
          }
        }
        return null;
      };
      
      // Extract values from paymentAmounts using all possible key variations
      // Check standard string keys first - null means not found, 0 is a valid value
      cashValue = getPaymentValue(['cash', 'Cash']);
      cardValue = getPaymentValue(['card', 'Card Payments', 'Card Payments (BMS)']);
      vouchersValue = getPaymentValue(['vouchers', 'Voucher', 'Vouchers']);
      staffExpensesValue = getPaymentValue(['Staff Expenses', 'staff_expenses']);
      amexValue = getPaymentValue(['amex', 'AMEX', 'Amex']);
      
      // Also check payment type IDs (like pt-0, pt-1, pay-0, etc.) using store's payment_types config
      if (storeDetails?.payment_types?.length > 0) {
        storeDetails.payment_types.forEach(pt => {
          const ptKey = pt.id || pt.name;
          const ptValue = paymentAmounts[ptKey];
          const standardKey = getStandardKey(pt.name);
          
          // Only use this value if we haven't found one yet (value is null)
          if (ptValue !== undefined && ptValue !== null && ptValue !== '' && standardKey) {
            const numValue = parseFloat(ptValue) || 0;
            if (standardKey === 'cash' && cashValue === null) cashValue = numValue;
            if (standardKey === 'card' && cardValue === null) cardValue = numValue;
            if (standardKey === 'vouchers' && vouchersValue === null) vouchersValue = numValue;
            if (standardKey === 'staff_expenses' && staffExpensesValue === null) staffExpensesValue = numValue;
            if (standardKey === 'amex' && amexValue === null) amexValue = numValue;
          }
        });
      }
      
      // Convert null to 0 for final values
      cashValue = cashValue ?? 0;
      cardValue = cardValue ?? 0;
      vouchersValue = vouchersValue ?? 0;
      staffExpensesValue = staffExpensesValue ?? 0;
      amexValue = amexValue ?? 0;
      
      // Save ONLY standard canonical keys - this ensures consistency across all stores
      paymentData['cash'] = cashValue;
      paymentData['card'] = cardValue;
      paymentData['vouchers'] = vouchersValue;
      if (staffExpensesValue > 0) {
        paymentData['staff_expenses'] = staffExpensesValue;
      }
      if (amexValue > 0 || storeDetails?.amex_tracking_enabled) {
        paymentData['amex'] = amexValue;
      }
      
      // Add any truly custom payment types (not standard ones) - like AMEX, etc.
      if (storeDetails?.payment_types?.length > 0) {
        storeDetails.payment_types.forEach(pt => {
          const ptKey = pt.id || pt.name;
          const ptValue = parseFloat(paymentAmounts[ptKey]) || 0;
          const standardKey = getStandardKey(pt.name);
          
          // If this is NOT a standard payment type (custom), add it with its name as key
          if (!standardKey && ptValue !== 0) {
            paymentData[pt.name] = ptValue;
          }
        });
      }

      // Set legacy fields from the standardized values
      let extractedCashAmount = cashValue;
      let extractedCardAmount = cardValue;
      let extractedVouchersAmount = vouchersValue;

      const payload = {
        store_id: selectedStoreId,
        sales_date: format(selectedDate, 'yyyy-MM-dd'),
        shift: selectedShift,
        department_amounts: Object.fromEntries(
          Object.entries(departmentAmounts).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        department_volumes: Object.fromEntries(
          Object.entries(departmentVolumes).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        service_amounts: Object.fromEntries(
          Object.entries(serviceAmounts).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        fuel_volumes: Object.fromEntries(
          Object.entries(fuelVolumes).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        fuel_costs: Object.fromEntries(
          Object.entries(fuelCosts).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        fuel_prices: Object.fromEntries(
          Object.entries(fuelPrices).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        payment_amounts: paymentData,
        cash_amount: extractedCashAmount,
        credit_card_amount: extractedCardAmount,
        vouchers_amount: extractedVouchersAmount,
        amex_amount: amexValue,  // Save AMEX amount as legacy field
        paid_outs: paidOuts.map(p => ({
          ...p,
          amount: parseFloat(p.amount) || 0
        })),
        lottery_prize_paid: parseFloat(lotteryPrizePaid) || 0,
        scratch_card_prize_paid: parseFloat(scratchPrizePaid) || 0,
        variance_reason: varianceReason || (isApprovedLock ? approvedVarianceReason : ''),
        scratch_card_quantities: Object.keys(scratchCardQuantities).length > 0 ? scratchCardQuantities : null,
        scratch_card_settled: Object.keys(scratchCardSettled).length > 0 ? scratchCardSettled : null,
        scratch_card_settled_pack_numbers: Object.keys(scratchCardSettledPackNumbers).length > 0 ? scratchCardSettledPackNumbers : null,
        scratch_card_closing_stock: Object.keys(scratchCardClosingStock).length > 0 ? scratchCardClosingStock : null,
        scratch_card_opening_stock: Object.keys(scratchCardOpeningStock).length > 0 ? scratchCardOpeningStock : null,  // Save opening stock for editing later
        // Total Sales (Z-reading) fields
        total_sales_amount: storeDetails?.enable_total_sales ? (parseFloat(totalSalesAmount) || 0) : null,
        other_departments_amount: storeDetails?.enable_other_departments ? otherDepartmentsAmount : null,
        // Fuel Sales department amount
        fuel_sales_dept_amount: storeDetails?.store_type === 'fuel_station' ? (parseFloat(fuelSalesDeptAmount) || 0) : null,
        // Drive Off / Indebtedness department amount
        driveoff_dept_amount: storeDetails?.store_type === 'fuel_station' ? (parseFloat(driveOffDeptAmount) || 0) : null,
        // Incident deductions for variance calculation (from incidents list)
        incident_deductions: storeDetails?.store_type === 'fuel_station' ? incidentDeductions : 0,
        // Incident recoveries for variance calculation
        incident_recoveries: storeDetails?.store_type === 'fuel_station' ? incidentRecoveries : 0,
        // Fuel card transactions for variance calculation
        fuel_card_transactions: storeDetails?.store_type === 'fuel_station' ? fuelCardTransactions : null,
        // Retail and Bunkering sales amounts for fuel stations
        retail_sales_amount: storeDetails?.store_type === 'fuel_station' ? (parseFloat(retailSalesAmount) || 0) : null,
        bunkering_sales_amount: storeDetails?.store_type === 'fuel_station' ? (parseFloat(bunkeringSalesAmount) || 0) : null,
        // Coffee tracking data
        coffee_tracking: (coffeeTracking.coffee_sold_enabled || coffeeTracking.small.enabled || coffeeTracking.large.enabled) ? {
          coffee_sold_qty: coffeeTracking.coffee_sold_enabled ? (parseInt(coffeeTracking.coffee_sold_qty) || 0) : null,
          small: coffeeTracking.small.enabled ? {
            opening_reading: parseInt(coffeeTracking.small.opening_reading) || 0,
            closing_reading: parseInt(coffeeTracking.small.closing_reading) || 0,
            calculated_sold: coffeeTracking.small.calculated_sold
          } : null,
          large: coffeeTracking.large.enabled ? {
            opening_reading: parseInt(coffeeTracking.large.opening_reading) || 0,
            closing_reading: parseInt(coffeeTracking.large.closing_reading) || 0,
            calculated_sold: coffeeTracking.large.calculated_sold
          } : null,
          // Calculate variance if both sold qty and tracking are enabled
          total_machine_sold: (coffeeTracking.small.enabled ? coffeeTracking.small.calculated_sold : 0) + 
                              (coffeeTracking.large.enabled ? coffeeTracking.large.calculated_sold : 0),
          variance: coffeeTracking.coffee_sold_enabled && (coffeeTracking.small.enabled || coffeeTracking.large.enabled) 
            ? ((coffeeTracking.small.enabled ? coffeeTracking.small.calculated_sold : 0) + 
               (coffeeTracking.large.enabled ? coffeeTracking.large.calculated_sold : 0)) - 
              (parseInt(coffeeTracking.coffee_sold_qty) || 0)
            : null
        } : null,
        // Frontend calculated variance for debugging
        frontend_variance: variance,
        frontend_total_payments: totalPayments,
        frontend_expected_payments: expectedPayments
      };

      // Submit or update based on editing mode
      if (isEditing) {
        // Include incidents marked for deletion (deferred execution)
        if (incidentsToDelete.length > 0) {
          payload.incidents_to_delete = incidentsToDelete;
        }
        await axios.put(`${API}/sales/${editingEntryId}`, payload);
        toast.success('Sales entry updated successfully!');
        if (approvalRequest?.status === 'approved') {
          axios.patch(`${API}/approval-requests/${approvalRequest.id}/complete`)
            .then(() => checkApprovalRequest(selectedStoreId, selectedDate, selectedShift))
            .catch(err => console.error('[ApprovalRequest] Failed to complete request:', err));
        }
        setIsEditing(false);
        setEditingEntryId(null);
        // Clear deferred deletions after successful submission
        setIncidentsToDelete([]);
      } else {
        await axios.post(`${API}/sales`, payload);
        toast.success('Sales entry submitted successfully!');
        if (adminEditRequestId) {
          const notes = sessionStorage.getItem(`approval_notes_${adminEditRequestId}`) || 'Admin edited and submitted the entry';
          sessionStorage.removeItem(`approval_notes_${adminEditRequestId}`);
          axios.patch(`${API}/approval-requests/${adminEditRequestId}/resolve`, { notes })
            .then(() => setTimeout(() => navigate('/approval-requests'), 1500))
            .catch(err => console.error('[ApprovalRequest] Failed to resolve request:', err));
        } else if (approvalRequest) {
          // Backend closes the approval request on submit - just refresh status
          checkApprovalRequest(selectedStoreId, selectedDate, selectedShift);
        }
      }
      
      // ========== RESET FORM IMMEDIATELY AFTER SUCCESSFUL SUBMISSION ==========
      // Do this BEFORE any optional operations (emails, incident updates) to ensure
      // the form is always cleared even if those operations fail
      
      // Reset form completely after successful submission
      setDepartmentAmounts({});
      setDepartmentVolumes({});
      setServiceAmounts({});
      setPaymentAmounts({});
      setFuelVolumes({});
      setFuelCosts({});
      setFuelPrices({});
      setFuelCostPrices({});
      setFuelCardTransactions({
        non_bunkering: {},
        bunkering: {},
        key_fuels: { transactions: [], shop_items_amount: 0, total_amount: 0 }
      });
      setTotalSalesAmount('0.00');
      setRetailSalesAmount('0.00');
      setBunkeringSalesAmount('0.00');
      setCommissions([]);
      setPaidOuts([]);
      setVarianceReason('');
      setLotteryPrizePaid('0.00');
      setScratchPrizePaid('0.00');
      setScratchCardQuantities({});
      setScratchCardSettled({});
      setScratchCardSettledPackNumbers({});
      setScratchCardClosingStock({});
      setScratchCardVerified(false);
      setFuelSalesDeptAmount('0.00');
      setDriveOffDeptAmount('0.00');
      setHasUnsavedChanges(false);
      setIncidentsToDelete([]);
      setIncidentsToRecover([]);
      setIncidents([]); // Clear incidents list to show fresh data for new date
      setFuelCostsCompleted(false);
      setRetailVarianceData(null);
      setBunkeringValidationData(null);
      setPumpValidationData(null);
      clearIncidentSearch();
      
      // Clear draft from database after successful submission
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      clearDraft(selectedStoreId, dateStr, selectedShift);
      
      // Reset incident form
      setNewIncident({
        incident_type: 'drive_off',
        incident_time: '',
        pump_number: '',
        fuel_grade_id: '',
        fuel_grade_name: '',
        litres: '',
        amount: '',
        vehicle_registration: '',
        vehicle_description: '',
        police_reference: '',
        customer_name: '',
        customer_phone: '',
        agreement_signed: false,
        notes: '',
        cashier_id: '',
        cashier_name: '',
        customer_address: ''
      });
      
      // Switch back to first tab (Fuel Costs for fuel stations, Sales for others)
      if (storeDetails?.store_type === 'fuel_station') {
        setActiveTab('fuelcost');
      } else {
        setActiveTab('sales');
      }
      
      // ========== OPTIONAL OPERATIONS (after form reset) ==========
      
      // Check if store has any email configured
      const hasStoreEmail = storeDetails?.email || storeDetails?.admin_email || storeDetails?.email_secondary;
      
      // Determine if we have variances to report
      const hasCashVariance = varianceData?.shouldSendEmail;
      
      // Calculate FRESH scratch card variance from current state (not from cached scratchCardVarianceData)
      let freshScratchDeclared = parseFloat(serviceAmounts['scratch_cards'] || 0);
      let freshScratchCalculated = 0;
      
      scratchCardDenominations.forEach(d => {
        const openingStock = parseInt(scratchCardOpeningStock[d.id]) || 0;
        const settled = parseInt(scratchCardSettled[d.id]) || 0;
        const closingStock = parseInt(scratchCardClosingStock[d.id]) || 0;
        const soldQty = (openingStock + settled) - closingStock;
        freshScratchCalculated += soldQty * (d.value || 0);
      });
      
      const freshScratchVariance = freshScratchDeclared - freshScratchCalculated;
      
      // Determine if there's a scratch card variance based on FRESH calculation
      const hasScratchCardVariance = Math.abs(freshScratchVariance) >= 0.01 && hasStoreEmail;
      
      // Check for coffee variance - use current state values
      const coffeePosEnabled = coffeeTracking.coffee_sold_enabled;
      const coffeeSmallEnabled = coffeeTracking.small?.enabled;
      const coffeeLargeEnabled = coffeeTracking.large?.enabled;
      const coffeeDeclared = parseInt(coffeeTracking.coffee_sold_qty) || 0;
      const coffeeSmallSold = coffeeSmallEnabled ? (coffeeTracking.small?.calculated_sold || 0) : 0;
      const coffeeLargeSold = coffeeLargeEnabled ? (coffeeTracking.large?.calculated_sold || 0) : 0;
      const coffeeCalculated = coffeeSmallSold + coffeeLargeSold;
      const coffeeVariance = coffeeDeclared - coffeeCalculated;
      
      // Coffee variance is detected when:
      // 1. POS tracking is enabled (coffee_sold_enabled)
      // 2. At least one machine is enabled (small or large)
      // 3. There is a non-zero variance
      // 4. Store has email configured
      const hasCoffeeVariance = coffeePosEnabled && 
                                (coffeeSmallEnabled || coffeeLargeEnabled) &&
                                coffeeVariance !== 0 && 
                                hasStoreEmail;
      
      console.log('=== COFFEE VARIANCE CHECK ===');
      console.log('coffeeTracking state:', coffeeTracking);
      console.log('coffeePosEnabled:', coffeePosEnabled);
      console.log('coffeeSmallEnabled:', coffeeSmallEnabled);
      console.log('coffeeLargeEnabled:', coffeeLargeEnabled);
      console.log('coffeeDeclared (POS):', coffeeDeclared);
      console.log('coffeeSmallSold:', coffeeSmallSold);
      console.log('coffeeLargeSold:', coffeeLargeSold);
      console.log('coffeeCalculated (total):', coffeeCalculated);
      console.log('coffeeVariance:', coffeeVariance);
      console.log('hasCoffeeVariance:', hasCoffeeVariance);
      console.log('=== END COFFEE CHECK ===');
      
      console.log('Email notification check:', {
        hasCashVariance,
        hasScratchCardVariance,
        hasCoffeeVariance,
        coffeeDeclared,
        coffeeCalculated,
        coffeeVariance,
        scratchCardVarianceData,
        scratchCardVarianceDataLength: scratchCardVarianceData?.length,
        storeEmail: storeDetails?.email,
        storeName: storeDetails?.name
      });
      
      // Send combined email if ANY variances exist
      const hasAnyVariance = hasCashVariance || hasScratchCardVariance || hasCoffeeVariance;
      
      if (hasAnyVariance && hasStoreEmail) {
        // Build fresh scratch card variance details
        const freshScratchDetails = [];
        if (hasScratchCardVariance) {
          scratchCardDenominations.forEach(d => {
            const openingStock = parseInt(scratchCardOpeningStock[d.id]) || 0;
            const settled = parseInt(scratchCardSettled[d.id]) || 0;
            const closingStock = parseInt(scratchCardClosingStock[d.id]) || 0;
            const soldQty = (openingStock + settled) - closingStock;
            const soldValue = soldQty * (d.value || 0);
            freshScratchDetails.push({
              denomination: d.name,
              value: d.value,
              opening: openingStock,
              settled: settled,
              closing: closingStock,
              sold_qty: soldQty,
              sold_value: soldValue
            });
          });
        }
        
        try {
          // Fallback email logic: primary email → admin_email → email_secondary
          const storeEmail = storeDetails.email || storeDetails.admin_email || storeDetails.email_secondary || '';
          
          await axios.post(`${API}/notifications/combined-variance`, {
            store_id: selectedStoreId,
            store_name: storeDetails.name,
            store_email: storeEmail,
            entry_date: format(selectedDate, 'yyyy-MM-dd'),
            shift: selectedShift,
            // Cash variance fields
            cash_variance: hasCashVariance ? variance : null,
            total_payments: hasCashVariance ? totalPayments : null,
            expected_payments: hasCashVariance ? expectedPayments : null,
            total_sales: varianceData?.totalSalesForEmail || 0,
            total_deductions: varianceData?.totalDeductionsForEmail || 0,
            cash_variance_reason: varianceReason || (isApprovedLock ? approvedVarianceReason : ''),
            // Scratch card variance fields - using FRESH calculated values
            scratch_card_variance_details: hasScratchCardVariance ? freshScratchDetails : null,
            scratch_card_variance_reason: scratchCardVarianceReason || '',
            scratch_card_declared: hasScratchCardVariance ? freshScratchDeclared : null,
            scratch_card_calculated: hasScratchCardVariance ? freshScratchCalculated : null,
            scratch_card_variance: hasScratchCardVariance ? freshScratchVariance : null,
            // Coffee variance fields
            coffee_declared: hasCoffeeVariance ? coffeeDeclared : null,
            coffee_calculated: hasCoffeeVariance ? coffeeCalculated : null,
            coffee_variance: hasCoffeeVariance ? coffeeVariance : null,
            coffee_variance_reason: '',
            coffee_small_enabled: coffeeSmallEnabled || false,
            coffee_large_enabled: coffeeLargeEnabled || false,
            coffee_small_opening: coffeeSmallEnabled ? (parseInt(coffeeTracking.small?.opening_reading) || 0) : null,
            coffee_small_closing: coffeeSmallEnabled ? (parseInt(coffeeTracking.small?.closing_reading) || 0) : null,
            coffee_small_sold: coffeeSmallEnabled ? coffeeSmallSold : null,
            coffee_large_opening: coffeeLargeEnabled ? (parseInt(coffeeTracking.large?.opening_reading) || 0) : null,
            coffee_large_closing: coffeeLargeEnabled ? (parseInt(coffeeTracking.large?.closing_reading) || 0) : null,
            coffee_large_sold: coffeeLargeEnabled ? coffeeLargeSold : null
          });
          
          const varianceTypes = [];
          if (hasCashVariance) varianceTypes.push('Cash');
          if (hasScratchCardVariance) varianceTypes.push('Scratch Card');
          if (hasCoffeeVariance) varianceTypes.push('Coffee');
          toast.info(`Variance notification sent (${varianceTypes.join(', ')})`);
        } catch (error) {
          console.error('Failed to send combined variance notification:', error);
          // Don't fail the submission if email fails
        }
      }
      
      // Process incident recoveries (mark as paid) after successful submission
      if (incidentsToRecover.length > 0) {
        try {
          await axios.post(`${API}/incidents/mark-paid-batch`, {
            incident_ids: incidentsToRecover.map(i => i.id),
            resolution_date: format(selectedDate, 'yyyy-MM-dd'),
            payment_method: 'cash',
            notes: 'Paid at store via Daily Sales Entry'
          });
          toast.success(`${incidentsToRecover.length} incident(s) marked as paid`);
        } catch (error) {
          console.error('Failed to mark incidents as paid:', error);
          toast.error('Sales entry saved, but failed to mark some incidents as paid');
        }
      }
      
      // Save pending incidents to database after successful submission
      if (pendingIncidents.length > 0) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        let savedCount = 0;
        for (const incident of pendingIncidents) {
          try {
            // Remove pending flags before saving
            const { id, isPending, status, ...incidentData } = incident;
            await axios.post(`${API}/incidents`, incidentData);
            savedCount++;
          } catch (error) {
            console.error('Failed to save incident:', error);
          }
        }
        if (savedCount > 0) {
          toast.success(`${savedCount} incident(s) saved successfully`);
        }
        // Clear pending incidents after saving
        setPendingIncidents([]);
      }
      
      // Small delay to ensure state updates propagate before fetching new data
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refresh only essential data from server
      fetchExistingEntries();
      // Refresh incidents list to show newly recorded incidents
      if (storeDetails?.store_type === 'fuel_station') {
        fetchIncidentsForDate();
        // Check fuel costs for the new/current date
        checkFuelCostsCompleted(selectedStoreId, selectedDate);
      }
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit sales entry');
    } finally {
      setSaving(false);
    }
  };

  const submitWithVariance = async () => {
    setVarianceDialogOpen(false);
    
    // Store variance data to send email AFTER successful submission
    const varianceEmailData = {
      shouldSendEmail: Math.abs(variance) >= varianceLimits.cash_reason && storeDetails?.email,
      totalSalesForEmail: totalSales + (isFuelStation ? fuelSalesZReading : 0),
      totalDeductionsForEmail: (parseFloat(lotteryPrizePaid) || 0) + 
        (parseFloat(scratchPrizePaid) || 0) + 
        totalPaidOut + 
        (isFuelStation ? incidentDeductions - incidentRecoveries : 0)
    };
    
    // Submit the data first - email will be sent ONLY after successful submission
    await performSubmit(varianceEmailData, scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null);
  };

  // Calculate scratch card total from quantities
  const calculateScratchCardTotal = () => {
    let total = 0;
    scratchCardDenominations.forEach(d => {
      const qty = parseInt(scratchCardQuantities[d.id] || 0);
      total += qty * d.value;
    });
    return total;
  };

  const verifyScratchCardQuantities = () => {
    const scratchCardSalesAmount = parseFloat(serviceAmounts['scratch_cards'] || 0);
    
    // Calculate total from stock count
    let calculatedTotal = 0;
    const stockDetails = [];
    
    scratchCardDenominations.forEach(d => {
      const openingStock = parseInt(scratchCardOpeningStock[d.id]) || 0;
      const settled = parseInt(scratchCardSettled[d.id]) || 0;
      const closingStock = parseInt(scratchCardClosingStock[d.id]) || 0;
      const soldQty = (openingStock + settled) - closingStock;
      const salesValue = soldQty * (d.value || 0);
      
      calculatedTotal += salesValue;
      
      if (soldQty !== 0) {
        stockDetails.push({
          name: d.name,
          value: d.value || 0,
          opening: openingStock,
          settled: settled,
          closing: closingStock,
          soldQty: soldQty,
          salesValue: salesValue
        });
      }
    });
    
    // Check if any settled quantities are not multiples of pack size - BLOCKING
    const invalidPackSizes = scratchCardDenominations.filter(d => {
      const qty = parseInt(scratchCardSettled[d.id] || 0);
      const packSize = d.pack_size || 100;
      return qty > 0 && qty % packSize !== 0;
    });
    
    if (invalidPackSizes.length > 0) {
      const errorMsg = invalidPackSizes.map(d => {
        const qty = scratchCardSettled[d.id];
        const packSize = d.pack_size || 100;
        return `${d.name}: ${qty} (must be multiple of ${packSize})`;
      }).join(', ');
      toast.error(`Invalid pack sizes! Please correct: ${errorMsg}`);
      return;
    }
    
    // Pack numbers validation - OPTIONAL WARNING only (not blocking)
    const missingPackNumbers = scratchCardDenominations.filter(d => {
      const settledQty = parseInt(scratchCardSettled[d.id] || 0);
      const packNumbers = scratchCardSettledPackNumbers[d.id]?.trim();
      return settledQty > 0 && (!packNumbers || packNumbers.length === 0);
    });
    
    if (missingPackNumbers.length > 0) {
      const errorMsg = missingPackNumbers.map(d => d.name).join(', ');
      toast.warning(`Pack numbers not entered for: ${errorMsg}. Consider adding them for tracking.`);
      // Don't return - allow to continue
    }
    
    // Check variance between calculated and declared amounts - Show POPUP DIALOG
    // Variance = Declared - Calculated (negative means SHORT, positive means OVER)
    const amountVariance = scratchCardSalesAmount - calculatedTotal;
    
    if (Math.abs(amountVariance) > 0.01 && stockDetails.length > 0) {
      // Only include items that were actually sold for reference
      // The real variance is the difference between declared and calculated
      const varianceData = stockDetails
        .filter(item => item.soldQty > 0)  // Only include items with sales
        .map(item => ({
          ...item,
          variance: item.soldQty,  // Sold quantity for reference
          varianceValue: item.salesValue  // Sales value for reference
        }));
      
      // Add overall variance info - 'name' field is required for backend parsing
      varianceData.push({
        name: 'VARIANCE SUMMARY',  // Backend checks this field
        value: 0,
        opening: null,
        settled: null,
        closing: null,
        soldQty: null,
        salesValue: null,
        variance: null,
        varianceValue: amountVariance,  // The actual variance amount (Declared - Calculated)
        declared: scratchCardSalesAmount,
        calculated: calculatedTotal
      });
      
      setScratchCardVarianceDetails(varianceData);
      setScratchCardVarianceDialogOpen(true);
      return; // Wait for user to click "Continue Anyway" or "Go Back"
    }
    
    // Check if calculated total matches entered amount - WARNING only
    if (Math.abs(calculatedTotal - scratchCardSalesAmount) > 0.01) {
      toast.warning(
        <div>
          <p className="font-semibold">⚠️ Amount Mismatch!</p>
          <p className="text-sm mt-1">Sales: £{scratchCardSalesAmount.toFixed(2)}, Calculated: £{calculatedTotal.toFixed(2)}</p>
          <p className="text-xs mt-1 text-muted-foreground">Please check the scratch card quantities and amount.</p>
        </div>,
        { duration: 8000 }
      );
      // Allow submission but warn - do NOT return here
    }
    
    // No variance - proceed with verification
    completeScratchCardVerification([]);
  };
  
  // Complete scratch card verification (called after variance dialog confirmation or if no variance)
  // NOTE: Email notification is sent AFTER successful submission, not here during verification
  const completeScratchCardVerification = (varianceData) => {
    // Store variance data - email will be sent after successful submission
    // The varianceData is stored in scratchCardVarianceDetails state
    
    // Calculate and store the sold quantities for submission
    const quantities = {};
    scratchCardDenominations.forEach(denom => {
      const openingStock = parseInt(scratchCardOpeningStock[denom.id]) || 0;
      const settled = parseInt(scratchCardSettled[denom.id]) || 0;
      const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
      quantities[denom.id] = ((openingStock + settled) - closingStock).toString();
    });
    setScratchCardQuantities(quantities);
    
    setScratchCardDialogOpen(false);
    setScratchCardVarianceDialogOpen(false);
    setScratchCardVerified(true);
    toast.success('Scratch card sales verified - you may now submit the sales entry');
    
    // NOTE: Cash variance check is done in handleSubmit, NOT here
  };
  
  // Function to send scratch card variance notification to store email
  const sendScratchCardVarianceNotification = async (varianceData) => {
    // Fallback email logic: primary email → admin_email → email_secondary
    const storeEmail = storeDetails?.email || storeDetails?.admin_email || storeDetails?.email_secondary;
    if (!storeEmail || varianceData.length === 0) return;
    
    try {
      await axios.post(`${API}/notifications/scratch-card-variance`, {
        store_id: selectedStoreId,
        store_name: storeDetails.name,
        store_email: storeEmail,
        entry_date: format(selectedDate, 'yyyy-MM-dd'),
        shift: selectedShift,
        variance_details: varianceData
      });
      console.log('Scratch card variance notification sent');
    } catch (error) {
      console.error('Failed to send variance notification:', error);
      // Don't block submission if notification fails
    }
  };

  // Check if entry exists for selected date/shift (case-insensitive, normalize spaces)
  const normalizeShift = (shift) => shift?.toLowerCase().replace(/\s+/g, '');
  const existingEntry = existingEntries.find(
    e => e.sales_date === format(selectedDate, 'yyyy-MM-dd') && 
         normalizeShift(e.shift) === normalizeShift(selectedShift)
  );

  // Find the latest entry date for edit restriction (admin can only edit the last entry)
  const latestEntryDate = useMemo(() => {
    if (allStoreEntries.length === 0) return null;
    const sortedEntries = [...allStoreEntries].sort((a, b) => 
      new Date(b.sales_date) - new Date(a.sales_date)
    );
    return sortedEntries[0]?.sales_date;
  }, [allStoreEntries]);

  // Check if current selected date is the latest entry (for edit restriction)
  const isLatestEntry = existingEntry && existingEntry.sales_date === latestEntryDate;
  
  // Historical entry = exists but NOT the latest
  const isHistoricalEntry = existingEntry && existingEntry.sales_date !== latestEntryDate;
  
  // Edit permissions:
  // - Latest entry: Admin or Super User can edit all fields
  // - Historical entry: ONLY Super User can edit, but scratch cards are disabled
  const canEditEntry = (isAdmin || isSuperUser) && isLatestEntry;
  const canEditHistoricalEntry = isSuperUser && isHistoricalEntry;
  
  // Combined edit permission (for showing edit UI)
  const canEdit = canEditEntry || canEditHistoricalEntry;
  
  // Scratch cards should be disabled for historical entries (even for super user)
  const scratchCardsDisabled = isHistoricalEntry;

  if (stores.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No stores available. Please contact admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-entry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            {isStaff ? 'Daily Sales Entry' : 'Sales Entry'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isStaff ? 'Enter your shift sales data' : 'Enter or review sales data'}
          </p>
        </div>
      </div>

      {/* Selection Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Store Selection */}
            <div className="space-y-2">
              <Label>Store</Label>
              <Select value={selectedStoreId} onValueChange={handleStoreChange}>
                <SelectTrigger data-testid="sales-store-select">
                  <SelectValue placeholder="Select Store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label>Sales Date</Label>
              {isStaffLike && !lastEntryDateLoaded ? (
                /* Staff: Loading state while checking for last entry */
                <div className="h-10 px-3 flex items-center bg-muted rounded-md border animate-pulse" data-testid="sales-date-loading">
                  <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : isStaffLike && lastEntryDate ? (
                /* Staff with existing entries: Date is auto-set to next day - can view previous day read-only */
                <div className="space-y-2">
                  <div className="h-10 px-3 flex items-center bg-muted rounded-md border" data-testid="sales-date-display">
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{format(selectedDate, 'PPP')}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {viewingPreviousDay ? '(Previous day - read only)' : `(Next after ${format(new Date(lastEntryDate), 'dd MMM')})`}
                    </span>
                  </div>
                  {viewingPreviousDay ? (
                    <Button variant="outline" size="sm" onClick={() => { resetForm(); fetchLastEntryDate(selectedStoreId); }} data-testid="back-to-new-entry-btn">
                      Back to New Entry
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={viewPreviousDay} data-testid="view-previous-day-btn">
                      <Eye className="h-4 w-4 mr-1" />View Previous Day ({format(new Date(lastEntryDate), 'dd MMM')})
                    </Button>
                  )}
                </div>
              ) : isStaffLike && lastEntryDateLoaded && !lastEntryDate ? (
                /* Staff with NO existing entries: Allow first date selection */
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="sales-date-picker-first"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, 'PPP')}
                      <span className="ml-2 text-xs text-muted-foreground">(First Entry - Select Date)</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                /* Admin/Manager: Can select any date */
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="sales-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Shift Selection */}
            <div className="space-y-2">
              <Label>Shift</Label>
              <Select value={selectedShift} onValueChange={setSelectedShift}>
                <SelectTrigger data-testid="sales-shift-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="midday">Mid Day</SelectItem>
                  <SelectItem value="dayend">Day End</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Store Type Indicator */}
            <div className="space-y-2">
              <Label>Store Type</Label>
              <div className="h-10 px-3 flex items-center bg-muted rounded-md text-sm">
                <Store className="h-4 w-4 mr-2 text-muted-foreground" />
                {storeDetails?.store_type?.replace('_', ' ')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Entry Warning */}
      {existingEntry && !isEditing && !isViewMode && (
        <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="font-medium text-yellow-500">Entry already exists</p>
              <p className="text-sm text-muted-foreground">
                An entry for {format(selectedDate, 'PPP')} ({selectedShift.toUpperCase()}) has already been submitted.
                {canEdit && ' You can edit it below.'}
                {canEditHistoricalEntry && ' (Historical entry - Scratch card quantities cannot be modified)'}
                {(isAdmin && !isSuperUser) && !canEditEntry && ' Only the most recent entry can be edited.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Entry button - available for ALL users */}
            <Button 
              onClick={() => loadEntryForViewing(existingEntry)}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10"
              data-testid="view-entry-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              View Entry
            </Button>
            {/* Edit Entry button - for Admin/Super User on latest entry OR Super User on historical */}
            {canEdit && (
              <Button 
                onClick={() => loadEntryForEditing(existingEntry)}
                variant="outline"
                className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                data-testid="edit-entry-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit Entry
              </Button>
            )}
            {isAdmin && !isSuperUser && !isLatestEntry && existingEntry && (
              <span className="text-xs text-muted-foreground">
                (Can only edit the most recent entry: {latestEntryDate})
              </span>
            )}
          </div>
        </div>
      )}

      {/* View Mode Header */}
      {isViewMode && existingEntry && (
        <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-green-500" />
            <div>
              <p className="font-medium text-green-500">Viewing Entry (Read-Only)</p>
              <p className="text-sm text-muted-foreground">
                Viewing entry for {format(selectedDate, 'PPP')} ({selectedShift.toUpperCase()})
              </p>
            </div>
          </div>
          <Button 
            onClick={cancelViewing}
            variant="outline"
            className="border-green-500/50 text-green-500 hover:bg-green-500/10"
          >
            Close View
          </Button>
        </div>
      )}

      {/* Editing Mode Header */}
      {isEditing && existingEntry && (
        <div className="flex items-center justify-between p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Pencil className="h-5 w-5 text-blue-500" />
            <div>
              <p className="font-medium text-blue-500">Editing Entry</p>
              <p className="text-sm text-muted-foreground">
                Modifying entry for {format(selectedDate, 'PPP')} ({selectedShift.toUpperCase()})
              </p>
            </div>
          </div>
          <Button 
            onClick={cancelEditing}
            variant="outline"
            className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
          >
            Cancel Edit
          </Button>
        </div>
      )}

      {storeDetails && !existingEntry && !viewingPreviousDay && (
        <ApprovalStatusBanner
          approvalRequest={approvalRequest}
          adminEditMode={!!adminEditRequestId}
          onCancelAdminEdit={() => navigate('/approval-requests')}
          onSubmitApproved={handleSubmit}
        />
      )}

      {(!existingEntry || isEditing || isViewMode) && storeDetails && (
        <>
          {/* Tab Navigation - Order: Fuel Costs (required first for fuel stations), then Shop Sales, Paid Outs, Scratch Cards, Fuel Sales, Drive Off/Debt, Bunkering, Tenders */}
          <div className="mb-6">
            <Tabs value={activeTab} onValueChange={(value) => {
              // For fuel stations, check if fuel costs are completed before allowing navigation to other tabs (skip in view mode)
              if (!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && value !== 'fuelcost' && !fuelCostsCompleted) {
                toast.error('Please enter and save daily fuel costs first before accessing other tabs.');
                return;
              }
              setActiveTab(value);
            }} className="w-full">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <TabsList className="flex flex-wrap gap-1 h-auto p-1 w-full lg:w-auto bg-muted/50">
                  {/* Fuel Cost Tab - FIRST for fuel stations (required) */}
                  {storeDetails?.store_type === 'fuel_station' && (
                    <TabsTrigger 
                      value="fuelcost" 
                      className={`flex items-center gap-2 ${fuelCostsCompleted ? 'bg-green-500/20 text-green-600 dark:text-green-400 data-[state=active]:bg-green-500' : 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 data-[state=active]:bg-cyan-500'} data-[state=active]:text-white`}
                    >
                      <DollarSign className="h-4 w-4" />
                      <span className="hidden sm:inline">Fuel Costs</span>
                      {fuelCostsCompleted && <span className="ml-1 text-xs">✓</span>}
                    </TabsTrigger>
                  )}
                  {/* Day End Report Tab - right after Fuel Costs (fuel stations, new entries) */}
                  {!isEditing && !isViewMode && (
                    <TabsTrigger 
                      value="dayend" 
                      disabled={storeDetails?.store_type === 'fuel_station' && !isEntryLocked && !fuelCostsCompleted}
                      className={`flex items-center gap-2 ${tillReport ? 'bg-green-500/20 text-green-600 dark:text-green-400 data-[state=active]:bg-green-500' : 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 data-[state=active]:bg-indigo-500'} data-[state=active]:text-white ${storeDetails?.store_type === 'fuel_station' && !isEntryLocked && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="tab-dayend"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="hidden sm:inline">Day End Report</span>
                      {tillReport && <span className="ml-1 text-xs">✓</span>}
                    </TabsTrigger>
                  )}
                  <TabsTrigger 
                    value="sales" 
                    disabled={!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted}
                    className={`flex items-center gap-2 bg-blue-500/20 text-blue-600 dark:text-blue-400 data-[state=active]:bg-blue-500 data-[state=active]:text-white ${!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PoundSterling className="h-4 w-4" />
                    <span className="hidden sm:inline">Shop Sales</span>
                  </TabsTrigger>
                  {hasScratchCards && (
                    <TabsTrigger 
                      value="scratch" 
                      disabled={!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted}
                      className={`flex items-center gap-2 bg-purple-500/20 text-purple-600 dark:text-purple-400 data-[state=active]:bg-purple-500 data-[state=active]:text-white ${!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Ticket className="h-4 w-4" />
                      <span className="hidden sm:inline">Scratch Cards</span>
                    </TabsTrigger>
                  )}
                  {storeDetails?.store_type === 'fuel_station' && (
                    <TabsTrigger 
                      value="fuel" 
                      disabled={!isViewMode && !fuelCostsCompleted}
                      className={`flex items-center gap-2 bg-amber-500/20 text-amber-600 dark:text-amber-400 data-[state=active]:bg-amber-500 data-[state=active]:text-white ${!isViewMode && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Fuel className="h-4 w-4" />
                      <span className="hidden sm:inline">Fuel Sales</span>
                    </TabsTrigger>
                  )}
                  {storeDetails?.store_type === 'fuel_station' && (
                    <TabsTrigger 
                      value="incidents" 
                      disabled={!isViewMode && !fuelCostsCompleted}
                      className={`flex items-center gap-2 bg-orange-500/20 text-orange-600 dark:text-orange-400 data-[state=active]:bg-orange-500 data-[state=active]:text-white ${!isViewMode && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Car className="h-4 w-4" />
                      <span className="hidden sm:inline">Drive Off / Debt</span>
                    </TabsTrigger>
                  )}
                  {storeDetails?.store_type === 'fuel_station' && storeDetails?.fuel_cards_enabled && (
                    <TabsTrigger 
                      value="fuelcards" 
                      disabled={!isViewMode && !fuelCostsCompleted}
                      className={`flex items-center gap-2 bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 data-[state=active]:bg-indigo-500 data-[state=active]:text-white ${!isViewMode && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <CreditCard className="h-4 w-4" />
                      <span className="hidden sm:inline">Bunkering</span>
                    </TabsTrigger>
                  )}
                  <TabsTrigger 
                    value="paidouts" 
                    disabled={!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted}
                    className={`flex items-center gap-2 bg-red-500/20 text-red-600 dark:text-red-400 data-[state=active]:bg-red-500 data-[state=active]:text-white ${!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Banknote className="h-4 w-4" />
                    <span className="hidden sm:inline">Paid Outs</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="tenders" 
                    disabled={!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted}
                    className={`flex items-center gap-2 bg-green-500/20 text-green-600 dark:text-green-400 data-[state=active]:bg-green-500 data-[state=active]:text-white ${!isViewMode && !isEntryLocked && storeDetails?.store_type === 'fuel_station' && !fuelCostsCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CreditCard className="h-4 w-4" />
                    <span className="hidden sm:inline">Tenders</span>
                  </TabsTrigger>
                </TabsList>

                {/* Save Draft Button - Accessible from any tab, hidden in View Mode */}
                {(!existingEntry || isEditing) && !isEditing && !isViewMode && !isEntryLocked && (adminEditRequestId || approvalRequest?.status !== 'approved') && (
                  <Button 
                    variant="outline"
                    onClick={saveDraft}
                    disabled={savingDraft || saving}
                    className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                    data-testid="save-draft-btn"
                  >
                    {savingDraft ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <ClipboardCopy className="h-4 w-4 mr-2" />
                        Save Draft
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Tabs>
          </div>
          
          <fieldset
            disabled={isPendingLock}
            className={`min-w-0 border-0 p-0 m-0 ${isEntryLocked ? 'opacity-80' : ''} ${isApprovedLock ? 'pointer-events-none' : ''}`}
            onKeyDownCapture={(e) => {
              if (isApprovedLock && e.key !== 'Tab') { e.preventDefault(); e.stopPropagation(); }
            }}
            onPasteCapture={(e) => { if (isApprovedLock) e.preventDefault(); }}
            onInputCapture={() => {
              // Defer so the controlled input's own change is committed first (avoids dropped keystrokes)
              if (isEntryLocked || interactionTimerRef.current) return;
              interactionTimerRef.current = setTimeout(() => { interactionTimerRef.current = null; setUserHasInteracted(true); }, 0);
            }}
            data-testid="sales-entry-form-fieldset"
          >
          {/* Shop Sales Tab */}
          {activeTab === 'sales' && (
            <ShopSalesTab
              tillLock={tillLock}
              tillServiceKeys={Object.keys(tillReport?.mapped?.service_amounts || {})}
              tillVolumeIds={Object.keys(tillReport?.mapped?.department_volumes || {})}
              tillCoffeeFilled={tillReport?.mapped?.coffee_sold_qty !== null && tillReport?.mapped?.coffee_sold_qty !== undefined}
              storeDetails={storeDetails}
              totalSalesAmount={totalSalesAmount}
              setTotalSalesAmount={setTotalSalesAmount}
              departmentAmounts={departmentAmounts}
              setDepartmentAmounts={setDepartmentAmounts}
              departmentVolumes={departmentVolumes}
              setDepartmentVolumes={setDepartmentVolumes}
              otherDepartmentsAmount={otherDepartmentsAmount}
              serviceAmounts={serviceAmounts}
              setServiceAmounts={setServiceAmounts}
              serviceKeys={serviceKeys}
              enabledServices={enabledServices}
              totalServiceSales={totalServiceSales}
              totalSales={totalSales}
              coffeeTracking={coffeeTracking}
              updateCoffeeSoldQty={updateCoffeeSoldQty}
              updateCoffeeReading={updateCoffeeReading}
              openingReadingsLoading={coffeeOpeningLoading}
              openingReadingsError={coffeeOpeningError}
              onRetryOpeningReadings={() => {
                // Retry ONLY coffee - preserve user-entered closing readings
                initializeCoffeeTracking(selectedDate, selectedStoreId, true);
              }}
              scratchCardVerified={scratchCardVerified}
              setScratchCardVerified={setScratchCardVerified}
              handleAmountChange={handleAmountChange}
              handleAmountBlur={handleAmountBlur}
              handleAmountFocus={handleAmountFocus}
              setUserHasInteracted={setUserHasInteracted}
              toast={toast}
            />
          )}

          {/* Fuel Cost Prices Tab */}
          {activeTab === 'fuelcost' && storeDetails?.store_type === 'fuel_station' && (
            <FuelCostTab
              storeDetails={storeDetails}
              selectedDate={selectedDate}
              fuelCostPrices={fuelCostPrices}
              setFuelCostPrices={setFuelCostPrices}
              previousDayCostPrices={previousDayCostPrices}
              saveFuelCostPrices={saveFuelCostPrices}
              savingCostPrices={savingCostPrices}
            />
          )}

          {/* Day End Report Tab */}
          {activeTab === 'dayend' && (
            <DayEndReportTab
              storeId={selectedStoreId}
              salesDate={selectedDate}
              tillReport={tillReport}
              onApply={applyTillReport}
              canUpload={!isEntryLocked}
            />
          )}

          {/* Fuel Sales Tab */}
          {activeTab === 'fuel' && storeDetails?.store_type === 'fuel_station' && (
            <FuelSalesTab
              tillLock={tillLock}
              storeDetails={storeDetails}
              fuelSalesDeptAmount={fuelSalesDeptAmount}
              setFuelSalesDeptAmount={setFuelSalesDeptAmount}
              retailSalesAmount={retailSalesAmount}
              setRetailSalesAmount={setRetailSalesAmount}
              bunkeringSalesAmount={bunkeringSalesAmount}
              setBunkeringSalesAmount={setBunkeringSalesAmount}
              driveOffDeptAmount={driveOffDeptAmount}
              setDriveOffDeptAmount={setDriveOffDeptAmount}
              fuelPrices={fuelPrices}
              fuelVolumes={fuelVolumes}
              setFuelVolumes={setFuelVolumes}
              fuelCosts={fuelCosts}
              fuelCardTotals={fuelCardTotals}
              calculatedFuelSales={calculatedFuelSales}
              incidentDeductions={incidentDeductions}
              incidentRecoveries={incidentRecoveries}
              handleAmountFocus={handleAmountFocus}
              priceSourceDate={fuelPriceSourceDate}
              pricesLoadedFrom={fuelPricesSource}
              isAdmin={isAdmin || isSuperUser}
            />
          )}

          {/* Bunkering Tab */}
          {activeTab === 'fuelcards' && storeDetails?.store_type === 'fuel_station' && storeDetails?.fuel_cards_enabled && (
            <BunkeringTab
              tillLock={tillLock}
              storeDetails={storeDetails}
              filteredActiveFuelCards={filteredActiveFuelCards}
              fuelCardTransactions={fuelCardTransactions}
              setFuelCardTransactions={setFuelCardTransactions}
              fuelPrices={fuelPrices}
            />
          )}


          {/* Paid Outs Tab */}
          {activeTab === 'paidouts' && (
            <PaidOutsTab
              hasLottery={hasLottery}
              hasScratchCards={hasScratchCards}
              lotteryPrizePaid={lotteryPrizePaid}
              setLotteryPrizePaid={setLotteryPrizePaid}
              scratchPrizePaid={scratchPrizePaid}
              setScratchPrizePaid={setScratchPrizePaid}
              paidOuts={paidOuts}
              paidOutTypes={paidOutTypes}
              suppliers={suppliers}
              creditors={creditors}
              newPaidOut={newPaidOut}
              setNewPaidOut={setNewPaidOut}
              addPaidOut={addPaidOut}
              removePaidOut={removePaidOut}
              editPaidOut={editPaidOut}
              editingPaidOutId={editingPaidOutId}
              cancelEditPaidOut={cancelEditPaidOut}
              totalPaidOut={totalPaidOut}
            />
          )}

          {/* Tenders Tab */}
          {activeTab === 'tenders' && (
            <TendersTab
              tillLock={tillLock && !!tillReport?.mapped?.tenders}
              tillCash={tillReport?.mapped?.tenders?.payments?.cash}
              tillCashDiscrepancy={tillReport?.mapped?.cash_discrepancy}
              tillOtherPayments={tillReport?.mapped?.tenders?.other_payments || {}}
              submitDisabled={isEntryLocked}
              storeDetails={storeDetails}
              enteredTotalSales={enteredTotalSales}
              totalServiceSales={totalServiceSales}
              fuelSalesZReading={fuelSalesZReading}
              totalSales={totalSales}
              lotteryPrizePaid={lotteryPrizePaid}
              scratchPrizePaid={scratchPrizePaid}
              totalPaidOut={totalPaidOut}
              incidentDeductions={incidentDeductions}
              incidentRecoveries={incidentRecoveries}
              paymentAmounts={paymentAmounts}
              setPaymentAmounts={setPaymentAmounts}
              handleAmountChange={handleAmountChange}
              handleAmountBlur={handleAmountBlur}
              handleAmountFocus={handleAmountFocus}
              filteredActiveFuelCards={filteredActiveFuelCards}
              fuelCardTransactions={fuelCardTransactions}
              setFuelCardTransactions={setFuelCardTransactions}
              fuelCardTotals={fuelCardTotals}
              totalPayments={totalPayments}
              expectedPayments={expectedPayments}
              variance={variance}
              existingEntry={existingEntry}
              isEditing={isEditing}
              isViewMode={isViewMode}
              saving={saving}
              handleSubmit={handleSubmit}
              cancelEditing={cancelEditing}
            />
          )}

          {/* Scratch Cards Tab */}
          {activeTab === 'scratch' && (
            <div className="space-y-6 max-w-4xl">
              {/* Historical Entry Warning for Scratch Cards */}
              {scratchCardsDisabled && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium text-orange-500">Historical Entry - Scratch Cards Locked</p>
                      <p className="text-sm text-muted-foreground">
                        Scratch card quantities cannot be modified on historical entries to maintain stock integrity.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <Card>
                <CardHeader className="bg-purple-500/5 border-b border-purple-500/20">
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Ticket className="h-5 w-5 text-purple-500" />
                    Scratch Card Stock Count
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Loading/Error state for scratch card opening stock */}
                  {scratchOpeningLoading && (
                    <div className="flex items-center gap-2 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20 mb-4">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      <span className="text-sm text-blue-500">Loading scratch card opening stock...</span>
                    </div>
                  )}
                  
                  {scratchOpeningError && (
                    <div className="flex items-center gap-2 p-4 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
                      <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-red-500 flex-1">{scratchOpeningError}</span>
                      <button 
                        onClick={() => fetchScratchCardOpeningStock(selectedStoreId, selectedDate, selectedShift, true)}
                        className="text-primary hover:underline font-medium text-sm"
                        type="button"
                        data-testid="scratch-opening-retry-btn"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  
                  {scratchCardDenominations.length > 0 ? (
                    <div className="space-y-4">
                      {/* Header Row */}
                      <div className="hidden md:grid md:grid-cols-6 gap-2 px-3 py-2 bg-muted/50 rounded-lg text-sm font-medium">
                        <div>Denomination</div>
                        <div className="text-center">Opening Stock</div>
                        <div className="text-center">Settled</div>
                        <div className="text-center">Closing Stock</div>
                        <div className="text-center">Sold Qty</div>
                        <div className="text-right">Sales Value</div>
                      </div>
                      
                      {/* Sort scratch cards by value in ascending order */}
                      {[...scratchCardDenominations].sort((a, b) => (a.value || 0) - (b.value || 0)).map(denom => {
                        // Show loading/error state for opening stock
                        const hasOpeningStock = scratchCardOpeningStock[denom.id] !== undefined;
                        const openingStock = hasOpeningStock ? (parseInt(scratchCardOpeningStock[denom.id]) || 0) : null;
                        const openingDisplay = scratchOpeningLoading ? '...' : 
                                               scratchOpeningError ? '!' :
                                               openingStock !== null ? openingStock : '...';
                        
                        const settled = parseInt(scratchCardSettled[denom.id]) || 0;
                        const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
                        // Sold Qty = (Opening Stock + Settled) - Closing (only calculate if opening is loaded)
                        const soldQty = openingStock !== null ? (openingStock + settled) - closingStock : 0;
                        const salesValue = soldQty * denom.value;
                        const isNegative = soldQty < 0;
                        
                        return (
                          <div key={denom.id} className="p-4 border rounded-lg bg-muted/30">
                            {/* Mobile layout */}
                            <div className="md:hidden space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">£{denom.value} - {denom.name}</span>
                                <span className="text-xs text-muted-foreground">Pack: {denom.pack_size}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs text-muted-foreground">Opening Stock</Label>
                                  <div className={`font-mono text-lg ${scratchOpeningLoading ? 'animate-pulse text-muted-foreground' : ''} ${scratchOpeningError ? 'text-red-500 font-bold' : ''}`} data-testid={`scratch-opening-${denom.id}`}>
                                    {openingDisplay}
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Settled</Label>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={scratchCardSettled[denom.id] || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '' || /^\d+$/.test(value)) {
                                        const packSize = denom.pack_size || 100;
                                        const numValue = parseInt(value) || 0;
                                        // Validate: must be 0 or a multiple of pack size
                                        if (value === '' || numValue === 0 || numValue % packSize === 0) {
                                          setScratchCardSettled({ ...scratchCardSettled, [denom.id]: value });
                                          setScratchCardVerified(false);
                                        } else {
                                          // Show error but still allow input for correction
                                          setScratchCardSettled({ ...scratchCardSettled, [denom.id]: value });
                                          setScratchCardVerified(false);
                                          toast.error(`${denom.name}: Settled must be multiple of ${packSize} (pack size)`);
                                        }
                                      }
                                    }}
                                    onFocus={handleAmountFocus}
                                    className={`w-full font-mono text-center ${
                                      scratchCardSettled[denom.id] && 
                                      parseInt(scratchCardSettled[denom.id]) > 0 &&
                                      parseInt(scratchCardSettled[denom.id]) % (denom.pack_size || 100) !== 0 
                                        ? 'border-red-500 bg-red-500/10' 
                                        : ''
                                    }`}
                                    placeholder="0"
                                    disabled={scratchCardsDisabled}
                                  />
                                  {scratchCardSettled[denom.id] && 
                                   parseInt(scratchCardSettled[denom.id]) > 0 &&
                                   parseInt(scratchCardSettled[denom.id]) % (denom.pack_size || 100) !== 0 && (
                                    <p className="text-xs text-red-500 mt-1">Must be multiple of {denom.pack_size}</p>
                                  )}
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Closing Stock</Label>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={scratchCardClosingStock[denom.id] || ''}
                                    onChange={(e) => {
                                      if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                                        setScratchCardClosingStock({ ...scratchCardClosingStock, [denom.id]: e.target.value });
                                        setScratchCardVerified(false);
                                      }
                                    }}
                                    onFocus={handleAmountFocus}
                                    className="w-full font-mono text-center"
                                    placeholder="0"
                                    disabled={scratchCardsDisabled}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Sold Qty</Label>
                                  <div className={`font-mono text-lg ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
                                    {soldQty}
                                  </div>
                                </div>
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t">
                                <span className="text-sm">Sales Value</span>
                                <span className={`font-mono font-bold ${isNegative ? 'text-red-500' : ''}`}>
                                  £{salesValue.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            
                            {/* Desktop layout */}
                            <div className="hidden md:grid md:grid-cols-6 gap-2 items-center">
                              <div>
                                <span className="font-medium">£{denom.value} - {denom.name}</span>
                                <span className="text-xs text-muted-foreground block">Pack: {denom.pack_size}</span>
                              </div>
                              <div className="text-center">
                                <span className={`font-mono text-lg ${scratchOpeningLoading ? 'animate-pulse text-muted-foreground' : ''} ${scratchOpeningError ? 'text-red-500 font-bold' : ''}`} data-testid={`scratch-opening-desktop-${denom.id}`}>
                                  {openingDisplay}
                                </span>
                              </div>
                              <div className="text-center">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={scratchCardSettled[denom.id] || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d+$/.test(value)) {
                                      const packSize = denom.pack_size || 100;
                                      const numValue = parseInt(value) || 0;
                                      setScratchCardSettled({ ...scratchCardSettled, [denom.id]: value });
                                      setScratchCardVerified(false);
                                      // Show toast error for invalid pack size
                                      if (value !== '' && numValue > 0 && numValue % packSize !== 0) {
                                        toast.error(`${denom.name}: Settled must be multiple of ${packSize} (pack size)`);
                                      }
                                    }
                                  }}
                                  onFocus={handleAmountFocus}
                                  className={`w-20 mx-auto font-mono text-center ${
                                    scratchCardSettled[denom.id] && 
                                    parseInt(scratchCardSettled[denom.id]) > 0 &&
                                    parseInt(scratchCardSettled[denom.id]) % (denom.pack_size || 100) !== 0 
                                      ? 'border-red-500 bg-red-500/10' 
                                      : ''
                                  }`}
                                  placeholder="0"
                                  disabled={scratchCardsDisabled}
                                />
                                {scratchCardSettled[denom.id] && 
                                 parseInt(scratchCardSettled[denom.id]) > 0 &&
                                 parseInt(scratchCardSettled[denom.id]) % (denom.pack_size || 100) !== 0 && (
                                  <p className="text-xs text-red-500 mt-1">Must be multiple of {denom.pack_size}</p>
                                )}
                              </div>
                              <div className="text-center">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={scratchCardClosingStock[denom.id] || ''}
                                  onChange={(e) => {
                                    if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                                      setScratchCardClosingStock({ ...scratchCardClosingStock, [denom.id]: e.target.value });
                                      setScratchCardVerified(false);
                                    }
                                  }}
                                  onFocus={handleAmountFocus}
                                  className="w-20 mx-auto font-mono text-center"
                                  placeholder="0"
                                  disabled={scratchCardsDisabled}
                                />
                              </div>
                              <div className={`text-center font-mono text-lg ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
                                {soldQty}
                              </div>
                              <div className={`text-right font-mono font-bold ${isNegative ? 'text-red-500' : ''}`}>
                                £{salesValue.toFixed(2)}
                              </div>
                            </div>
                            
                            {isNegative && (
                              <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Closing stock + Settled exceeds Opening stock
                              </p>
                            )}
                            
                            {/* Pack Numbers Input - shown when settled qty > 0 */}
                            {parseInt(scratchCardSettled[denom.id] || 0) > 0 && (
                              <div className="mt-2 pt-2 border-t border-dashed">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  Pack Number(s) <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  placeholder="e.g. 1021-123456789, 1021-987654321"
                                  value={scratchCardSettledPackNumbers[denom.id] || ''}
                                  onChange={(e) => setScratchCardSettledPackNumbers({ 
                                    ...scratchCardSettledPackNumbers, 
                                    [denom.id]: e.target.value 
                                  })}
                                  className={`font-mono text-sm mt-1 ${
                                    !scratchCardSettledPackNumbers[denom.id]?.trim() 
                                      ? 'border-red-500 bg-red-500/10' 
                                      : ''
                                  }`}
                                  disabled={scratchCardsDisabled}
                                />
                                {!scratchCardSettledPackNumbers[denom.id]?.trim() && (
                                  <p className="text-xs text-red-500 mt-1">Pack number is required</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Total Row */}
                      <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Total Scratch Card Sales</span>
                          <span className="font-mono text-xl font-bold">
                            £{scratchCardDenominations.reduce((sum, denom) => {
                              const openingStock = parseInt(scratchCardOpeningStock[denom.id]) || 0;
                              const settled = parseInt(scratchCardSettled[denom.id]) || 0;
                              const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
                              // Correct formula: Sold Qty = (Opening Stock + Settled) - Closing Stock
                              const soldQty = (openingStock + settled) - closingStock;
                              return sum + (soldQty * denom.value);
                            }, 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Variance Display - Compare declared amount vs calculated */}
                      {(() => {
                        const declaredAmount = parseFloat(serviceAmounts['scratch_cards'] || 0);
                        const calculatedAmount = scratchCardDenominations.reduce((sum, denom) => {
                          const openingStock = parseInt(scratchCardOpeningStock[denom.id]) || 0;
                          const settled = parseInt(scratchCardSettled[denom.id]) || 0;
                          const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
                          const soldQty = (openingStock + settled) - closingStock;
                          return sum + (soldQty * denom.value);
                        }, 0);
                        const variance = declaredAmount - calculatedAmount;
                        const hasVariance = Math.abs(variance) >= 0.01;
                        
                        return (
                          <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                            <div className="flex justify-between items-center text-sm">
                              <span>Declared on Shop Sales Tab:</span>
                              <span className="font-mono font-medium">£{declaredAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span>Calculated from Stock Count:</span>
                              <span className="font-mono font-medium">£{calculatedAmount.toFixed(2)}</span>
                            </div>
                            <div className={`flex justify-between items-center p-2 rounded ${
                              !hasVariance 
                                ? 'bg-green-500/10 border border-green-500/30' 
                                : variance < 0
                                  ? 'bg-red-500/10 border border-red-500/30'
                                  : 'bg-yellow-500/10 border border-yellow-500/30'
                            }`}>
                              <span className="font-medium">Variance:</span>
                              <span className={`font-mono font-bold ${
                                !hasVariance ? 'text-green-500' : variance < 0 ? 'text-red-500' : 'text-yellow-500'
                              }`}>
                                £{variance.toFixed(2)}
                              </span>
                            </div>
                            {hasVariance && (
                              <p className={`text-xs mt-1 ${variance < 0 ? 'text-red-500' : 'text-yellow-500'}`}>
                                Please verify scratch card sales amount matches the stock count
                              </p>
                            )}
                          </div>
                        );
                      })()}
                      
                      {!scratchCardVerified && (
                        <Button
                          onClick={() => {
                            // STEP 1: Pack Size Validation - BLOCKING
                            const invalidPackSizes = scratchCardDenominations.filter(denom => {
                              const qty = parseInt(scratchCardSettled[denom.id] || 0);
                              const packSize = denom.pack_size || 100;
                              return qty > 0 && qty % packSize !== 0;
                            });
                            
                            if (invalidPackSizes.length > 0) {
                              const errorMsg = invalidPackSizes.map(d => {
                                const qty = scratchCardSettled[d.id];
                                const packSize = d.pack_size || 100;
                                return `${d.name}: ${qty} (must be multiple of ${packSize})`;
                              }).join(', ');
                              toast.error(`Invalid pack sizes! Please correct: ${errorMsg}`);
                              return; // BLOCKING - must fix pack sizes
                            }
                            
                            // STEP 2: Calculate total from stock count
                            let calculatedTotal = 0;
                            const stockDetails = [];
                            
                            scratchCardDenominations.forEach(denom => {
                              const openingStock = parseInt(scratchCardOpeningStock[denom.id]) || 0;
                              const settled = parseInt(scratchCardSettled[denom.id]) || 0;
                              const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
                              // Sold Qty = (Opening Stock + Settled) - Closing Stock
                              const soldQty = (openingStock + settled) - closingStock;
                              const salesValue = soldQty * (denom.value || 0);
                              
                              calculatedTotal += salesValue;
                              
                              // Include all items with sales in stock details
                              if (soldQty !== 0) {
                                stockDetails.push({
                                  name: denom.name,
                                  value: denom.value || 0,
                                  opening: openingStock,
                                  settled: settled,
                                  closing: closingStock,
                                  soldQty: soldQty,
                                  salesValue: salesValue
                                });
                              }
                            });
                            
                            // STEP 3: Check variance between calculated and declared amounts
                            const scratchCardSalesAmount = parseFloat(serviceAmounts['scratch_cards'] || 0);
                            const amountVariance = calculatedTotal - scratchCardSalesAmount;
                            
                            // Show popup if there's a variance between declared and calculated
                            if (Math.abs(amountVariance) > 0.01) {
                              // Build variance data for dialog - use 'variance' field for backend compatibility
                              const varianceData = stockDetails.map(item => ({
                                name: item.name,
                                value: item.value,
                                opening: item.opening,
                                settled: item.settled,
                                closing: item.closing,
                                soldQty: item.soldQty,
                                salesValue: item.salesValue,
                                variance: item.soldQty,  // For backend email API compatibility
                                varianceValue: item.salesValue  // For backend email API compatibility
                              }));
                              
                              setScratchCardVarianceDetails(varianceData);
                              setScratchCardVarianceDialogOpen(true);
                              return; // Wait for user confirmation via dialog
                            }
                            
                            // STEP 4: No variance - proceed with verification
                            if (Math.abs(amountVariance) > 0.01) {
                              toast.warning(
                                <div>
                                  <p className="font-bold">⚠️ Amount Mismatch (Non-blocking)</p>
                                  <p className="mt-2">Declared Sales: £{scratchCardSalesAmount.toFixed(2)}</p>
                                  <p>Calculated from Stock: £{calculatedTotal.toFixed(2)}</p>
                                  <p className="mt-2 font-semibold text-amber-400">Variance: £{amountVariance.toFixed(2)}</p>
                                  <p className="mt-2 text-sm">Consider updating the Scratch Cards amount in Shop Sales tab.</p>
                                </div>,
                                { duration: 8000 }
                              );
                            }
                            
                            // STEP 5: All validations passed - update scratchCardQuantities with calculated sold qty
                            const quantities = {};
                            scratchCardDenominations.forEach(denom => {
                              const openingStock = parseInt(scratchCardOpeningStock[denom.id]) || 0;
                              const settled = parseInt(scratchCardSettled[denom.id]) || 0;
                              const closingStock = parseInt(scratchCardClosingStock[denom.id]) || 0;
                              quantities[denom.id] = ((openingStock + settled) - closingStock).toString();
                            });
                            setScratchCardQuantities(quantities);
                            setScratchCardVerified(true);
                            toast.success('Scratch card sales verified');
                          }}
                          className="w-full"
                          variant="outline"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Verify Stock Count
                        </Button>
                      )}
                      
                      {scratchCardVerified && (
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <span className="text-green-500 font-medium">Scratch card sales verified</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No scratch card denominations configured</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Drive Off / Debt Tab */}
          {activeTab === 'incidents' && storeDetails?.store_type === 'fuel_station' && (
            <div className="space-y-6 max-w-4xl">
              {/* Search Past Incidents for Payment */}
              <Card className="border-green-500/30">
                <CardHeader className="bg-green-500/5 border-b border-green-500/20 cursor-pointer" onClick={() => setIncidentSearchOpen(!incidentSearchOpen)}>
                  <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Barlow Condensed' }}>
                    <div className="flex items-center gap-2">
                      <Search className="h-5 w-5 text-green-500" />
                      Search Past Incidents for Payment
                    </div>
                    <span className="text-sm font-normal text-muted-foreground">
                      {incidentSearchOpen ? '▼' : '▶'} {incidentsToRecover.length > 0 && `(${incidentsToRecover.length} staged)`}
                    </span>
                  </CardTitle>
                </CardHeader>
                {incidentSearchOpen && (
                  <CardContent className="space-y-4 pt-4">
                    <p className="text-sm text-muted-foreground">
                      Search for unpaid Drive Off or Debt incidents. When a customer pays their debt at the store, mark it here to add the recovered amount to today's expected payments.
                    </p>
                    
                    {/* Search Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="space-y-2">
                        <Label>Date From</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                              data-testid="incident-search-date-from"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {incidentSearchDateFrom ? format(new Date(incidentSearchDateFrom), 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={incidentSearchDateFrom ? new Date(incidentSearchDateFrom) : undefined}
                              onSelect={(date) => setIncidentSearchDateFrom(date ? format(date, 'yyyy-MM-dd') : '')}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Date To</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                              data-testid="incident-search-date-to"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {incidentSearchDateTo ? format(new Date(incidentSearchDateTo), 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={incidentSearchDateTo ? new Date(incidentSearchDateTo) : undefined}
                              onSelect={(date) => setIncidentSearchDateTo(date ? format(date, 'yyyy-MM-dd') : '')}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Car Registration</Label>
                        <Input
                          placeholder="e.g. AB12 CDE"
                          value={incidentSearchCarReg}
                          onChange={(e) => setIncidentSearchCarReg(e.target.value.toUpperCase())}
                          data-testid="incident-search-carreg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Amount (±£5)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="e.g. 45.00"
                          value={incidentSearchAmount}
                          onChange={(e) => setIncidentSearchAmount(e.target.value)}
                          data-testid="incident-search-amount"
                        />
                      </div>
                      <div className="space-y-2 flex items-end gap-2">
                        <Button 
                          onClick={handleIncidentSearch} 
                          disabled={incidentSearchLoading}
                          className="flex-1"
                          data-testid="incident-search-btn"
                        >
                          {incidentSearchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                          Search
                        </Button>
                        <Button variant="outline" onClick={clearIncidentSearch} size="icon">
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Search Results */}
                    {incidentSearchResults.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Search Results ({incidentSearchResults.length} found)</Label>
                          <Button 
                            onClick={handleMarkForRecovery}
                            disabled={selectedRecoveryIncidents.length === 0}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="mark-for-recovery-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark {selectedRecoveryIncidents.length > 0 ? `(${selectedRecoveryIncidents.length})` : ''} as Paid Today
                          </Button>
                        </div>
                        <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                          {incidentSearchResults.map(incident => (
                            <div 
                              key={incident.id}
                              className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50 ${
                                selectedRecoveryIncidents.find(i => i.id === incident.id) ? 'bg-green-500/10' : ''
                              }`}
                              onClick={() => toggleRecoverySelection(incident)}
                            >
                              <input
                                type="checkbox"
                                checked={!!selectedRecoveryIncidents.find(i => i.id === incident.id)}
                                onChange={() => toggleRecoverySelection(incident)}
                                className="h-4 w-4"
                              />
                              <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                                <span>{incident.incident_date}</span>
                                <span className="font-mono">{incident.vehicle_registration || 'N/A'}</span>
                                <span className="font-mono font-bold">£{incident.amount?.toFixed(2)}</span>
                                <span className={incident.incident_type === 'drive_off' ? 'text-red-500' : 'text-purple-500'}>
                                  {incident.incident_type === 'drive_off' ? 'Drive Off' : 'Debt'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Staged Recoveries */}
                    {incidentsToRecover.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-green-600">Today's Recoveries (Staged for Submission)</Label>
                        <div className="border border-green-500/30 rounded-lg bg-green-500/5 divide-y divide-green-500/20">
                          {incidentsToRecover.map(incident => (
                            <div key={incident.id} className="p-3 flex items-center justify-between">
                              <div className="grid grid-cols-4 gap-2 text-sm flex-1">
                                <span>{incident.incident_date}</span>
                                <span className="font-mono">{incident.vehicle_registration || 'N/A'}</span>
                                <span className="font-mono font-bold text-green-600">£{incident.amount?.toFixed(2)}</span>
                                <span className={incident.incident_type === 'drive_off' ? 'text-red-500' : 'text-purple-500'}>
                                  {incident.incident_type === 'drive_off' ? 'Drive Off' : 'Debt'}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveRecovery(incident)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <div className="p-3 flex justify-between items-center bg-green-500/10">
                            <span className="font-medium">Total Recovery Amount</span>
                            <span className="font-mono font-bold text-green-600 text-lg">
                              +£{stagedRecoveries.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          These incidents will be marked as paid when you submit the sales entry. The recovery amount is added to the expected payments.
                        </p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* New Incident Form */}
              <Card>
                <CardHeader className="bg-red-500/5 border-b border-red-500/20">
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Car className="h-5 w-5 text-red-500" />
                    Record New Incident
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Incident Type */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="drive_off"
                        checked={newIncident.incident_type === 'drive_off'}
                        onChange={(e) => setNewIncident(prev => ({ ...prev, incident_type: e.target.value }))}
                      />
                      <span className="font-medium text-red-500">Drive Off</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="indebtedness"
                        checked={newIncident.incident_type === 'indebtedness'}
                        onChange={(e) => setNewIncident(prev => ({ ...prev, incident_type: e.target.value }))}
                      />
                      <span className="font-medium text-purple-500">Indebtedness</span>
                    </label>
                  </div>

                  {/* Basic Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Time *</Label>
                      <Input
                        type="time"
                        value={newIncident.incident_time}
                        onChange={(e) => setNewIncident(prev => ({ ...prev, incident_time: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pump *</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={newIncident.pump_number}
                        onChange={(e) => {
                          if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                            setNewIncident(prev => ({ ...prev, pump_number: e.target.value }));
                          }
                        }}
                        placeholder="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fuel Grade *</Label>
                      <Select
                        value={newIncident.fuel_grade_id}
                        onValueChange={(val) => {
                          const grade = storeDetails.fuel_grades?.find(g => g.id === val);
                          // Use current day's price from fuelPrices state
                          const currentPrice = parseFloat(fuelPrices[val]) || grade?.selling_price || 0;
                          const litres = parseFloat(newIncident.litres) || 0;
                          const calculatedAmount = (litres * currentPrice) / 100;
                          
                          setNewIncident(prev => ({
                            ...prev,
                            fuel_grade_id: val,
                            fuel_grade_name: grade?.name || '',
                            amount: litres > 0 ? calculatedAmount.toFixed(2) : prev.amount
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent>
                          {storeDetails.fuel_grades?.map(grade => {
                            const gradeColors = getFuelGradeColor(grade.name);
                            return (
                              <SelectItem key={grade.id} value={grade.id}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${gradeColors.bg}`}></span>
                                  {grade.name}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Litres *</Label>
                      <Input
                        type="text"
                        value={newIncident.litres}
                        onChange={(e) => handleIncidentLitresChange(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount (£) <span className="text-xs text-muted-foreground">(Auto-calculated)</span></Label>
                      <Input
                        type="text"
                        value={newIncident.amount}
                        readOnly
                        disabled
                        className="font-mono bg-muted cursor-not-allowed"
                        placeholder="0.00"
                      />
                      {newIncident.fuel_grade_id && (
                        <p className="text-xs text-muted-foreground">
                          Price: {parseFloat(fuelPrices[newIncident.fuel_grade_id]) || storeDetails?.fuel_grades?.find(g => g.id === newIncident.fuel_grade_id)?.selling_price || 0}p/L 
                          <span className="text-blue-500 ml-1">(from today's Fuel Sales)</span>
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Vehicle Registration</Label>
                      <Input
                        value={newIncident.vehicle_registration}
                        onChange={(e) => setNewIncident(prev => ({ ...prev, vehicle_registration: e.target.value.toUpperCase() }))}
                        placeholder="AB12 CDE"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cashier *</Label>
                      <Select
                        value={newIncident.cashier_id}
                        onValueChange={(val) => {
                          const cashier = cashiers.find(c => c.id === val);
                          setNewIncident(prev => ({
                            ...prev,
                            cashier_id: val,
                            cashier_name: cashier?.name || ''
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select cashier" />
                        </SelectTrigger>
                        <SelectContent>
                          {cashiers.map(cashier => (
                            <SelectItem key={cashier.id} value={cashier.id}>{cashier.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Drive Off specific */}
                  {newIncident.incident_type === 'drive_off' && (
                    <div className="grid grid-cols-2 gap-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="space-y-2">
                        <Label>Vehicle Description</Label>
                        <Input
                          value={newIncident.vehicle_description}
                          onChange={(e) => setNewIncident(prev => ({ ...prev, vehicle_description: e.target.value }))}
                          placeholder="Silver Ford Focus"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Police Reference</Label>
                        <Input
                          value={newIncident.police_reference}
                          onChange={(e) => setNewIncident(prev => ({ ...prev, police_reference: e.target.value }))}
                          placeholder="CAD-123456"
                        />
                      </div>
                    </div>
                  )}

                  {/* Indebtedness specific */}
                  {newIncident.incident_type === 'indebtedness' && (
                    <div className="space-y-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Customer Name</Label>
                          <Input
                            value={newIncident.customer_name}
                            onChange={(e) => setNewIncident(prev => ({ ...prev, customer_name: e.target.value }))}
                            placeholder="John Smith"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={newIncident.customer_phone}
                            onChange={(e) => setNewIncident(prev => ({ ...prev, customer_phone: e.target.value }))}
                            placeholder="07123 456789"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Customer Address <span className="text-red-500">*</span></Label>
                        <Input
                          value={newIncident.customer_address || ''}
                          onChange={(e) => setNewIncident(prev => ({ ...prev, customer_address: e.target.value }))}
                          placeholder="123 Main Street, London, SW1A 1AA"
                          className={!newIncident.customer_address ? 'border-red-500/50' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Vehicle Description</Label>
                        <Input
                          value={newIncident.vehicle_description}
                          onChange={(e) => setNewIncident(prev => ({ ...prev, vehicle_description: e.target.value }))}
                          placeholder="e.g. Silver Ford Focus, Blue Van, etc."
                        />
                      </div>
                      <div className={`p-3 rounded-lg ${!newIncident.agreement_signed ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newIncident.agreement_signed}
                            onChange={(e) => setNewIncident(prev => ({ ...prev, agreement_signed: e.target.checked }))}
                          />
                          <span className="font-medium">Credit agreement signed *</span>
                          <span className="text-xs text-muted-foreground">(Required)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {editingIncident ? (
                      <>
                        <Button onClick={handleUpdateIncident} className="flex-1">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Update Incident
                        </Button>
                        <Button variant="outline" onClick={handleCancelEditIncident}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={addIncident} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Record Incident
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Today's Incidents - Only show incidents for the selected date */}
              {(() => {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                const todayIncidents = incidents.filter(i => 
                  i.incident_date === dateStr || i.resolution_date === dateStr
                );
                
                if (todayIncidents.length === 0) return null;
                
                return (
                  <Card>
                    <CardHeader className="bg-orange-500/5 border-b border-orange-500/20">
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        Incidents for {format(selectedDate, 'dd MMM yyyy')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {todayIncidents.map(incident => (
                          <div 
                            key={incident.id}
                            className={`p-3 rounded-lg border ${
                              incident.status === 'open' ? 'border-red-500/30 bg-red-500/10' :
                              ['paid_store', 'paid_agency'].includes(incident.status) ? 'border-green-500/30 bg-green-500/10' :
                              'border-gray-500/30 bg-gray-500/10'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  incident.incident_type === 'drive_off' ? 'bg-red-500/20 text-red-500' : 'bg-purple-500/20 text-purple-500'
                                }`}>
                                  {incident.incident_type === 'drive_off' ? 'Drive Off' : 'Indebtedness'}
                                </span>
                                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                  incident.isPending ? 'bg-amber-500/20 text-amber-500' :
                                  incident.status === 'open' ? 'bg-yellow-500/20 text-yellow-500' :
                                  ['paid_store', 'paid_agency'].includes(incident.status) ? 'bg-green-500/20 text-green-500' :
                                  'bg-gray-500/20 text-gray-500'
                                }`}>
                                  {incident.isPending ? 'NOT SAVED' : incident.status.replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </div>
                              <span className="text-lg font-bold font-mono">£{incident.amount?.toFixed(2)}</span>
                            </div>
                            <div className="mt-2 text-sm text-muted-foreground">
                              <p className="flex items-center gap-1">
                                Pump {incident.pump_number} • 
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${getFuelGradeColor(incident.fuel_grade_name).badge}`}>
                                  {incident.fuel_grade_name}
                                </span>
                                 • {incident.litres}L
                              </p>
                              {incident.vehicle_registration && <p>Vehicle Reg: {incident.vehicle_registration}</p>}
                              {incident.vehicle_description && <p>Vehicle: {incident.vehicle_description}</p>}
                              {incident.customer_name && <p>Customer: {incident.customer_name}</p>}
                            </div>
                            
                            {/* Show Edit/Delete buttons for:
                                - Pending incidents (not yet saved to DB) - any user can edit/delete
                                - Open incidents (saved to DB) - any user can edit/delete before submission
                            */}
                            {(incident.isPending || incident.status === 'open') && (
                              <div className="mt-3 flex gap-2 flex-wrap">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditIncident(incident)}
                                  data-testid={`edit-incident-${incident.id}`}
                                >
                                  <Edit2 className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-destructive border-destructive/30"
                                  onClick={() => handleDeleteIncident(incident)}
                                  data-testid={`delete-incident-${incident.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                                {incident.isPending && (
                                  <span className="text-xs text-amber-500 flex items-center">
                                    (Not yet saved - will be saved on submission)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Summary Card showing impact on payments */}
              {(incidentDeductions > 0 || incidentRecoveries > 0 || stagedRecoveries > 0) && (
                <Card className="border-orange-500/30">
                  <CardHeader>
                    <CardTitle className="text-orange-500" style={{ fontFamily: 'Barlow Condensed' }}>
                      Impact on Today's Sales
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {incidentDeductions > 0 && (
                      <div className="flex justify-between p-2 bg-red-500/10 rounded">
                        <span>Drive Off / Debt Losses (New)</span>
                        <span className="font-mono text-red-500">-£{incidentDeductions.toFixed(2)}</span>
                      </div>
                    )}
                    {existingRecoveries > 0 && (
                      <div className="flex justify-between p-2 bg-green-500/10 rounded">
                        <span>Previously Recovered (Already Paid)</span>
                        <span className="font-mono text-green-500">+£{existingRecoveries.toFixed(2)}</span>
                      </div>
                    )}
                    {stagedRecoveries > 0 && (
                      <div className="flex justify-between p-2 bg-green-500/10 rounded border border-green-500/30">
                        <span>New Recoveries (Staged Today)</span>
                        <span className="font-mono text-green-600 font-bold">+£{stagedRecoveries.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between p-2 bg-orange-500/10 rounded border-t-2 mt-2">
                      <span className="font-medium">Net Impact</span>
                      <span className={`font-mono font-bold ${(incidentRecoveries - incidentDeductions) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {(incidentRecoveries - incidentDeductions) >= 0 ? '+' : ''}£{(incidentRecoveries - incidentDeductions).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      These amounts are automatically factored into the payment summary when you submit.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          </fieldset>
        </>
      )}

      {/* Variance Reason Dialog */}
      <Dialog open={varianceDialogOpen} onOpenChange={setVarianceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Variance Explanation Required
            </DialogTitle>
            <DialogDescription>
              The cash variance is £{Math.abs(variance).toFixed(2)} (reason required from £{Number(varianceLimits.cash_reason || 0).toFixed(2)}). Please provide a reason for this {variance > 0 ? 'overage' : 'shortage'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${variance > 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              <p className="text-sm text-muted-foreground">Current Variance ({variance > 0 ? 'Over' : 'Short'})</p>
              <p className={`text-2xl font-bold font-mono ${variance > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Variance</Label>
              <Textarea
                placeholder="Please explain the reason for this variance..."
                value={varianceReason}
                onChange={(e) => setVarianceReason(e.target.value)}
                rows={4}
                data-testid="variance-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVarianceDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitWithVariance}
              disabled={!varianceReason.trim()}
              data-testid="submit-with-variance-btn"
            >
              Submit with Explanation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Change Confirmation Dialog */}
      <Dialog open={dateChangeDialogOpen} onOpenChange={setDateChangeDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved data. What would you like to do with the current entries?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-yellow-500/10 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Changing date from <strong>{format(selectedDate, 'PPP')}</strong> to <strong>{pendingDate ? format(pendingDate, 'PPP') : ''}</strong>
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              variant="secondary"
              onClick={applyDateChangeKeepData}
              className="w-full sm:w-auto"
            >
              Keep Data
            </Button>
            <Button 
              variant="destructive"
              onClick={applyDateChangeWithClear}
              className="w-full sm:w-auto"
            >
              Clear Data
            </Button>
            <Button variant="outline" onClick={() => {
              setDateChangeDialogOpen(false);
              setPendingDate(null);
            }} className="w-full sm:w-auto">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Store Change Confirmation Dialog */}
      <Dialog open={storeChangeDialogOpen} onOpenChange={setStoreChangeDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved data. What would you like to do with the current entries?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-yellow-500/10 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Changing store from <strong>{stores.find(s => s.id === selectedStoreId)?.name || 'Unknown'}</strong> to <strong>{stores.find(s => s.id === pendingStoreId)?.name || ''}</strong>
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              variant="secondary"
              onClick={applyStoreChangeKeepData}
              className="w-full sm:w-auto"
            >
              Keep Data
            </Button>
            <Button 
              variant="destructive"
              onClick={applyStoreChangeWithClear}
              className="w-full sm:w-auto"
            >
              Clear Data
            </Button>
            <Button variant="outline" onClick={() => {
              setStoreChangeDialogOpen(false);
              setPendingStoreId(null);
            }} className="w-full sm:w-auto">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Confirmation Dialog */}
      <Dialog open={submitConfirmDialogOpen} onOpenChange={setSubmitConfirmDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              {Math.abs(variance) < 0.01
                ? <CheckCircle className="h-5 w-5 text-green-500" />
                : <AlertTriangle className={`h-5 w-5 ${Math.abs(variance) > varianceLimits.cash ? 'text-red-500' : 'text-yellow-500'}`} />}
              Cash Variance
            </DialogTitle>
            <DialogDescription>
              Please review the summary before submitting.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Sales:</span>
                  <span className="font-mono">{formatCurrency(totalSales + (isFuelStation ? fuelSalesZReading : 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Payments:</span>
                  <span className="font-mono">{formatCurrency(totalPayments)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected:</span>
                  <span className="font-mono">{formatCurrency(expectedPayments)}</span>
                </div>
              </div>
            </div>

            {/* Variance Warning */}
            <div className={`p-4 rounded-lg ${
              Math.abs(variance) < 0.01 ? 'bg-green-500/10 border border-green-500/30' : 
              Math.abs(variance) < 5 ? 'bg-yellow-500/10 border border-yellow-500/30' : 
              'bg-red-500/10 border border-red-500/30'
            }`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {Math.abs(variance) < 0.01 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertTriangle className={`h-5 w-5 ${Math.abs(variance) < 5 ? 'text-yellow-500' : 'text-red-500'}`} />
                  )}
                  <span className="font-medium">Variance</span>
                </div>
                <span className={`font-mono font-bold text-lg ${
                  Math.abs(variance) < 0.01 ? 'text-green-500' : 
                  variance > 0 ? 'text-green-500' : 'text-red-500'
                }`}>
                  {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                </span>
              </div>
              {Math.abs(variance) >= varianceLimits.cash_reason && (
                <p className="text-sm text-muted-foreground mt-2">
                  ⚠️ Variance is £{Number(varianceLimits.cash_reason).toFixed(2)} or more. A notification will be sent to the store email.
                </p>
              )}
              {Math.abs(variance) > varianceLimits.cash && (
                <p className="text-sm text-red-500 font-medium mt-1" data-testid="cash-variance-approval-note">
                  Variance is more than £{Number(varianceLimits.cash).toFixed(2)}. Admin Approval is required to submit this entry.
                </p>
              )}
            </div>

            {/* Entry Details */}
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p><strong>Store:</strong> {storeDetails?.name}</p>
              <p><strong>Date:</strong> {format(selectedDate, 'PPP')}</p>
              <p><strong>Shift:</strong> {selectedShift === 'midday' ? 'Mid Day' : 'Day End'}</p>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              onClick={validateAndSubmit}
              disabled={saving}
              className="w-full sm:w-auto"
              data-testid="submit-confirm-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Confirm & Submit
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setSubmitConfirmDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sales Entry Confirmation Dialog */}
      {/* Total Sales Variance Dialog - Blocks submission if variance > £1 */}
      <Dialog open={pumpValidationDialogOpen} onOpenChange={setPumpValidationDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5" />
              Total Sales Variance Detected
            </DialogTitle>
            <DialogDescription>
              The Total Sales* (Z-Reading) doesn't match the expected value. Please correct before submitting.
            </DialogDescription>
          </DialogHeader>

          {pumpValidationData && (
            <div className="space-y-4">
              {/* Formula Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Calculated Retail Sales</span>
                  <span className="font-mono text-right">{formatCurrency(pumpValidationData.calculatedFuelSales)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">+ Bunkering Total</span>
                  <span className="font-mono text-right text-orange-500">+{formatCurrency(pumpValidationData.bunkeringTotal || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">+ Drive Off / Debt</span>
                  <span className="font-mono text-right text-red-500">+{formatCurrency(pumpValidationData.driveOffLosses || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50 font-medium bg-muted/30 px-2 rounded">
                  <span>= Expected Total Sales*</span>
                  <span className="font-mono text-right">{formatCurrency(pumpValidationData.expectedPump)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Entered Total Sales* (Z-Reading)</span>
                  <span className="font-mono text-right">{formatCurrency(pumpValidationData.enteredFuelSales)}</span>
                </div>
              </div>

              {/* Variance Error */}
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Variance</span>
                  <span className="text-xl font-bold font-mono text-red-500">
                    {formatCurrency(pumpValidationData.variance)}
                  </span>
                </div>
                <p className="text-xs text-red-600 mt-2">
                  Variance exceeds £1.00 tolerance. Please verify and correct the figures before submitting.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setPumpValidationDialogOpen(false);
                setActiveTab('fuel');
              }}
              className="w-full sm:w-auto"
            >
              Review Fuel Sales
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setPumpValidationDialogOpen(false);
                setActiveTab('fuelcards');
              }}
              className="w-full sm:w-auto"
            >
              Review Fuel Cards
            </Button>
            {/* Admin only: Allow proceeding with variance */}
            {isAdmin && (
              <Button 
                onClick={() => {
                  setPumpValidationDialogOpen(false);
                  // Check variance reason before proceeding
                  if (proceedToSubmitWithVarianceCheck()) {
                    performSubmit(null, scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null);
                  }
                }}
                disabled={saving}
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="admin-proceed-variance-btn"
              >
                {saving ? 'Submitting...' : 'Proceed Anyway (Admin)'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bunkering Sales Validation Warning Dialog */}
      <Dialog open={bunkeringValidationDialogOpen} onOpenChange={setBunkeringValidationDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5" />
              Bunkering Sales Variance Detected
            </DialogTitle>
            <DialogDescription>
              The Bunkering Sales amount entered on the Fuel Sales tab doesn't match the Bunkering Total calculated on the Fuel Cards tab.
            </DialogDescription>
          </DialogHeader>

          {bunkeringValidationData && (
            <div className="space-y-4">
              {/* Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Bunkering Sales (Fuel Sales Tab)</span>
                  <span className="font-mono">{formatCurrency(bunkeringValidationData.enteredBunkeringSales)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Bunkering Total (Fuel Cards Tab)</span>
                  <span className="font-mono text-orange-500">{formatCurrency(bunkeringValidationData.calculatedBunkeringTotal)}</span>
                </div>
              </div>

              {/* Variance Warning */}
              <div className={`p-4 rounded-lg ${bunkeringValidationData.variance > 10 ? 'bg-red-500/10 border border-red-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Variance</span>
                  <span className={`text-xl font-bold font-mono ${bunkeringValidationData.variance > 10 ? 'text-red-500' : 'text-orange-500'}`}>
                    {formatCurrency(bunkeringValidationData.variance)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Please double-check the Bunkering Sales amount and Fuel Card entries before proceeding.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setBunkeringValidationDialogOpen(false);
                setActiveTab('fuelcards');
              }}
              className="w-full sm:w-auto"
            >
              Review Fuel Cards
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setBunkeringValidationDialogOpen(false);
                setActiveTab('fuel');
              }}
              className="w-full sm:w-auto"
            >
              Review Fuel Sales
            </Button>
            <Button 
              onClick={() => {
                setBunkeringValidationDialogOpen(false);
                // Check variance reason before proceeding
                if (proceedToSubmitWithVarianceCheck()) {
                  performSubmit(null, scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null);
                }
              }}
              disabled={saving}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="proceed-bunkering-variance-btn"
            >
              {saving ? 'Submitting...' : 'Proceed Anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retail Sales Minor Variance Confirmation Dialog */}
      <Dialog open={retailVarianceDialogOpen} onOpenChange={setRetailVarianceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5" />
              Minor Retail Sales Variance
            </DialogTitle>
            <DialogDescription>
              There is a small variance between entered and calculated Retail Sales. Please confirm to proceed.
            </DialogDescription>
          </DialogHeader>

          {retailVarianceData && (
            <div className="space-y-4">
              {/* Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Entered Retail Sales</span>
                  <span className="font-mono">{formatCurrency(retailVarianceData.enteredRetailSales)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Calculated Retail Sales</span>
                  <span className="font-mono">{formatCurrency(retailVarianceData.calculatedRetailSales)}</span>
                </div>
              </div>

              {/* Variance Warning */}
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Variance</span>
                  <span className="text-xl font-bold font-mono text-yellow-500">
                    £{retailVarianceData.variance.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  This variance is within the acceptable tolerance (±£{Number(varianceLimits.retail).toFixed(2)}).
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setRetailVarianceDialogOpen(false);
                setActiveTab('fuel');
              }}
              className="w-full sm:w-auto"
            >
              Edit Fuel Sales
            </Button>
            <Button 
              onClick={() => {
                setRetailVarianceDialogOpen(false);
                // Check variance reason before proceeding
                if (proceedToSubmitWithVarianceCheck()) {
                  performSubmit(null, scratchCardVarianceDetails.length > 0 ? scratchCardVarianceDetails : null);
                }
              }}
              disabled={saving}
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white"
              data-testid="proceed-retail-variance-btn"
            >
              {saving ? 'Submitting...' : 'Proceed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved data - leave page warning */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent data-testid="unsaved-leave-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Unsaved Sales Entry Data
            </DialogTitle>
            <DialogDescription>
              You have entered data that has not been submitted or saved as a draft. If you leave now, this data will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setLeaveDialogOpen(false)} data-testid="unsaved-stay-btn">Stay on Page</Button>
            <Button variant="destructive" onClick={() => leavePage(false)} data-testid="unsaved-leave-btn">Leave Without Saving</Button>
            <Button onClick={() => leavePage(true)} disabled={savingDraft} data-testid="unsaved-save-draft-leave-btn">
              {savingDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save Draft &amp; Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variance Approval Request Dialog */}
      <VarianceApprovalDialog
        open={varianceApprovalDialogOpen}
        onOpenChange={setVarianceApprovalDialogOpen}
        checks={varianceDetails?.checks || []}
        adminMode={isAdminUser && !adminEditRequestId}
        requesting={requestingApproval || saving}
        comment={varianceComment}
        onCommentChange={setVarianceComment}
        onEditData={() => {
          setVarianceApprovalDialogOpen(false);
          setActiveTab(varianceDetails?.checks?.[0]?.tab || 'fuel');
        }}
        onRequestApproval={() => {
          if (isAdminUser && !adminEditRequestId) {
            adminVarianceAckRef.current = true;
            setVarianceApprovalDialogOpen(false);
            validateAndSubmit();
          } else {
            requestVarianceApproval();
          }
        }}
      />

      {/* Draft Found Dialog */}
      <Dialog open={draftDialogOpen} onOpenChange={setDraftDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-500">
              <ClipboardCopy className="h-5 w-5" />
              Draft Found
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              A saved draft was found for this store, date, and shift. Would you like to load the draft or start fresh?
            </p>
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm">
              <p className="font-medium text-blue-400">Draft Details:</p>
              <p className="text-muted-foreground mt-1">
                {selectedStore?.name} - {format(selectedDate, 'dd/MM/yyyy')} - {selectedShift === 'dayend' ? 'Day End' : 'Day'}
              </p>
              {draftInfo && (
                <p className="text-muted-foreground mt-1">
                  Saved by: <span className="text-blue-400">{draftInfo.savedBy}</span>
                  {draftInfo.savedAt && ` at ${format(new Date(draftInfo.savedAt), 'dd/MM/yyyy HH:mm')}`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setDraftDialogOpen(false);
                // Don't load draft, start fresh - delete from database
                if (draftKey) {
                  clearDraft(draftKey.storeId, draftKey.dateStr, draftKey.shift);
                }
                toast.info('Starting fresh - draft cleared');
              }}
              data-testid="draft-start-fresh-btn"
            >
              Start Fresh
            </Button>
            <Button 
              onClick={() => {
                if (draftKey) {
                  loadDraft(draftKey.storeId, draftKey.dateStr, draftKey.shift);
                }
                setDraftDialogOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="draft-load-btn"
            >
              Load Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scratch Card Variance Confirmation Dialog */}
      <Dialog open={scratchCardVarianceDialogOpen} onOpenChange={setScratchCardVarianceDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Scratch Card Variance Detected
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              A variance has been detected between the declared scratch card sales and the calculated amount from stock count.
            </p>

            {/* Amount Comparison */}
            <div className="p-4 border border-amber-500/30 bg-amber-500/10 rounded-lg space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span>Declared on Shop Sales Tab:</span>
                <span className="font-mono font-medium">£{parseFloat(serviceAmounts['scratch_cards'] || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span>Calculated from Stock Count:</span>
                <span className="font-mono font-medium">£{scratchCardVarianceDetails
                  .filter(i => i.name !== 'VARIANCE SUMMARY')
                  .reduce((sum, i) => sum + (i.salesValue || 0), 0).toFixed(2)}</span>
              </div>
              <div className="pt-2 border-t border-amber-500/30 flex justify-between items-center font-medium">
                <span>Variance:</span>
                {(() => {
                  const declared = parseFloat(serviceAmounts['scratch_cards'] || 0);
                  const calculated = scratchCardVarianceDetails
                    .filter(i => i.name !== 'VARIANCE SUMMARY')
                    .reduce((sum, i) => sum + (i.salesValue || 0), 0);
                  const variance = declared - calculated;  // Declared - Calculated (negative = SHORT)
                  return (
                    <span className={`font-mono ${variance < 0 ? 'text-red-500' : 'text-green-400'}`}>
                      £{variance.toFixed(2)}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Stock Details */}
            <div className="text-sm font-medium text-muted-foreground">Stock Movement Details:</div>
            <div className="space-y-2 max-h-[150px] overflow-y-auto text-sm">
              {scratchCardVarianceDetails
                .filter(item => item.name !== 'VARIANCE SUMMARY')
                .map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span>{item.name}</span>
                  <div className="flex gap-3 font-mono text-xs">
                    <span className="text-muted-foreground">Sold: {item.soldQty}</span>
                    <span>£{(item.salesValue || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Variance Reason Input - Required for ANY scratch card variance */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason for Variance (Required) *</Label>
              <Textarea
                placeholder="Please explain the reason for this variance..."
                value={scratchCardVarianceReason}
                onChange={(e) => setScratchCardVarianceReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="scratch-variance-reason-input"
              />
            </div>

            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> A variance notification will be sent to the store email address after submission.
            </p>
          </div>

          <DialogFooter className="flex gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setScratchCardVarianceDialogOpen(false);
                setScratchCardVarianceReason('');
              }}
              data-testid="scratch-variance-go-back-btn"
            >
              Go Back
            </Button>
            <Button 
              onClick={() => completeScratchCardVerification(scratchCardVarianceDetails)} 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="scratch-variance-continue-btn"
              disabled={!scratchCardVarianceReason.trim()}
            >
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SalesEntryPage;
